import './guild-dashboard.css';
import { getWeekRangeForDate } from './utils/dateHelpers.js';
import { isMemberActiveInEvent, getActivePeriodAtDate } from './utils/calculations.js';
import { MemberPeriodsEditor } from './components/MemberPeriodsEditor.jsx';
import { PlayerComparison } from './components/PlayerComparison.jsx';
import { buildUploadEndpoint } from './services/uploadConfig.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;
// DEBUG mode - set to false by default, enable with: localStorage.setItem('DEBUG_MODE', 'true')
const DEBUG = typeof localStorage !== 'undefined' ? localStorage.getItem('DEBUG_MODE') === 'true' : false;
const initialTab = (window.location.hash && window.location.hash.replace('#','')) || window.INITIAL_TAB || 'dashboard';
// API URLs: all go through Vercel proxy for security
const API_BASE = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/data` : '/api/data';
const EVENTS_API = '/api/events'; // Routes through Vercel proxy with API key
const WEEKLY_EVENTS_API = '/api/events?frequency=weekly'; // Legacy endpoint for backward compatibility
const VPS_API_URL = process.env.REACT_APP_BACKEND_URL || 'https://nexus.hot-topheroes.com:3001'; // For reference only

// NOTE: supabase object is provided by supabase-stub-v2.js (webpack entry)
// Stub provides backend proxy interface for authentication and data operations
const MINIMUM_SCORE = 1500000;
const DAY_NAMES = ['Construction', 'Research', 'World Battles', 'Hero Upgrading', 'Soldier Training', 'PvP'];
const DAY_ICONS = ['🏗️', '🔬', '⚔️', '⭐', '🛡️', '⚡'];
const ROLE_ID = process.env.REACT_APP_ROLE_ID || '1278052383523602443';
const parseScore = (str) => { if (!str) return 0; return parseFloat(str.toString().replace(/,/g, '')) || 0; };
const formatNumber = (num) => { if (num === 0) return '0'; if (typeof num === 'string') num = parseFloat(num) || 0; const parsed = parseFloat(num); if (isNaN(parsed)) return '0'; return Math.floor(parsed).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','); };
const formatCompact = (num) => { if (num === 0 || !num) return '0'; if (typeof num === 'string') num = parseFloat(num) || 0; const parsed = parseFloat(num); if (isNaN(parsed)) return '0'; if (parsed >= 1e9) { const divided = parsed / 1e9; return (Math.round(divided * 100) / 100).toFixed(2) + 'B'; } if (parsed >= 1e6) { const divided = parsed / 1e6; return (Math.round(divided * 100) / 100).toFixed(2) + 'M'; } if (parsed >= 1e3) { const divided = parsed / 1e3; return (Math.round(divided * 10) / 10).toFixed(1) + 'K'; } return Math.floor(parsed).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','); };
// Helper to parse aliases safely (handles array, string, Supabase JSON, or CSV)
const parseAliases = (aliasesData) => { 
  if (!aliasesData) return []; 
  if (Array.isArray(aliasesData)) return aliasesData; 
  if (typeof aliasesData === 'string') { 
    // Remove leading {" and trailing "} if present (Supabase format)
    let cleaned = aliasesData.trim();
    if (cleaned.startsWith('{"')) cleaned = cleaned.substring(2);
    if (cleaned.endsWith('"}')) cleaned = cleaned.substring(0, cleaned.length - 2);
    
    try { 
      const parsed = JSON.parse(`[${cleaned}]`); // Try to parse as JSON array
      if (Array.isArray(parsed)) return parsed.filter(a => a && typeof a === 'string');
    } catch (e) { 
      // Not JSON, try splitting by common delimiters
      return cleaned.split(/[,;|]/).map(a => {
        if (typeof a !== 'string') return '';
        return a.trim().replace(/^["']|["']$/g, '');
      }).filter(a => a && a !== '{' && a !== '}'); 
    } 
  } 
  return []; 
};
// Helper to format dates without timezone issues
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '(not set)';
  try {
    // Parse as UTC and convert to local timezone
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) throw new Error('Invalid date');
    // Get local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  } catch (e) {
    // Fallback: extract yyyy-mm-dd part only
    let dateOnly = dateStr;
    if (dateStr.includes('T')) {
      dateOnly = dateStr.split('T')[0];
    }
    const parts = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!parts) return dateStr;
    const [, year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
};

// Helper to extract date for input fields (YYYY-MM-DD) preserving local timezone
const extractDateForInput = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) throw new Error('Invalid date');
    // Get components in local timezone (same as formatDateDisplay)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    // Fallback: if it's already YYYY-MM-DD, return as-is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    return '';
  }
};

// API helper function
const apiCall = async (action, table, options = {}) => { const body = { action, table, ...options }; const headers = { 'Content-Type': 'application/json' }; const response = await fetch(API_BASE, { method: 'POST', headers, body: JSON.stringify(body) }); if (!response.ok) throw new Error(`API error: ${response.statusText}`); const result = await response.json(); return result.data || result; };

// SECURITY: Create an authenticated API call helper that includes session token
const apiCallWithAuth = async (action, table, sessionToken, options = {}) => { 
  if (!sessionToken) throw new Error('Session token required for authenticated requests'); 
  const body = { action, table, ...options }; 
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionToken}` }; 
  const response = await fetch(API_BASE, { method: 'POST', headers, body: JSON.stringify(body) }); 
  if (!response.ok) throw new Error(`API error: ${response.statusText}`); 
  const result = await response.json(); 
  return result.data || result; 
};

// Helper to get rank class name for glowing effect
const getRankClass = (rank) => {
    const rankStr = (rank || 'R1').toLowerCase();
    return `rank-glow ${rankStr}`;
};

function GuildDashboard() {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editingGuildEventId, setEditingGuildEventId] = useState(null);
    const [editingGuildEventData, setEditingGuildEventData] = useState(null);
    const [members, setMembers] = useState([]);
    const [memberPeriods, setMemberPeriods] = useState({}); // Map of memberId -> [periods]
    const [dailyScores, setDailyScores] = useState([]);
    const [memberContributions, setMemberContributions] = useState([]);
    const [eventName, setEventName] = useState('');
    const [opponentName, setOpponentName] = useState('');
    const [eventServer, setEventServer] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventEndDate, setEventEndDate] = useState('');
    const [isLowPriority, setIsLowPriority] = useState(false);
    const [memberInput, setMemberInput] = useState('');
    const [dayScores, setDayScores] = useState(Array(6).fill(''));
    const [opponentDayScores, setOpponentDayScores] = useState(Array(6).fill(''));
    const [selectedDay, setSelectedDay] = useState(1);
    const [memberScoresInput, setMemberScoresInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [memberFormData, setMemberFormData] = useState({ displayName: '', status: 'active', joinedDate: '', leftDate: '', newAliases: '', rank: 'R1' });
    const [memberAllContributions, setMemberAllContributions] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // Show a small hint when pressing promote buttons for members who don't have an auth user linked
    const [showAuthHint, setShowAuthHint] = useState(false);
    const [toast, setToast] = useState(null);
    const [searchQueryManage, setSearchQueryManage] = useState('');
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const [showMembersList, setShowMembersList] = useState(false);
    const [memberFilter, setMemberFilter] = useState('all');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [sortBy, setSortBy] = useState('total');
    const [sortAscending, setSortAscending] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [filterLowPerformers, setFilterLowPerformers] = useState(false);
    const [filterActiveOnly, setFilterActiveOnly] = useState(true);
    const [searchQueryRankings, setSearchQueryRankings] = useState('');
    const [compareSelectA, setCompareSelectA] = useState(null);
    const [compareSelectB, setCompareSelectB] = useState(null);
    
    // Memoize individual event medals calculation
    const eventMedalData = useMemo(() => {
        return (rankings && rankings.length > 0) ? getDailyMedals(rankings) : {};
    }, [rankings, getDailyMedals]);
    const [overallStats, setOverallStats] = useState(null);
    const [maxPlayersDisplayed, setMaxPlayersDisplayed] = useState(20); // Pagination for performance
    // Compare tab state
    const [comparePlayerA, setComparePlayerA] = useState('');
    const [comparePlayerB, setComparePlayerB] = useState('');
    const [compareData, setCompareData] = useState(null);
    const [compareLoading, setCompareLoading] = useState(false);
    const [compareOpponent, setCompareOpponent] = useState('ALL');
    const [compareChartType, setCompareChartType] = useState('total');
    const [compareView, setCompareView] = useState('text'); // 'text' or 'visual'
    const compareCanvasRef = useRef(null);
    // Calendar tab state - load from localStorage initially, then sync with Supabase
    const [calendarEvents, setCalendarEvents] = useState(() => {
        try {
            const saved = localStorage.getItem('hot_calendar_events');
            if (saved) return JSON.parse(saved);
        } catch (e) { console.error('Failed to load calendar from localStorage', e); }
        return [];  // Start empty - events loaded from database on mount
    });
    const [calendarConfig, setCalendarConfig] = useState({
        gb_anchor: '2026-01-28T14:00:00Z',
        di_anchor: '2026-01-23T14:00:00Z',
        gb_interval_days: 3,
        di_interval_days: 7,
        time_slots: [14, 19],
        reminder_times_minutes: [240, 60, 30, 5]
    });
    const [calendarSyncing, setCalendarSyncing] = useState(false);
    const [showSyncSettings, setShowSyncSettings] = useState(false);
    const [newCalEvent, setNewCalEvent] = useState({ name: '', abbr: '', day: 'Monday', time: '00:00', frequency: 'weekly', color: '#ff5a3a', date: '', startDate: '', backgroundImage: '', eventTypeId: null, eventType: '', reminders: { 5: true, 30: true, 60: true, 240: true }, reminderMessage: '', reminderMessages: {}, guideId: null, guideText: '', guideUrl: '' });
    const [editingEventId, setEditingEventId] = useState(null);
    const [editingEventData, setEditingEventData] = useState(null);
    const [editingRotatingEvent, setEditingRotatingEvent] = useState(null);
    const [calendarWeekOffset, setCalendarWeekOffset] = useState(0);
    const [eventTypes, setEventTypes] = useState([]);
    const [newEventType, setNewEventType] = useState({ name: '', color: '#ff5a3a', backgroundImage: '' });
    const [editingEventTypeId, setEditingEventTypeId] = useState(null);
    const [sixDayTemplateExpanded, setSixDayTemplateExpanded] = useState(false);
    const [eventTemplates, setEventTemplates] = useState([]); // Six-day event templates from database
    const [newTemplate, setNewTemplate] = useState({ 
        type: 'six-day', // 'regular' or 'six-day'
        name: '', 
        abbr: '', 
        color: '#ff5a3a', 
        defaultTime: '14:00',
        backgroundImage: '', 
        dayMessages: { "1": "", "2": "", "3": "", "4": "", "5": "", "6": "" },
        description: ''
    });
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [passwordInput, setPasswordInput] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [sessionToken, setSessionToken] = useState(null); // SECURITY: Use session token instead of localStorage auth
    const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
    const [performanceModalData, setPerformanceModalData] = useState(null);
    const [isComparisonOpen, setIsComparisonOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [kebabMenuOpen, setKebabMenuOpen] = useState(false);
    const [memberDetailsModal, setMemberDetailsModal] = useState(null);
    const [memberDetailsFullData, setMemberDetailsFullData] = useState(null);
    const [memberDetailsFormData, setMemberDetailsFormData] = useState({ displayName: '', status: 'active', joinedDate: '', leftDate: '', newAliases: '', isAlt: false });
    const [memberDetailsScores, setMemberDetailsScores] = useState([0, 0, 0, 0, 0, 0]);
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // Guides state
    const [guides, setGuides] = useState(() => {
        try {
            const saved = localStorage.getItem('hot_guides');
            if (saved) return JSON.parse(saved);
        } catch (e) { console.error('Failed to load guides from localStorage', e); }
        return [];
    });
    const [selectedGuideId, setSelectedGuideId] = useState(null);
    const [editingGuideId, setEditingGuideId] = useState(null);
    const [isCreatingNewGuide, setIsCreatingNewGuide] = useState(false);
    const [newGuide, setNewGuide] = useState({ title: '', description: '', content: '', backgroundImage: '' });
    const [guideToDelete, setGuideToDelete] = useState(null);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showBackgroundModal, setShowBackgroundModal] = useState(false);
    const [uploadErrorModal, setUploadErrorModal] = useState(null);
    const [errorModalEdits, setErrorModalEdits] = useState({});
    const [errorSearchInputs, setErrorSearchInputs] = useState({});
    const [activeSearchIndex, setActiveSearchIndex] = useState(null);
    const [linkInput, setLinkInput] = useState({ text: 'link text', url: 'https://' });
    const [imageInput, setImageInput] = useState({ alt: '', url: '' });
    const [backgroundInput, setBackgroundInput] = useState('');
    
    // Debounce timers for guide edits (prevents save on every keystroke)
    const guideDebounceTimers = useRef({
        title: null,
        description: null,
        content: null
    });
    
    // Local state for editing form - updates immediately for display
    const [editingGuideData, setEditingGuideData] = useState({ title: '', description: '', content: '', backgroundImage: '', guideUrl: '' });

    const extractOpponentFromName = (name) => { if (!name) return null; const m = name.match(/\bvs\b\s*(.+)$/i) || name.match(/\bv\b\s*(.+)$/i); return m ? m[1].trim() : null; };

    // Rotating events calculator (GB every 3 days, DI every Thursday)
    const getRotatingEvents = useMemo(() => () => {
        const events = [];
        const now = new Date();
        const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
        const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;
        
        // GB Anchor: Jan 28, 2026 at 14:00 UTC (slot 0 = 14:00)
        const gbAnchorTime = Date.UTC(2026, 0, 28, 14, 0, 0);
        // DI Anchor: Jan 15, 2026 (Thursday) at 14:00 UTC (slot 0 = 14:00)
        const diAnchorTime = Date.UTC(2026, 0, 15, 14, 0, 0);
        
        // Generate GB instances (every 3 days, alternating 14:00/19:00)
        let gbTime = gbAnchorTime;
        let gbSlot = 0; // 0 = 14:00, 1 = 19:00
        // Move forward until we're at or past now
        while (gbTime < now.getTime()) {
            gbTime += THREE_DAYS;
            gbSlot = 1 - gbSlot;
        }
        // Generate next 10 GB instances
        for (let i = 0; i < 10; i++) {
            const hour = gbSlot === 0 ? 14 : 19;
            const eventDate = new Date(gbTime);
            eventDate.setUTCHours(hour, 0, 0, 0);
            events.push({
                type: 'GB',
                name: 'Guild Boss',
                date: eventDate,
                color: '#ff9500',
                baseHour: hour
            });
            gbTime += THREE_DAYS;
            gbSlot = 1 - gbSlot;
        }
        
        // Generate DI instances (every Thursday, alternating 14:00/19:00)
        let diTime = diAnchorTime;
        let diSlot = 0; // 0 = 14:00, 1 = 19:00
        // Move forward until we're at or past now
        while (diTime < now.getTime()) {
            diTime += ONE_WEEK;
            diSlot = 1 - diSlot;
        }
        // Generate next 6 DI instances
        for (let i = 0; i < 6; i++) {
            const hour = diSlot === 0 ? 14 : 19;
            const eventDate = new Date(diTime);
            eventDate.setUTCHours(hour, 0, 0, 0);
            events.push({
                type: 'DI',
                name: 'Dark Invasion',
                date: eventDate,
                color: '#9d5eff',
                baseHour: hour
            });
            diTime += ONE_WEEK;
            diSlot = 1 - diSlot;
        }
        
        // Check for collisions: if GB and DI are at same date+time, shift GB +30min
        events.forEach(ev => {
            if (ev.type === 'GB') {
                const collision = events.find(other => 
                    other.type === 'DI' && 
                    other.date.toDateString() === ev.date.toDateString() && 
                    other.date.getUTCHours() === ev.date.getUTCHours()
                );
                if (collision) {
                    ev.date = new Date(ev.date.getTime() + 30 * 60 * 1000); // +30 minutes
                    ev.shifted = true;
                }
            }
        });
        
        return events.sort((a, b) => a.date - b.date);
    }, []);

    // Expose calendar data globally for external access (Discord bot, etc.)
    window.getHOTCalendarJSON = () => {
        const rotating = getRotatingEvents();
        const weekly = calendarEvents;
        return {
            generated: new Date().toISOString(),
            rotating: rotating.map(ev => ({
                type: ev.type,
                name: ev.name,
                date: (ev.date instanceof Date ? ev.date : new Date(ev.date)).toISOString(),
                color: ev.color,
                shifted: ev.shifted || false
            })),
            weekly: weekly.map(ev => ({
                id: ev.id,
                name: ev.name,
                abbr: ev.abbr,
                day: ev.day,
                time: ev.time,
                frequency: ev.frequency,
                color: ev.color
            }))
        };
    };

    // Load calendar config from PostgreSQL - now loads separate configs for GB (id=1) and DI (id=2)
    const loadCalendarFromSupabase = async () => {
        try {
            // Load both GB and DI configs
            const gbData = await apiCall('get', 'calendar_config', { filters: { id: 1 } });
            const diData = await apiCall('get', 'calendar_config', { filters: { id: 2 } });
            
            if (!Array.isArray(gbData) || gbData.length === 0 || !Array.isArray(diData) || diData.length === 0) {
                console.warn('[LOAD_CONFIG] Missing GB or DI config');
                return;
            }

            const gbConfig = gbData[0];
            const diConfig = diData[0];
            
            // Parse JSONB fields if they come as strings
            const parseJsonField = (field) => {
                if (typeof field === 'string') {
                    try { return JSON.parse(field); }
                    catch (e) { return {}; }
                }
                return field || {};
            };

            // Helper to parse reminder times
            const parseReminderTimes = (val) => {
                if (Array.isArray(val)) return val;
                if (typeof val === 'string') try { return JSON.parse(val); } catch(e) { return [5, 30, 60, 240]; }
                return [5, 30, 60, 240];
            };

            // Merge guides and backgrounds from both configs
            const gbGuides = parseJsonField(gbConfig.rotating_event_guides)?.GB || {};
            const diGuides = parseJsonField(diConfig.rotating_event_guides)?.DI || {};
            const gbBackgrounds = parseJsonField(gbConfig.rotating_event_backgrounds)?.GB || null;
            const diBackgrounds = parseJsonField(diConfig.rotating_event_backgrounds)?.DI || null;
            const gbReminderMessagesObj = parseJsonField(gbConfig.rotating_event_reminder_messages);
            const diReminderMessagesObj = parseJsonField(diConfig.rotating_event_reminder_messages);
            const gbReminderMessage = gbReminderMessagesObj?.GB || null;
            const diReminderMessage = diReminderMessagesObj?.DI || null;
            
            // Load separate reminder times for each event type - DON'T MERGE THESE
            const gbReminderTimes = parseReminderTimes(gbConfig.reminder_times_minutes);
            const diReminderTimes = parseReminderTimes(diConfig.reminder_times_minutes);

            // Merge into single objects for frontend (frontend still uses ev.type to access)
            const mergedGuides = { GB: gbGuides, DI: diGuides };
            const mergedBackgrounds = { GB: gbBackgrounds, DI: diBackgrounds };
            const mergedReminderMessages = { GB: gbReminderMessage, DI: diReminderMessage };

            setCalendarConfig({
                gb_anchor: gbConfig.anchor,  // Map to gb_anchor for internal use
                di_anchor: diConfig.anchor,  // Map to di_anchor for internal use
                gb_interval_days: parseInt(gbConfig.interval_days) || 3,  // Default to 3 days
                di_interval_days: parseInt(diConfig.interval_days) || 7,  // Default to 7 days
                time_slots: gbConfig.time_slots || [14, 19],
                gb_reminder_times_minutes: gbReminderTimes,  // SEPARATE - GB only
                di_reminder_times_minutes: diReminderTimes,  // SEPARATE - DI only
                event_time_overrides: parseJsonField(gbConfig.event_time_overrides),
                rotating_event_backgrounds: mergedBackgrounds,
                rotating_event_guides: mergedGuides,
                rotating_event_reminder_messages: mergedReminderMessages
            });

        } catch (e) { 
            console.error('[LOAD_CONFIG] Failed to load calendar from PostgreSQL:', e); 
        }
    };

    // Save calendar config via backend API (NOT direct Supabase - uses column normalization)
    const saveCalendarToBackend = async (config = calendarConfig) => {
        try {
            // Save both GB (id=1) and DI (id=2) configs
            const gbData = {
                id: '1',
                anchor: config.gb_anchor,
                interval_days: String(config.gb_interval_days),
                time_slots: config.time_slots,
                reminder_times_minutes: config.gb_reminder_times_minutes || [5, 30, 60, 240],
                event_time_overrides: { GB: config.event_time_overrides?.GB || null },
                rotating_event_backgrounds: { GB: config.rotating_event_backgrounds?.GB || null },
                rotating_event_guides: { GB: config.rotating_event_guides?.GB || null },
                rotating_event_reminder_messages: { GB: config.rotating_event_reminder_messages?.GB || null },
                updated_at: new Date().toISOString()
            };

            const diData = {
                id: '2',
                anchor: config.di_anchor,
                interval_days: String(config.di_interval_days),
                time_slots: config.time_slots,
                reminder_times_minutes: config.di_reminder_times_minutes || [5, 30, 60, 240],
                event_time_overrides: { DI: config.event_time_overrides?.DI || null },
                rotating_event_backgrounds: { DI: config.rotating_event_backgrounds?.DI || null },
                rotating_event_guides: { DI: config.rotating_event_guides?.DI || null },
                rotating_event_reminder_messages: { DI: config.rotating_event_reminder_messages?.DI || null },
                updated_at: new Date().toISOString()
            };

            const gbResp = await fetch(API_BASE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'upsert', table: 'calendar_config', data: gbData })
            });

            const diResp = await fetch(API_BASE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'upsert', table: 'calendar_config', data: diData })
            });

            if (!gbResp.ok || !diResp.ok) {
                const gbErr = await gbResp.json();
                const diErr = await diResp.json();
                console.error('Failed to save to backend:', { gbErr, diErr });
                return false;
            }

            return true;
        } catch (e) { 
            console.error('Failed to save to backend:', e); 
            return false; 
        }
    };

    // Old direct Supabase function (deprecated - kept for reference)
    const saveCalendarToSupabase = async () => {
        return saveCalendarToBackend();
    };

    // Save individual event to PostgreSQL backend
    const saveEventToBackend = async (event) => {
        try {
            console.log('[SAVE_EVENT_BACKEND] Starting save, event object:', event);
            // Save all events to backend (weekly, daily, monthly, one-time, bi-weekly)
            // Only include ID for existing events (updates), not for new events (inserts)
            const eventData = {
                name: event.name,
                abbr: event.abbr,
                frequency: event.frequency || 'weekly',
                color: event.color || '#CCCCCC',
                background_image: event.backgroundImage || null,
                event_type_id: event.eventTypeId ? parseInt(event.eventTypeId, 10) : null,
                // Handle both camelCase (from frontend) and snake_case (from backend) field names
                reminder_message: event.reminderMessage || event.reminder_message || null,
                guide_id: event.guideId ? parseInt(event.guideId, 10) : null,
                guide_text: event.guideText || event.guide_text || null,
                guide_url: event.guideUrl || event.guide_url || null
            };
            console.log('[SAVE_EVENT_BACKEND] eventData before send:', { guide_url: eventData.guide_url, guide_text: eventData.guide_text });

            // Only include ID if it's a real database ID (small integer), not a timestamp
            // Frontend timestamp IDs are temporary and should not be sent to DB
            if (event.id && typeof event.id === 'number' && event.id < 2147483647) {
                eventData.id = event.id;
            }

            // Add reminder preferences (enabled reminders)
            if (event.reminders) {
                eventData.reminders = event.reminders;
            }

            // Add frequency-specific fields
            if (event.frequency === 'one-time') {
                // Ensure date is sent as a pure string YYYY-MM-DD, not as a timestamp
                if (event.date instanceof Date) {
                    const year = event.date.getUTCFullYear();
                    const month = String(event.date.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(event.date.getUTCDate()).padStart(2, '0');
                    eventData.date = `${year}-${month}-${day}`;
                } else if (typeof event.date === 'string' && event.date.includes('T')) {
                    // If it's an ISO timestamp, extract just the date part
                    eventData.date = event.date.split('T')[0];
                } else {
                    // Already a pure date string
                    eventData.date = event.date;
                }
                // Also send startDate as fallback for migrations from other frequencies
                if (event.startDate) {
                    if (event.startDate instanceof Date) {
                        const year = event.startDate.getUTCFullYear();
                        const month = String(event.startDate.getUTCMonth() + 1).padStart(2, '0');
                        const day = String(event.startDate.getUTCDate()).padStart(2, '0');
                        eventData.startDate = `${year}-${month}-${day}`;
                    } else if (typeof event.startDate === 'string') {
                        eventData.startDate = event.startDate.includes('T') ? event.startDate.split('T')[0] : event.startDate;
                    }
                }
                eventData.time = event.time || '00:00';
            } else if (event.frequency === 'monthly') {
                eventData.day = event.day;
                eventData.time = event.time || '00:00';
            } else if (event.frequency === 'six-day') {
                // Six-day events need start_date and event_type
                if (event.startDate) {
                    if (event.startDate instanceof Date) {
                        const year = event.startDate.getUTCFullYear();
                        const month = String(event.startDate.getUTCMonth() + 1).padStart(2, '0');
                        const day = String(event.startDate.getUTCDate()).padStart(2, '0');
                        eventData.startDate = `${year}-${month}-${day}`;
                    } else if (typeof event.startDate === 'string') {
                        eventData.startDate = event.startDate.includes('T') ? event.startDate.split('T')[0] : event.startDate;
                    }
                }
                // Use event_type (not event_type_id) - defaults to snake_case version of name
                eventData.event_type = event.eventType || event.event_type || event.name.toLowerCase().replace(/\s+/g, '_');
                eventData.time = event.time || '19:00';
                // Initialize empty reminder_messages (can be edited later in DB)
                eventData.reminder_messages = event.reminderMessages || event.reminder_messages || {};
            } else {
                // weekly, daily, bi-weekly
                eventData.day = event.day;
                eventData.time = event.time || '00:00';
            }

            const response = await fetch(EVENTS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(eventData)
            });
            const result = await response.json();
            if (response.ok) {
                if (DEBUG) console.log('[Calendar] Event saved to backend:', result.event);
                return result.event; // Return event with database-generated ID
            } else {
                console.error('[Calendar] Backend save failed:', result.error);
                showToast('error', 'Backend Save Failed', result.error || 'Failed to save to backend');
                return null;
            }
        } catch (e) {
            console.error('[Calendar] Backend save error:', e);
            showToast('error', 'Save Error', e.message);
            return null;
        }
    };

    // Delete event from backend
    const deleteEventFromBackend = async (eventId, frequency = 'weekly') => {
        try {
            if (DEBUG) console.log('[Calendar] Deleting from backend:', `${EVENTS_API}/${eventId}?frequency=${frequency}`);
            const response = await fetch(`${EVENTS_API}/${eventId}?frequency=${frequency}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (response.ok) {
                const result = await response.json();
                if (DEBUG) console.log('[Calendar] Event deleted from backend:', result);
                return true;
            } else {
                // Try to parse error response if possible
                try {
                    const result = await response.json();
                    console.error('[Calendar] Backend delete failed:', result.error);
                } catch {
                    console.error('[Calendar] Backend delete failed with status:', response.status);
                }
                return false;
            }
        } catch (e) {
            console.error('[Calendar] Backend delete error:', e);
            return false;
        }
    };

    // Sync calendar to GitHub (instant update)
    // Sync calendar to backend for reminder processing - now saves GB and DI to separate rows with simplified column names
    const syncCalendarToBackend = async () => {
        setCalendarSyncing(true);
        try {
            // Split config into GB (id=1) and DI (id=2) data with new simplified column names
            const gbData = {
                id: '1',
                anchor: calendarConfig.gb_anchor,  // New simplified column name
                interval_days: String(calendarConfig.gb_interval_days),  // New simplified column name
                time_slots: calendarConfig.time_slots,
                reminder_times_minutes: calendarConfig.gb_reminder_times_minutes || [5, 30, 60, 240],
                rotating_event_reminders: calendarConfig.rotating_event_reminders?.GB || {},
                rotating_event_guides: calendarConfig.rotating_event_guides?.GB || null,
                rotating_event_backgrounds: calendarConfig.rotating_event_backgrounds?.GB || null
            };

            const diData = {
                id: '2',
                anchor: calendarConfig.di_anchor,  // New simplified column name
                interval_days: String(calendarConfig.di_interval_days),  // New simplified column name
                time_slots: calendarConfig.time_slots,
                reminder_times_minutes: calendarConfig.di_reminder_times_minutes || [5, 30, 60, 240],
                rotating_event_reminders: calendarConfig.rotating_event_reminders?.DI || {},
                rotating_event_guides: calendarConfig.rotating_event_guides?.DI || null,
                rotating_event_backgrounds: calendarConfig.rotating_event_backgrounds?.DI || null
            };

            // Save both configs
            const gbResponse = await fetch(API_BASE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'upsert', table: 'calendar_config', data: gbData })
            });

            const diResponse = await fetch(API_BASE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'upsert', table: 'calendar_config', data: diData })
            });

            const gbResult = await gbResponse.json();
            const diResult = await diResponse.json();

            if (gbResponse.ok && diResponse.ok) {
                showToast('success', 'Saved!', 'Calendar config saved to database');
                return true;
            } else {
                const error = !gbResponse.ok ? gbResult.error : diResult.error;
                showToast('error', 'Save Failed', error || 'Unknown error');
                return false;
            }
        } catch (e) {
            console.error('[SYNC_CONFIG] Calendar save failed:', e);
            showToast('error', 'Save Failed', e.message);
            return false;
        } finally {
            setCalendarSyncing(false);
        }
    };

    // Helper to get emoji for event abbreviation
    const getEventEmoji = (abbr) => {
        const emojiMap = {
            'GB': '🟠', 'DI': '🟣', 'GR': '🔵', 'GAR': '🟡', 
            'GW': '⚔️', 'SW': '🌟', 'DR': '🐉', 'AR': '🏃',
            'BH': '💀', 'WB': '👹', 'CO': '🏆', 'EV': '🎉'
        };
        return emojiMap[abbr] || '📣';
    };
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Reminders are now sent automatically via VPS cron job
    
    const showDebug = (title, obj) => {
        try {
            let d = document.getElementById('supabase-debug');
            if (!d) {
                d = document.createElement('div');
                d.id = 'supabase-debug';
                d.style.position = 'fixed';
                d.style.right = '12px';
                d.style.bottom = '12px';
                d.style.zIndex = 2000;
                d.style.background = 'rgba(0,0,0,0.8)';
                d.style.color = '#fff';
                d.style.padding = '12px';
                d.style.borderRadius = '8px';
                d.style.maxWidth = '460px';
                d.style.fontSize = '12px';
                d.style.fontFamily = 'monospace';
                d.style.whiteSpace = 'pre-wrap';
                d.style.boxShadow = '0 8px 32px rgba(0,0,0,0.6)';
                document.body.appendChild(d);
            }
            let text = title;
            if (obj) {
                if (obj.message) text += '\n' + obj.message;
                else if (obj.err) text += '\n' + JSON.stringify(obj.err);
                else text += '\n' + (typeof obj === 'string' ? obj : JSON.stringify(obj));
            }
            d.innerText = text;
            console.error(title, obj);
        } catch (e) { console.error('showDebug failed', e); }
    };

    const showToast = (type, title, message) => { setToast({ type, title, message }); setTimeout(() => setToast(null), 4000); }; 
    
    // ========== GUIDE FUNCTIONS ==========
    const saveGuidesToStorage = (guidesArray) => {
        try {
            // Strip base64 data URLs before saving to localStorage to avoid quota issues
            // Base64 images will be saved to Supabase, but we keep external URLs in localStorage
            const guidesForStorage = guidesArray.map(g => ({
                ...g,
                backgroundImage: (g.backgroundImage && typeof g.backgroundImage === 'string' && g.backgroundImage.startsWith('data:')) ? '' : g.backgroundImage
            }));
            localStorage.setItem('hot_guides', JSON.stringify(guidesForStorage));
        } catch (e) { console.error('Failed to save guides to localStorage', e); }
    };

    // Helper function to compress images to keep database size manageable
    const compressImage = (file, callback) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                
                // Resize if image is too large
                if (width > 800 || height > 600) {
                    const ratio = Math.min(800 / width, 600 / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }
                
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                // Compress to JPEG with quality 0.7
                const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                callback(compressedDataUrl);
            };
        };
    };

    // Compress and upload background image to MinIO
    const uploadBackgroundImage = async (file, guideSlug, callback, errorCallback) => {
        try {
            // Compress the image first
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async (event) => {
                try {
                    const img = new Image();
                    img.src = event.target.result;
                    img.onload = async () => {
                        try {
                            const canvas = document.createElement('canvas');
                            let width = img.width;
                            let height = img.height;
                            
                            // Resize if image is too large
                            if (width > 800 || height > 600) {
                                const ratio = Math.min(800 / width, 600 / height);
                                width = Math.round(width * ratio);
                                height = Math.round(height * ratio);
                            }
                            
                            canvas.width = width;
                            canvas.height = height;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0, width, height);
                            
                            // Convert canvas to blob
                            canvas.toBlob(async (blob) => {
                                try {
                                    // Create FormData and upload to MinIO
                                    const formData = new FormData();
                                    formData.append('file', blob, 'background.jpg');
                                    
                                    const endpoint = `/api/upload?guideSlug=${encodeURIComponent(guideSlug)}&type=background`;
                                    const response = await fetch(endpoint, {
                                        method: 'POST',
                                        body: formData
                                    });
                                    
                                    if (!response.ok) {
                                        const error = await response.text();
                                        throw new Error(`Upload failed: ${response.status} - ${error}`);
                                    }
                                    
                                    const data = await response.json();
                                    callback(data.url);
                                } catch (err) {
                                    console.error('Background upload error:', err);
                                    errorCallback(err.message);
                                }
                            }, 'image/jpeg', 0.7);
                        } catch (err) {
                            console.error('Canvas error:', err);
                            errorCallback(err.message);
                        }
                    };
                } catch (err) {
                    console.error('Image load error:', err);
                    errorCallback(err.message);
                }
            };
        } catch (err) {
            console.error('File read error:', err);
            errorCallback(err.message);
        }
    };

    // Load guides from Supabase (with localStorage fallback)
    const loadGuidesFromSupabase = async () => {
        try {
            // Load from PostgreSQL API instead of Supabase
            const data = await apiCall('get', 'guides');
            console.log('[LOAD_GUIDES] Raw data from API:', data);
            if (Array.isArray(data)) {
                // Map snake_case fields to camelCase for consistency
                const mapped = data.map(g => ({
                    id: g.id,
                    title: g.title,
                    description: g.description,
                    content: g.content,
                    backgroundImage: g.background_image || '',
                    createdAt: g.created_at,
                    updatedAt: g.updated_at,
                    isPinned: g.is_pinned || false,
                    reminderMessage: g.reminder_message,
                    guideText: g.guide_text,
                    guideUrl: g.guide_url
                }));
                console.log('[LOAD_GUIDES] Mapped guides:', mapped);
                return mapped;
            }
            return null;
        } catch (e) {
            console.warn('PostgreSQL guides load error, using localStorage:', e);
            return null;
        }
    };

    // Save guide to Supabase (insert or update)
    const saveGuideToSupabase = async (guide) => {
        try {
            console.log('[SAVE_GUIDE] Starting save for guide:', guide);
            
            // Check if this is a new guide (has large timestamp ID like 1717796418491) vs existing (small sequential like 1-7)
            const guideId = typeof guide.id === 'number' ? guide.id : parseInt(guide.id, 10);
            const isNewGuide = guideId > 10000000000; // Timestamp IDs are huge, sequential IDs are small
            
            console.log('[SAVE_GUIDE] Is new guide?', isNewGuide, 'ID:', guideId);
            
            // For existing guides (small sequential IDs), try UPDATE first
            if (!isNewGuide) {
                try {
                    console.log('[SAVE_GUIDE] Attempting update with id:', guideId);
                    await apiCall('update', 'guides', {
                        id: guideId,
                        data: {
                            title: guide.title,
                            description: guide.description,
                            content: guide.content,
                            background_image: guide.backgroundImage,
                            is_pinned: guide.isPinned || false,
                            guide_url: guide.guideUrl || guide.guide_url || null,
                            updated_at: new Date().toISOString()
                        }
                    });
                    console.log('[SAVE_GUIDE] Update succeeded');
                    return true;
                } catch (updateError) {
                    console.warn('[SAVE_GUIDE] Update failed:', updateError.message);
                    // Fall through to insert below
                }
            }
            
            // For new guides OR if update failed, do INSERT
            try {
                // Calculate next sequential ID from guides in state
                let nextId = 1;
                if (guides && guides.length > 0) {
                    // Find max ID from current guides in state
                    const numericIds = guides
                        .map(g => typeof g.id === 'number' ? g.id : parseInt(g.id, 10))
                        .filter(id => !isNaN(id) && id > 0 && id < 10000000000); // Exclude timestamp IDs
                    if (numericIds.length > 0) {
                        nextId = Math.max(...numericIds) + 1;
                    }
                }
                console.log('[SAVE_GUIDE] Inserting new guide with sequential ID:', nextId);
                
                await apiCall('insert', 'guides', {
                    data: {
                        id: nextId,
                        title: guide.title,
                        description: guide.description,
                        content: guide.content,
                        background_image: guide.backgroundImage,
                        is_pinned: guide.isPinned || false,
                        guide_url: guide.guideUrl || guide.guide_url || null,
                        created_at: guide.createdAt,
                        updated_at: new Date().toISOString()
                    }
                });
                console.log('[SAVE_GUIDE] Insert succeeded with final ID:', nextId);
                
                // Update the guide's ID in state after successful save
                const updatedGuide = { ...guide, id: nextId };
                const updatedGuides = guides.map(g => g.title === guide.title && g.createdAt === guide.createdAt ? updatedGuide : g);
                setGuides(updatedGuides);
                return true;
            } catch (insertError) {
                console.error('[SAVE_GUIDE] Insert failed:', insertError);
                return false;
            }
        } catch (e) {
            console.error('PostgreSQL guide save error:', e);
            return false;
        }
    };

    // Delete guide from PostgreSQL
    const deleteGuideFromSupabase = async (guideId) => {
        try {
            await apiCall('delete', 'guides', { id: guideId });

            return true;
        } catch (e) {
            console.error('PostgreSQL guide delete error:', e);
            return false;
        }
    };

    // Toggle pin status for a guide
    const togglePinGuide = async (guideId) => {
        const guide = guides.find(g => g.id === guideId);
        if (!guide) return;
        
        const isPinned = !guide.isPinned;
        const updatedGuides = guides.map(g => 
            g.id === guideId 
                ? { ...g, isPinned: isPinned, updatedAt: new Date().toISOString() }
                : g
        );
        
        setGuides(updatedGuides);
        saveGuidesToStorage(updatedGuides);
        
        // Save to PostgreSQL
        try {
            await saveGuideToSupabase({ ...guide, isPinned });
        } catch (e) {
            console.error('Error updating guide pin status:', e);
        }
        
        // Save to Supabase
        try {
            await supabase
                .from('guides')
                .update({
                    is_pinned: isPinned,
                    updated_at: new Date().toISOString()
                })
                .eq('id', guideId);
        } catch (e) {
            console.error('Failed to update pin status:', e);
        }
        
        showToast('success', isPinned ? '📌 Pinned' : '📌 Unpinned', `Guide ${isPinned ? 'pinned' : 'unpinned'} successfully`);
    };

    // Helper function to insert markdown formatting
    const insertFormatting = (fieldName, before, after = '', defaultText = 'text') => {
        const textarea = document.querySelector(`textarea[data-guide-field="${fieldName}"]`);
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = fieldName === 'content' ? editingGuideData.content : (fieldName === 'title' ? editingGuideData.title : editingGuideData.description);
        let selectedText = currentValue.substring(start, end);
        if (!selectedText && defaultText) {
            selectedText = defaultText;
        }
        const newValue = currentValue.substring(0, start) + before + selectedText + after + currentValue.substring(end);

        // Update the content
        debouncedUpdateGuide(editingGuideId, fieldName, newValue);
        
        // Restore cursor position
        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = start + before.length;
            textarea.selectionEnd = start + before.length + selectedText.length;
        }, 0);
    };

    // Debounced update for guide edits - delays save until user stops typing (500ms)
    // Saves to Supabase and localStorage
    const debouncedUpdateGuide = (guideId, fieldName, value) => {
        // Update local editing state immediately (so user sees their typing)
        setEditingGuideData(prev => ({ ...prev, [fieldName]: value }));
        
        // Clear previous timer for this field
        if (guideDebounceTimers.current[fieldName]) {
            clearTimeout(guideDebounceTimers.current[fieldName]);
        }
        
        // Set new timer to save after 500ms of inactivity
        guideDebounceTimers.current[fieldName] = setTimeout(() => {
            // Build updated guides array with new value
            const updatedGuides = guides.map(g => {
                if (g.id === guideId) {
                    const updated = { ...g, [fieldName]: value, updatedAt: new Date().toISOString() };
                    // If title changed, regenerate guide_url
                    if (fieldName === 'title') {
                        const guideSlug = slugify(value);
                        updated.guideUrl = `#guides/${guideSlug}`;
                    }
                    return updated;
                }
                return g;
            });
            
            // Update guides array to reflect in card preview
            setGuides(updatedGuides);
            
            // Save to localStorage
            try {
                localStorage.setItem('hot_guides', JSON.stringify(updatedGuides));
            } catch (e) { 
                console.error('Failed to save guide to localStorage', e); 
            }
            
            // Save to Supabase asynchronously (don't wait for response)
            const updatedGuide = updatedGuides.find(g => g.id === guideId);
            if (updatedGuide) {
                saveGuideToSupabase(updatedGuide).catch(e => console.error('Supabase save failed:', e));
            }
        }, 500);
    };

    const createGuide = async () => {
        if (!newGuide.title.trim()) {
            showToast('error', 'Invalid Guide', 'Please enter a title');
            return;
        }
        
        // Auto-generate guide URL from title
        const guideSlug = slugify(newGuide.title);
        const guideUrl = `#guides/${guideSlug}`;
        
        const guide = {
            id: Date.now(),
            title: newGuide.title,
            description: newGuide.description,
            content: newGuide.content,
            backgroundImage: newGuide.backgroundImage,
            guideUrl: guideUrl,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
        const updatedGuides = [...guides, guide];
        setGuides(updatedGuides);
        saveGuidesToStorage(updatedGuides);
        
        // Save to Supabase
        const saved = await saveGuideToSupabase(guide);
        if (saved) {
        } else {
            console.warn('Failed to sync guide to Supabase, but saved locally');
        }
        
        setNewGuide({ title: '', description: '', content: '', backgroundImage: '' });
        showToast('success', 'Guide Created', `"${guide.title}" created successfully`);
    };

    const updateGuide = async (guideId, updatedData) => {
        const updatedGuides = guides.map(g => 
            g.id === guideId 
                ? { ...g, ...updatedData, updatedAt: new Date().toISOString() }
                : g
        );
        setGuides(updatedGuides);
        saveGuidesToStorage(updatedGuides);
        showToast('success', 'Guide Updated', 'Guide updated successfully');
    };

    const deleteGuide = async (guideId) => {
        const guideName = guides.find(g => g.id === guideId)?.title || 'Guide';
        const updatedGuides = guides.filter(g => g.id !== guideId);
        setGuides(updatedGuides);
        saveGuidesToStorage(updatedGuides);
        
        // Delete from PostgreSQL
        try {
            await deleteGuideFromSupabase(guideId);
        } catch (e) {
            console.error('Error deleting guide from PostgreSQL:', e);
        }
        
        setGuideToDelete(null);
        setSelectedGuideId(null);
        setEditingGuideId(null);
        showToast('success', 'Guide Deleted', `"${guideName}" deleted successfully`);
    };
    
    // ========== ANALYTICS FUNCTIONS ==========
    // Calculate stats for a specific day type across all events
    const getStatsPerDay = useCallback(() => {
        // Determine which contributions to use
        let contribsToUse = memberContributions;
        
        // If "All Events" is selected, we need to load all historical data
        if (selectedEvent?.id === 'ALL') {
            // Load all contributions from Supabase (this would be async, so for now we'll display a message)
            // For now, show that we're using aggregate data
            if (memberContributions.length === 0 && events.length > 0) {
                return Array(6).fill(null).map((_, i) => ({
                    dayNumber: i + 1,
                    dayName: DAY_NAMES[i],
                    dayIcon: DAY_ICONS[i],
                    scores: [],
                    median: 0,
                    p10: 0,
                    p90: 0,
                    min: 0,
                    max: 0,
                    guildTotals: []
                }));
            }
        }
        
        if (events.length === 0 || !contribsToUse || contribsToUse.length === 0) {
            return Array(6).fill(null).map((_, i) => ({
                dayNumber: i + 1,
                dayName: DAY_NAMES[i],
                dayIcon: DAY_ICONS[i],
                scores: [],
                median: 0,
                p10: 0,
                p90: 0,
                min: 0,
                max: 0,
                guildTotals: []
            }));
        }

        const statsByDay = Array(6).fill(null).map(() => ({ scores: [], guildTotals: [] }));
        
        // Group contributions by day and collect guild totals
        events.forEach(event => {
            const eventContribs = contribsToUse.filter(c => c.event_id === event.id);
            const guildDailyTotals = Array(6).fill(0);
            
            eventContribs.forEach(c => {
                if (c.day_number >= 1 && c.day_number <= 6) {
                    const dayIdx = c.day_number - 1;
                    statsByDay[dayIdx].scores.push(Number(c.score) || 0);
                    guildDailyTotals[dayIdx] += (Number(c.score) || 0);
                }
            });
            
            guildDailyTotals.forEach((total, idx) => {
                if (total > 0) statsByDay[idx].guildTotals.push(total);
            });
        });

        // Calculate percentiles and stats for each day
        const calculatePercentile = (arr, p) => {
            if (arr.length === 0) return 0;
            const sorted = arr.sort((a, b) => a - b);
            const idx = Math.ceil((p / 100) * sorted.length) - 1;
            return sorted[Math.max(0, idx)];
        };

        return statsByDay.map((dayStats, idx) => {
            const scores = dayStats.scores.filter(s => s > 0);
            const guildTotals = dayStats.guildTotals;
            
            const sorted = [...scores].sort((a, b) => a - b);
            const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0;
            const p10 = calculatePercentile([...scores], 10);
            const p90 = calculatePercentile([...scores], 90);
            const min = scores.length > 0 ? Math.min(...scores) : 0;
            const max = scores.length > 0 ? Math.max(...scores) : 0;

            return {
                dayNumber: idx + 1,
                dayName: DAY_NAMES[idx],
                dayIcon: DAY_ICONS[idx],
                scores,
                median,
                p10,
                p90,
                min,
                max,
                guildTotals
            };
        });
    }, [selectedEvent?.id, memberContributions, events]);

    // Get bottom performers - members with zero scores, below threshold, or bottom 10% per day
    const getBottomPerformers = useCallback(() => {
        if (!selectedEvent || members.length === 0) return [];
        
        let contribsToAnalyze = Array.isArray(memberContributions) ? memberContributions : [];
        const isAllEvents = selectedEvent.id === 'ALL';
        
        // Filter members based on event time (for individual events)
        let membersToAnalyze = members;
        if (!isAllEvents && selectedEvent.date) {
            const eventDate = new Date(selectedEvent.date);
            membersToAnalyze = members.filter(m => {
                if (m.joined_date && new Date(m.joined_date) > eventDate) return false;
                if (m.left_date && new Date(m.left_date) < eventDate) return false;
                return true;
            });
        }
        
        const memberPerformance = {};
        membersToAnalyze.forEach(m => {
            memberPerformance[m.id] = {
                id: m.id,
                name: m.display_name || m.name,
                status: m.is_active === false ? 'inactive' : 'active',
                allScores: [], // All individual scores
                total: 0,
                zeroCount: 0,
                belowThresholdCount: 0,
                avgScore: 0
            };
        });

        // For "All Events", collect ALL scores. For individual event, track by day
        if (isAllEvents) {
            // Collect ALL individual scores for each member WITH day information
            contribsToAnalyze.forEach(c => {
                if (memberPerformance[c.member_id]) {
                    let score = c.score;
                    if (typeof score === 'string') {
                        score = parseFloat(score) || 0;
                    } else {
                        score = Number(score) || 0;
                    }
                    // Store object with day info instead of just the score
                    memberPerformance[c.member_id].allScores.push({
                        score: score,
                        day: c.day_number,
                        eventId: c.event_id
                    });
                }
            });
            
            // Calculate metrics for All Events
            Object.values(memberPerformance).forEach(m => {
                m.total = m.allScores.reduce((s, v) => s + v.score, 0);
                m.zeroCount = m.allScores.filter(s => s.score === 0).length;
                m.belowThresholdCount = m.allScores.filter(s => s.score > 0 && s.score < MINIMUM_SCORE).length;
                m.avgScore = m.allScores.length > 0 ? m.allScores.reduce((a, b) => a + b.score, 0) / m.allScores.length : 0;
            });
        } else {
            // Original logic for individual events (by day)
            const scoresByDay = {};
            membersToAnalyze.forEach(m => {
                scoresByDay[m.id] = Array(6).fill(0);
            });
            
            contribsToAnalyze.forEach(c => {
                if (scoresByDay[c.member_id] && c.day_number >= 1 && c.day_number <= 6) {
                    scoresByDay[c.member_id][c.day_number - 1] = Number(c.score) || 0;
                }
            });
            
            Object.values(memberPerformance).forEach(m => {
                const dayScores = scoresByDay[m.id];
                m.total = dayScores.reduce((s, v) => s + v, 0);
                m.zeroCount = dayScores.filter(s => s === 0).length;
                m.belowThresholdCount = dayScores.filter(s => s > 0 && s < MINIMUM_SCORE).length;
                m.avgScore = dayScores.length > 0 ? dayScores.reduce((a, b) => a + b, 0) / dayScores.length : 0;
            });
        }

        // Filter for bottom performers: those with zero scores, below threshold, or very low average
        // AND only show active members
        let bottomPerformers = Object.values(memberPerformance).filter(m => {
            const hasIssues = m.zeroCount > 0 || m.belowThresholdCount > 0 || m.total === 0;
            const isActive = m.status === 'active';
            
            if (!hasIssues || !isActive) return false;
            
            // For "All Events": only show if they had issues in the last 2 events
            if (isAllEvents) {
                const sortedEvents = [...events].sort((a, b) => b.date - a.date);
                const last2EventIds = sortedEvents.slice(0, 2).map(e => e.id);
                const last2EventsContribs = contribsToAnalyze.filter(c => 
                    last2EventIds.includes(c.event_id) && c.member_id === m.id
                );
                const hasIssuesInLast2 = last2EventsContribs.some(c => 
                    c.score === 0 || (c.score > 0 && c.score < MINIMUM_SCORE)
                );
                return hasIssuesInLast2;
            }
            
            // For individual events: show if they have general issues (original behavior)
            return true;
        });

        // Sort by total score (ascending) and then by zero count
        (Array.isArray(bottomPerformers) ? bottomPerformers : []).sort((a, b) => {
            if (a.total === 0 && b.total === 0) return b.zeroCount - a.zeroCount;
            if (a.total === 0) return -1;
            if (b.total === 0) return 1;
            return a.total - b.total;
        });

        return bottomPerformers;
    }, [selectedEvent, members, memberContributions, events]);
    
    // Fetch all historical contributions for analytics
    const getAllHistoricalContributions = async () => {
        try {
            const { data: allContribs, error } = await supabase.from('member_contributions').select('*');
            if (error) throw error;
            return allContribs || [];
        } catch (e) {
            console.error('Failed to load historical contributions:', e);
            return [];
        }
    };
    
    // Fetch all historical daily scores for trend analysis
    const getAllHistoricalScores = async () => {
        try {
            const { data: allScores, error } = await supabase.from('daily_scores').select('*');
            if (error) throw error;
            return allScores || [];
        } catch (e) {
            console.error('Failed to load historical scores:', e);
            return [];
        }
    };
    
    // ========== END ANALYTICS FUNCTIONS ==========
    
    // Fetch role profile for a given auth user id (robust: uses maybeSingle and defensive logging)
    const fetchProfile = async (uid) => {
        if (!uid) return null;
        // Profiles table doesn't have role info in PostgreSQL, so just return null
        return null;
    };

    // Fetch member row by auth_id (if you store auth_id on members). Uses maybeSingle to avoid errors when not found.
    const fetchMemberByAuthId = async (uid) => {
        try {
            if (DEBUG) console.log('[Auth] Fetching member for auth_id:', uid);
            const members = await apiCall('get', 'members', { filters: { auth_id: uid } });
            const memberArray = Array.isArray(members) ? members : [];
            if (DEBUG) console.log('[Auth] Members found:', memberArray.length);
            if (memberArray.length === 0) {
                if (DEBUG) console.log('[Auth] No member found with auth_id:', uid);
                return null;
            }
            const member = memberArray[0];
            if (DEBUG) console.log('[Auth] Member found:', { id: member.id, name: member.name, role: member.role });
            return member;
        } catch (e) { 
            console.error('[Auth] Error fetching member:', e);
            return null; 
        }
    }; 

    // Sign in using Supabase auth (email + password). Uses member.role (members.auth_id) if present, else profiles.role
    const handleLogin = async () => {
        try {
            const email = (emailInput || '').trim();
            const password = passwordInput;
            if (!email || !password) { showToast('error', 'Missing credentials', 'Enter email and password'); return; }
            
            // Call our backend API for authentication
            const response = await fetch('/api/auth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            const result = await response.json();

            if (!response.ok) {
                showToast('error', 'Sign-in failed', result.error || 'Authentication failed');
                setPasswordInput('');
                return;
            }

            if (!result.authenticated) {
                showToast('error', 'Sign-in failed', result.error || 'Invalid credentials');
                setPasswordInput('');
                return;
            }

            const authUser = result.user;
            if (DEBUG) console.log('Auth successful:', authUser);

            // Store user info in memory (NOT in localStorage)
            const fakeUser = { id: authUser.id, email: authUser.email };
            setCurrentUser(fakeUser);
            setUserRole(authUser.role);
            const isAdmin = authUser.role === 'admin' || authUser.role === 'super_admin';
            setIsAuthenticated(isAdmin);

            // SECURITY FIX: Store session token in sessionStorage (persists across refreshes)
            // sessionStorage is cleared when browser closes, but persists across page refreshes
            // Pass this token in Authorization header for future API requests
            if (result.sessionToken) {
              setSessionToken(result.sessionToken);
              sessionStorage.setItem('sessionToken', result.sessionToken);
              sessionStorage.setItem('authUser', JSON.stringify(fakeUser));
              sessionStorage.setItem('userRole', authUser.role);
              if (DEBUG) console.log('[Auth] Session stored in sessionStorage');
            }

            setShowLoginModal(false);
            showToast('success', 'Signed In', isAdmin ? 'Admin access granted' : `Logged in as ${authUser.role || 'user'}`);
            setEmailInput('');
            setPasswordInput('');
        } catch (e) {
            if (DEBUG) console.error('login error', e);
            showToast('error', 'Error', e.message || 'Login failed');
            setPasswordInput('');
        }
    }; 

    
    const handleLogout = async () => {
        try {
            // No longer using Supabase Auth, just clear local state
            setIsAuthenticated(false);
            setCurrentUser(null);
            setUserRole(null);
            setSessionToken(null); // SECURITY: Clear session token on logout
            setActiveTab('dashboard');
            
            // Clear sessionStorage on logout
            sessionStorage.removeItem('sessionToken');
            sessionStorage.removeItem('authUser');
            sessionStorage.removeItem('userRole');
            
            showToast('success', 'Logged Out', '');
        } catch (e) {
            if (DEBUG) console.error('logout error', e);
            showToast('error', 'Error', e.message || 'Logout failed');
        }
    };
    
    // Handle window resize for responsive layout
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const { data } = await supabase.auth.getSession();
                const user = data?.session?.user;
                if (user) {
                    setCurrentUser(user);
                    const [profile, member] = await Promise.all([fetchProfile(user.id), fetchMemberByAuthId(user.id)]);
                    const effectiveRole = member?.role || profile?.role || null;
                    setUserRole(effectiveRole);
                    setIsAuthenticated(effectiveRole === 'admin' || effectiveRole === 'super_admin');
                }
            } catch (e) { showDebug('auth init error', e); }
        };
        init();

        // Keep auth state in sync
        const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
            const user = session?.user || null;
            setCurrentUser(user);
            if (!user) { setUserRole(null); setIsAuthenticated(false); return; }
            if (DEBUG) console.log('[Auth] State changed for user:', user.id);
            try {
                const profile = fetchProfile(user.id);
                if (profile && typeof profile.then === 'function') {
                    // If fetchProfile returns a promise
                    profile.then(p => {
                        fetchMemberByAuthId(user.id).then(member => {
                            const effectiveRole = member?.role || p?.role || null;
                            if (DEBUG) console.log('[Auth] Auth state change - effective role:', effectiveRole, 'Is admin:', effectiveRole === 'admin' || effectiveRole === 'super_admin');
                            setUserRole(effectiveRole);
                            setIsAuthenticated(effectiveRole === 'admin' || effectiveRole === 'super_admin');
                        }).catch(err => console.error('[Auth] Error fetching member:', err));
                    }).catch(err => console.error('[Auth] Error fetching profile:', err));
                } else {
                    // fetchProfile returns synchronously
                    fetchMemberByAuthId(user.id).then(member => {
                        const effectiveRole = member?.role || profile?.role || null;
                        if (DEBUG) console.log('[Auth] Auth state change - effective role:', effectiveRole, 'Is admin:', effectiveRole === 'admin' || effectiveRole === 'super_admin');
                        setUserRole(effectiveRole);
                        setIsAuthenticated(effectiveRole === 'admin' || effectiveRole === 'super_admin');
                    }).catch(err => console.error('[Auth] Error fetching member:', err));
                }
            } catch (e) {
                console.error('[Auth] Exception in onAuthStateChange:', e);
            }
        });
        return () => { listener?.subscription?.unsubscribe?.(); };
    }, []);
    
    // SECURITY FIX: Restore auth from sessionStorage (persists across refreshes, cleared on browser close)
    // sessionStorage is more secure than localStorage but still user-friendly for same-session access
    useEffect(() => {
        try {
            const savedSessionToken = sessionStorage.getItem('sessionToken');
            const savedAuthUser = sessionStorage.getItem('authUser');
            const savedUserRole = sessionStorage.getItem('userRole');
            
            if (savedSessionToken && savedAuthUser && savedUserRole) {
                const authUser = JSON.parse(savedAuthUser);
                const isAdmin = savedUserRole === 'admin' || savedUserRole === 'super_admin';
                
                setCurrentUser(authUser);
                setUserRole(savedUserRole);
                setIsAuthenticated(isAdmin);
                setSessionToken(savedSessionToken);
                
                if (DEBUG) console.log('[Auth] Restored session from sessionStorage, user:', authUser.email, 'role:', savedUserRole);
            }
        } catch (e) {
            if (DEBUG) console.error('[Auth] Failed to restore session:', e);
            // Clear corrupted session data
            sessionStorage.removeItem('sessionToken');
            sessionStorage.removeItem('authUser');
            sessionStorage.removeItem('userRole');
        }
    }, []);
    
    useEffect(() => { loadData(); }, []);

    // Close event dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.custom-event-dropdown')) {
                setEventDropdownOpen(false);
            }
        };
        if (eventDropdownOpen) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [eventDropdownOpen]);

    // Load calendar from Supabase on mount
    useEffect(() => { loadCalendarFromSupabase(); }, []);

    // Load guides from Supabase on mount
    useEffect(() => {
        const loadGuidesOnMount = async () => {
            const supabaseGuides = await loadGuidesFromSupabase();
            if (supabaseGuides && supabaseGuides.length > 0) {
                // Guides loaded from Supabase
                setGuides(supabaseGuides);
                saveGuidesToStorage(supabaseGuides);
            } else {
                // Fallback to localStorage
                try {
                    const saved = localStorage.getItem('hot_guides');
                    if (saved) {
                        const localGuides = JSON.parse(saved);
                        setGuides(localGuides);
                        // Sync local guides to Supabase
                        for (const guide of localGuides) {
                            await saveGuideToSupabase(guide);
                        }
                    }
                } catch (e) { 
                    console.error('Failed to load guides from localStorage', e); 
                }
            }
        };
        loadGuidesOnMount();
    }, []);

    // Persist calendar events to localStorage
    useEffect(() => {
        try { localStorage.setItem('hot_calendar_events', JSON.stringify(calendarEvents)); }
        catch (e) { console.error('Failed to save calendar to localStorage', e); }
        // Events are synced to PostgreSQL backend when user adds/edits/deletes them
    }, [calendarEvents]);

    // Load all events from PostgreSQL on mount
    useEffect(() => {
        const loadEventsFromBackend = async () => {
            try {
                if (DEBUG) console.log('[Calendar] Loading from:', EVENTS_API);
                const response = await fetch(EVENTS_API);
                if (!response.ok) {
                    if (response.status === 404) {
                        console.warn('[Calendar] Backend URL not found. Set REACT_APP_BACKEND_URL in environment.');
                        return;
                    }
                    throw new Error(`API error: ${response.status}`);
                }
                const data = await response.json();
                
                // Handle both response formats: {success, events} or direct array
                const events = data.events || (Array.isArray(data) ? data : []);
                
                if (events && Array.isArray(events) && events.length > 0) {
                    if (DEBUG) console.log('[Calendar] Loaded from backend:', events);
                    // Map database events to frontend format
                    const backendEvents = events.map(ev => ({
                        id: ev.id,
                        name: ev.name,
                        abbr: ev.abbr,
                        day: ev.day || '',
                        time: ev.time || '00:00',
                        frequency: ev.frequency || 'weekly',
                        color: ev.color || '#CCCCCC',
                        backgroundImage: ev.background_image,
                        date: ev.date || '',
                        startDate: ev.start_date || '',
                        day_of_month: ev.day_of_month,
                        eventTypeId: ev.event_type_id,
                        eventType: ev.event_type,
                        guideId: ev.guide_id,
                        guideText: ev.guide_text,
                        guideUrl: ev.guide_url,
                        reminderMessage: ev.reminder_message,
                        reminderMessages: ev.reminder_messages ? (typeof ev.reminder_messages === 'string' ? JSON.parse(ev.reminder_messages) : ev.reminder_messages) : {},
                        reminders: ev.reminders
                    }));
                    setCalendarEvents(backendEvents);
                    if (DEBUG) console.log('[Calendar] Populated calendar with', backendEvents.length, 'events from database');
                } else {
                    if (DEBUG) console.log('[Calendar] No events in backend response');
                }
            } catch (e) {
                console.warn('[Calendar] Backend events not available:', e.message);
                // Don't block page load if backend is unavailable
            }
        };
        
        loadEventsFromBackend();
    }, []);

    // Load event templates from PostgreSQL on mount
    useEffect(() => {
        const loadTemplatesFromBackend = async () => {
            try {
                const response = await fetch('/api/event-templates');
                if (!response.ok) {
                    if (response.status === 404) {
                        console.warn('[Templates] Backend URL not found.');
                        return;
                    }
                    throw new Error(`API error: ${response.status}`);
                }
                const data = await response.json();
                
                const templates = data.templates || [];
                if (templates && Array.isArray(templates)) {
                    if (DEBUG) console.log('[Templates] Loaded from backend:', templates);
                    setEventTemplates(templates);
                    
                    // If no templates exist, seed them
                    if (templates.length === 0) {
                        await fetch('/api/event-templates/seed', { method: 'POST' });
                        // Reload templates after seeding
                        const seedResponse = await fetch('/api/event-templates');
                        if (seedResponse.ok) {
                            const seedData = await seedResponse.json();
                            setEventTemplates(seedData.templates || []);
                        }
                    }
                }
            } catch (e) {
                console.warn('[Templates] Backend templates not available:', e.message);
            }
        };
        
        loadTemplatesFromBackend();
    }, []);

    // Keep the active tab in sync with the hash (hash routing)
    useEffect(() => {
        const onHashChange = () => {
            const hash = window.location.hash && window.location.hash.replace('#','');
            
            // Check if it's a guide URL (guides/slug)
            if (hash && hash.startsWith('guides/')) {
                const slug = hash.split('/')[1];
                const guide = guides.find(g => slugify(g.title) === slug);
                
                if (guide) {
                    setActiveTab('guides');
                    setSelectedGuideId(guide.id);
                    // Don't reset editingGuideId if we're currently editing - only set it on hash navigation
                    if (!editingGuideId) {
                        setEditingGuideId(null);
                    }
                } else {
                    // If guide not found, stay on guides tab but no guide selected
                    setActiveTab('guides');
                    setSelectedGuideId(null);
                    setEditingGuideId(null);
                }
            } else {
                const tab = hash || initialTab || 'dashboard';
                setActiveTab(tab);
                // Don't reset selected/editing state if we're navigating within guides
                if (tab !== 'guides') {
                    setSelectedGuideId(null);
                    setEditingGuideId(null);
                }
            }
        };
        
        // Call on mount to handle direct URL access
        onHashChange();
        
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, [guides, editingGuideId]);

    // Map guide URLs to guide IDs for events that have URLs but no IDs
    useEffect(() => {
        if (calendarEvents && guides && guides.length > 0) {
            const updatedEvents = calendarEvents.map(event => {
                // If event has guide_url/guideUrl but no guideId, try to find the guide
                if ((event.guideUrl || event.guide_url) && !event.guideId) {
                    const guideUrl = event.guideUrl || event.guide_url;
                    // Find guide by matching URL or title slugified
                    const matchedGuide = guides.find(g => 
                        g.guideUrl === guideUrl || 
                        g.guide_url === guideUrl ||
                        slugify(g.title) === guideUrl.replace('#guides/', '')
                    );
                    if (matchedGuide) {
                        return { ...event, guideId: matchedGuide.id };
                    }
                }
                return event;
            });
            // Only update if something changed
            const hasChanges = updatedEvents.some((ev, i) => ev.guideId !== calendarEvents[i]?.guideId);
            if (hasChanges) {
                setCalendarEvents(updatedEvents);
                if (DEBUG) console.log('[Calendar] Mapped guide URLs to IDs');
            }
        }
    }, [calendarEvents, guides]);

    // Load all historical data when "All Events" is selected
    useEffect(() => {
        // Data is now loaded via loadData() and API calls
        // No additional loading needed here - memberContributions are already set
        if (selectedEvent?.id === 'ALL') {
            if (DEBUG) console.log(`All Events selected - using existing ${memberContributions.length} contributions`);
        }
    }, [selectedEvent?.id, memberContributions.length]);

    // Ensure the URL hash reflects the initial tab
    useEffect(() => {
        const current = window.location.hash.replace('#','');
        if (!current && initialTab) window.location.hash = initialTab;
    }, []);

    // Save guides to localStorage whenever they change
    useEffect(() => {
        saveGuidesToStorage(guides);
    }, [guides]);

    // Auto-save calendar config when reminder times change
    useEffect(() => {
        if (!calendarConfig) return;
        const timer = setTimeout(() => {
            // Save separate reminder times for each event type
            // Don't include rotating_event_* fields to avoid contamination
            const gbData = {
                id: '1',
                anchor: calendarConfig.gb_anchor,
                interval_days: String(calendarConfig.gb_interval_days),
                time_slots: calendarConfig.time_slots,
                reminder_times_minutes: calendarConfig.gb_reminder_times_minutes || [5, 30, 60, 240],
                // Don't include rotating_event_* to avoid spreading merged data
                updated_at: new Date().toISOString()
            };
            const diData = {
                id: '2',
                anchor: calendarConfig.di_anchor,
                interval_days: String(calendarConfig.di_interval_days),
                time_slots: calendarConfig.time_slots,
                reminder_times_minutes: calendarConfig.di_reminder_times_minutes || [5, 30, 60, 240],
                // Don't include rotating_event_* to avoid spreading merged data
                updated_at: new Date().toISOString()
            };
            ( async () => {
                try {
                    const gbResp = await fetch(API_BASE, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'upsert', table: 'calendar_config', data: gbData }) });
                    const diResp = await fetch(API_BASE, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'upsert', table: 'calendar_config', data: diData }) });
                    console.log('[AUTO_SAVE_REMINDERS] Saved reminder times:', gbResp.ok && diResp.ok);
                } catch (e) {
                    console.error('[AUTO_SAVE_REMINDERS] Failed:', e.message);
                }
            })();
        }, 1000); // Debounce saves by 1 second
        return () => clearTimeout(timer);
    }, [JSON.stringify(calendarConfig.gb_reminder_times_minutes), JSON.stringify(calendarConfig.di_reminder_times_minutes)]);

    // Initialize editing data when entering/exiting edit mode
    useEffect(() => {
        if (editingGuideId) {
            const guide = guides.find(g => g.id === editingGuideId);
            if (guide) {
                setEditingGuideData({
                    title: guide.title,
                    description: guide.description,
                    content: guide.content,
                    backgroundImage: guide.backgroundImage || '',
                    guideUrl: guide.guideUrl || ''
                });
            }
        } else {
            // Clear editing data when exiting edit mode
            setEditingGuideData({ title: '', description: '', content: '', backgroundImage: '', guideUrl: '' });
        }
    }, [editingGuideId]);

    const navigateTo = (tab) => { setActiveTab(tab); try { if (window.location.hash.replace('#','') !== tab) window.location.hash = tab; } catch (e) {} };

    // Slugify function to convert guide title to URL-friendly slug
    const slugify = (str) => {
        return str
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '') // Remove emojis and special characters, keep only letters/numbers
            .replace(/[\s_]+/g, '-')      // Replace spaces/underscores with hyphens
            .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
    };

    // Navigate to specific guide with URL
    const navigateToGuide = (guideId) => {
        const guide = guides.find(g => g.id === guideId);
        if (!guide) return;
        
        const slug = slugify(guide.title);
        setSelectedGuideId(guideId);
        setEditingGuideId(null);
        try { 
            window.location.hash = `guides/${slug}`;
        } catch (e) {}
    };

    const loadData = async () => {
        setLoading(true);
        try { 
            const eventsData = await loadEvents();
            await loadMembers();
            // Auto-select "All Events" by default
            if (eventsData && eventsData.length > 0) {
                // Load all events data
                await loadEventData('ALL');
                setSelectedEvent({ id: 'ALL', name: 'All Events', opponent: 'All Opponents' });
            }
        }
        catch (e) { console.error(e); showDebug('loadData error', e); showToast('error', 'Error', e?.message || 'Failed to load data'); }
        setLoading(false);
    }; 

    const loadEvents = async () => {
        try {
            // Fetch events from API (not Supabase stub)
            const data = await apiCall('get', 'events', { limit: 10000 });
            if (DEBUG) console.log('📅 Loaded events:', Array.isArray(data) ? data.length : 0);
            if (Array.isArray(data)) {
                if (DEBUG) data.forEach(e => console.log(`   - Event ${e.id}: ${e.name}`));
                setEvents(data);
                return data;
            } else {
                showDebug('loadEvents: unexpected data format', data);
                return [];
            }
        } catch (error) { 
            showDebug('loadEvents error', error); 
            throw error; 
        }
    }; 

    const loadMembers = async () => {
        try {
            const data = await apiCall('get', 'members');
            if (Array.isArray(data)) {
                // Ensure aliases is parsed as an array and is_alt is boolean for each member
                const processedMembers = data.map(m => ({
                    ...m,
                    aliases: parseAliases(m.aliases),
                    is_alt: m.is_alt === true
                }));
                setMembers(processedMembers);

                
                // Load member periods asynchronously in the background (don't wait)
                // Members display immediately, periods load without blocking
                loadMemberPeriodsAsync(processedMembers);
            } else {
                showDebug('loadMembers: unexpected data format', data);
                setMembers([]);
            }
        } catch (error) { 
            showDebug('loadMembers error', error); 
            throw error; 
        }
    };
    
    /**
     * Load periods asynchronously without blocking members display
     * Fetches all periods from the backend in a single efficient query
     */
    const loadMemberPeriodsAsync = async (membersArray) => {
        try {
            // Fetch all periods at once from the backend bulk endpoint (via Vercel proxy)
            const response = await fetch(`/api/periods`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (!response.ok) {
                console.warn(`[Periods] API returned status ${response.status}`);
                return;
            }
            
            const periodsMap = await response.json();
            setMemberPeriods(periodsMap);
            
            const totalPeriods = Object.values(periodsMap).flat().length;
        } catch (error) {
            console.error('❌ Error loading periods:', error.message);
        }
    }; 

    const loadEventData = async (eventId) => {
        try {
            if (eventId === 'ALL') {
                // For 'ALL', fetch all data from the API
                const contribData = await apiCall('get', 'member_contributions', { limit: 10000 });
                const scoresData = await apiCall('get', 'daily_scores', { limit: 10000 });
                if (DEBUG) console.log('📊 Loaded ALL events - Contributions:', Array.isArray(contribData) ? contribData.length : 0, 'Scores:', Array.isArray(scoresData) ? scoresData.length : 0);
                setMemberContributions(Array.isArray(contribData) ? contribData : []);
                setDailyScores(Array.isArray(scoresData) ? scoresData : []);
            } else {
                // For specific event, use filters - ensure eventId is a number
                const eventIdNum = parseInt(eventId);
                
                const contribData = await apiCall('get', 'member_contributions', { filters: { event_id: eventIdNum }, limit: 10000 });
                const scoresData = await apiCall('get', 'daily_scores', { filters: { event_id: eventIdNum }, limit: 10000 });
                
                const contribCount = Array.isArray(contribData) ? contribData.length : 0;
                const scoresCount = Array.isArray(scoresData) ? scoresData.length : 0;
                
                setMemberContributions(Array.isArray(contribData) ? contribData : []);
                setDailyScores(Array.isArray(scoresData) ? scoresData : []);
            }
        } catch (error) { 
            console.error('❌ loadEventData error:', error);
            showDebug('loadEventData error', error);
            setMemberContributions([]);
            setDailyScores([]);
        }
    }; 

    const loadMemberAllContributions = async (memberId) => {
        // First fetch contributions (no embedded relations) to avoid malformed select errors
        const { data: contribData, error: contribError } = await supabase.from('member_contributions').select('id,member_id,event_id,day_number,score,created_at,updated_at').eq('member_id', memberId);
        if (contribError) { 
            showDebug('loadMemberAllContributions error', contribError); 
            showToast('error', 'Scores Load Failed', contribError.message || 'Unable to load member contributions');
            setMemberAllContributions([]); 
            return []; 
        }
        const contribs = contribData || [];

        // Collect unique event IDs (coerce to numbers) and fetch events separately
        const eventIds = [...new Set(contribs.map(c => (c.event_id ? parseInt(c.event_id, 10) : null)).filter(Boolean))];
        let eventsMap = {};
        if (eventIds.length > 0) {
            // showDebug('loadMemberAllContributions.eventIds', eventIds);
            let eventsData = null, eventsError = null;
            try {
                if (eventIds.length <= 12) {
                    const orClause = eventIds.map(id => `id.eq.${id}`).join(',');
                    // showDebug('loadMemberAllContributions.orClause', orClause);
                    ({ data: eventsData, error: eventsError } = await supabase.from('events').select('id,name,opponent,event_date,end_date').or(orClause));
                } else {
                    ({ data: eventsData, error: eventsError } = await supabase.from('events').select('id,name,opponent,event_date,end_date').in('id', eventIds));
                }
            } catch (ex) {
                showDebug('loadMemberAllContributions.events exception', ex?.message || ex);
            }

            if (eventsError) showDebug('loadMemberAllContributions.events error', eventsError);
            // showDebug('loadMemberAllContributions.eventsResult', { eventsCount: (eventsData || []).length, requested: eventIds.length });

            // If OR/IN failed or returned fewer rows, fetch individually as a final fallback
            if (!eventsData || (eventsData || []).length < eventIds.length) {
                // showDebug('loadMemberAllContributions.fallback', 'fetching individually');
                try {
                    const fetched = await Promise.all(eventIds.map(async id => {
                        try {
                            const { data: singleData, error: singleError } = await supabase.from('events').select('id,name,opponent,event_date,end_date').eq('id', id);
                            if (singleError) { showDebug('loadMemberAllContributions.singleEventError', { id, err: singleError }); return null; }
                            // data may be an array; take first
                            if (Array.isArray(singleData)) return singleData[0] || null;
                            return singleData || null;
                        } catch (e) { showDebug('loadMemberAllContributions.singleEventException', { id, err: e?.message || e }); return null; }
                    }));
                    const valid = (fetched || []).filter(Boolean);
                    if (valid.length > 0) {
                        eventsData = (eventsData || []).concat(valid);
                    }
                } catch (e) { showDebug('loadMemberAllContributions.fallbackException', e?.message || e); }
            }

            eventsMap = (eventsData || []).reduce((m, e) => { m[e.id] = e; return m; }, {});
            const missing = eventIds.filter(id => !eventsMap[id]);
            // if (missing.length > 0) showDebug('loadMemberAllContributions.missingEvents', { missing });
        }

        const grouped = {};
        contribs.forEach(c => {
            if (!grouped[c.event_id]) grouped[c.event_id] = { event: eventsMap[c.event_id] || null, scores: Array(6).fill(0) };
            if (c.day_number >= 1 && c.day_number <= 6) grouped[c.event_id].scores[c.day_number - 1] = parseFloat(c.score) || 0;
        });
        const arr = Object.values(grouped).sort((a, b) => new Date(b.event?.event_date || 0).getTime() - new Date(a.event?.event_date || 0).getTime());
        setMemberAllContributions(arr);
        // showDebug('loadMemberAllContributions loaded', { memberId, contributions: contribs.length, eventsFetched: Object.keys(eventsMap).length, count: arr.length, sample: arr.slice(0, 2) });
        return arr;
    };

    const handleSelectMember = async (member) => {
        // reset any temporary UI hints when changing selection
        setShowAuthHint(false);
        // Ensure aliases are parsed as an array and is_alt is boolean before setting selectedMember
        const parsedMember = {
            ...member,
            aliases: parseAliases(member.aliases),
            is_alt: member.is_alt === true  // Convert string "false" to boolean false
        };
        setSelectedMember(parsedMember);
        // Load member's periods from database
        await loadSelectedMemberPeriods(parseInt(parsedMember.id, 10));
        // Normalize old status values (kicked, quit) to 'inactive'
        const normalizedStatus = member.is_active === false ? 'inactive' : 'active';
        
        // Format dates to yyyy-mm-dd for HTML date input (avoid timezone issues)
        const formatDateForInput = (dateStr) => {
            if (!dateStr) return '';
            // If already in yyyy-mm-dd format, return as-is
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
            // If it has ISO format with time, extract just the date part
            if (dateStr.includes('T')) return dateStr.split('T')[0];
            // Fallback: try to parse as date
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        };
        
        setMemberFormData({ displayName: member.display_name || member.name || '', status: normalizedStatus, isAlt: member.is_alt === true, joinedDate: formatDateForInput(member.joined_date), leftDate: formatDateForInput(member.left_date), newAliases: '', rank: member.rank || 'R1' });
        setShowDeleteConfirm(false);
        const contributions = await loadMemberAllContributions(member.id);
    };

    const handleUpdateMember = async () => {
        if (!selectedMember) return;
        setSaving(true);
        try {
            let updatedAliases = [...(selectedMember.aliases || [])];
            if (memberFormData.newAliases.trim()) {
                const newAliases = memberFormData.newAliases.split(/[,\n]/).map(a => a.trim()).filter(a => a.length > 0 && !updatedAliases.includes(a));
                updatedAliases = [...updatedAliases, ...newAliases];
            }
            if (memberFormData.displayName !== selectedMember.display_name && selectedMember.display_name && !updatedAliases.includes(selectedMember.display_name)) {
                updatedAliases.push(selectedMember.display_name);
            }
            // Include joined_date/left_date for quick reference/fallback
            const updateData = { display_name: memberFormData.displayName.trim(), is_active: memberFormData.status === 'active', is_alt: memberFormData.isAlt === true, joined_date: memberFormData.joinedDate || null, left_date: memberFormData.leftDate || null, rank: memberFormData.rank || 'R1', aliases: updatedAliases, updated_at: new Date().toISOString() };
            // Use API server instead of Supabase
            const response = await apiCall('update', 'members', { id: selectedMember.id, data: updateData });
            if (!response || response.error) throw new Error(response?.error?.message || 'Failed to update member');
            await loadMembers();
            // Extract the updated member from response (could be array or object)
            let updatedMember = Array.isArray(response) ? response[0] : (response.data ? (Array.isArray(response.data) ? response.data[0] : response.data) : response);
            if (!updatedMember) throw new Error('No data returned from update');
            
            // Update selectedMember with the fresh data, parsing aliases and converting is_alt to boolean
            setSelectedMember({ 
                ...updatedMember, 
                aliases: parseAliases(updatedMember.aliases),
                is_alt: updatedMember.is_alt === true
            });
            setMemberFormData(prev => ({ ...prev, newAliases: '' }));
            showToast('success', 'Member Updated', 'All changes saved!');
        } catch (e) { console.error('Update error:', e); showToast('error', 'Error', e.message); }
        setSaving(false);
    };

    const handleRemoveAlias = async (aliasToRemove) => {
        if (!selectedMember) return;
        setSaving(true);
        try {
            const updatedAliases = (selectedMember.aliases || []).filter(a => a !== aliasToRemove);
            await apiCall('update', 'members', { id: selectedMember.id, data: { aliases: updatedAliases, updated_at: new Date().toISOString() } });
            setSelectedMember(prev => ({ ...prev, aliases: updatedAliases }));
            await loadMembers();
            showToast('success', 'Alias Removed', `"${aliasToRemove}" removed`);
        } catch (e) { showToast('error', 'Error', 'Failed to remove alias'); }
        setSaving(false);
    };

    const handleDeleteMember = async () => {
        if (!selectedMember) return;
        setSaving(true);
        try {
            await apiCall('delete', 'member_contributions', { member_id: selectedMember.id });
            await apiCall('delete', 'members', { id: selectedMember.id });
            await loadMembers();
            setSelectedMember(null);
            setShowDeleteConfirm(false);
            showToast('success', 'Member Deleted', 'Member and all data removed');
        } catch (e) { showToast('error', 'Error', e.message); }
        setSaving(false);
    };

    // ============ MEMBER PERIODS HANDLERS ============
    const loadSelectedMemberPeriods = async (memberId) => {
        if (!memberId) return;
        try {
            const res = await fetch(`/api/member-periods/${memberId}`);
            if (!res.ok) {
                console.warn(`⚠️ Failed to load periods for member ${memberId}: ${res.status} ${res.statusText}`);
                setMemberPeriods(prev => ({ ...prev, [memberId]: [] }));
                return;
            }
            const text = await res.text();
            if (!text) {
                console.debug(`ℹ️ No periods found for member ${memberId}`);
                setMemberPeriods(prev => ({ ...prev, [memberId]: [] }));
                return;
            }
            try {
                const periods = JSON.parse(text);
                setMemberPeriods(prev => ({ ...prev, [memberId]: Array.isArray(periods) ? periods : [] }));
            } catch (parseErr) {
                console.warn(`⚠️ Failed to parse periods JSON for member ${memberId}: ${parseErr.message}`);
                setMemberPeriods(prev => ({ ...prev, [memberId]: [] }));
            }
        } catch (error) {
            console.warn(`⚠️ Error loading member periods for ${memberId}:`, error.message);
            setMemberPeriods(prev => ({ ...prev, [memberId]: [] }));
        }
    };

    const handleAddPeriod = async (startDate, endDate, notes) => {
        if (!selectedMember || !startDate) {
            showToast('error', 'Invalid Input', 'Start date is required');
            return;
        }
        setSaving(true);
        try {
            const memberId = parseInt(selectedMember.id, 10);
            if (isNaN(memberId)) {
                showToast('error', 'Invalid Member', 'Member ID is invalid');
                setSaving(false);
                return;
            }
            const res = await fetch('/api/member-periods', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    member_id: memberId,
                    start_date: startDate,
                    end_date: endDate || null,
                    notes: notes || ''
                })
            });
            if (!res.ok) {
                if (res.status === 404) {
                    showToast('error', 'API Not Ready', 'Member periods API endpoints are not yet available. Please restart the API server on Nexus.');
                } else {
                    const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
                    showToast('error', 'Error', `Failed to add period: ${errorData.error || res.status}`);
                }
                setSaving(false);
                return;
            }
            const text = await res.text();
            if (!text) {
                showToast('success', 'Period Added', 'New membership period created');
                await loadSelectedMemberPeriods(memberId);
                setSaving(false);
                return;
            }
            try {
                JSON.parse(text);
                showToast('success', 'Period Added', 'New membership period created');
                await loadSelectedMemberPeriods(memberId);
            } catch (parseErr) {
                showToast('error', 'API Error', 'API returned invalid JSON. Server may need restart.');
            }
        } catch (error) {
            console.error('Error adding period:', error);
            showToast('error', 'Error', 'Failed to add period: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleUpdatePeriod = async (periodId, updates) => {
        if (!selectedMember) return;
        setSaving(true);
        try {
            const memberId = parseInt(selectedMember.id, 10);
            const res = await fetch(`/api/member-periods/${periodId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            if (!res.ok) {
                if (res.status === 404) {
                    showToast('error', 'API Not Ready', 'Member periods API endpoints are not yet available.');
                } else {
                    showToast('error', 'Error', `Failed to update period: ${res.status}`);
                }
                setSaving(false);
                return;
            }
            const text = await res.text();
            if (!text) {
                showToast('success', 'Period Updated', 'Membership period saved');
                await loadSelectedMemberPeriods(memberId);
                setSaving(false);
                return;
            }
            try {
                JSON.parse(text);
                showToast('success', 'Period Updated', 'Membership period saved');
                await loadSelectedMemberPeriods(memberId);
            } catch (parseErr) {
                showToast('error', 'API Error', 'API returned invalid JSON.');
            }
        } catch (error) {
            console.error('Error updating period:', error);
            showToast('error', 'Error', error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeletePeriod = async (periodId) => {
        if (!selectedMember) return;
        if (!window.confirm('Delete this membership period? This cannot be undone.')) return;
        setSaving(true);
        try {
            const memberId = parseInt(selectedMember.id, 10);
            const res = await fetch(`/api/member-periods/${periodId}`, { method: 'DELETE' });
            if (!res.ok) {
                if (res.status === 404) {
                    showToast('error', 'API Not Ready', 'Member periods API endpoints are not yet available.');
                } else {
                    showToast('error', 'Error', `Failed to delete period: ${res.status}`);
                }
                setSaving(false);
                return;
            }
            showToast('success', 'Period Deleted', 'Membership period removed');
            await loadSelectedMemberPeriods(memberId);
        } catch (error) {
            console.error('Error deleting period:', error);
            showToast('error', 'Error', error.message);
        } finally {
            setSaving(false);
        }
    };

    // Promote or set role for a member (super-admin only)
    const handleSetMemberRole = async (memberId, role) => {
        if (!currentUser || userRole !== 'super_admin') { showToast('error', 'Not Allowed', 'Only super-admins can change roles'); return; }
        const ok = window.confirm(`Are you sure you want to set role for member ${memberId} to "${role}"?`);
        if (!ok) return;
        setSaving(true);
        try {
            const { data, error } = await supabase.from('members').update({ role }).eq('id', memberId);
            if (error) { showDebug('setMemberRole error', error); showToast('error', 'Error', error.message || 'Failed to set role'); setSaving(false); return; }
            await loadMembers();
            // refresh selectedMember if it was the one changed
            if (selectedMember?.id === memberId) {
                const refreshed = (await supabase.from('members').select('*').eq('id', memberId).single());
                if (!refreshed.error) setSelectedMember(refreshed.data);
            }
            showToast('success', 'Role Updated', `Member role set to ${role}`);
        } catch (e) { showDebug('setMemberRole exception', e); showToast('error', 'Error', e.message || e.toString()); }
        setSaving(false);
    };

    // Helper: when promoting via Member Management UI, ensure member has an auth user.
    // If no auth_id is set, prompt the super-admin for an email and ask them to create the auth user in Supabase (dashboard). Then paste the auth user id (UUID) which this will link and proceed to set role.
    const handlePromoteMember = async (member, role) => {
        if (!currentUser || userRole !== 'super_admin') { showToast('error', 'Not Allowed', 'Only super-admins can change roles'); return; }
        if (!member) return;

        // If member already linked to auth user, just set role
        if (member.auth_id) {
            return handleSetMemberRole(member.id, role);
        }

        const email = window.prompt('This member is not linked to an Auth user. Enter their email to create/invite them in Supabase Auth (leave blank to cancel):');
        if (!email) return;

        // guide the super-admin: client cannot create admin/auth users securely; instruct them to use Supabase dashboard
        const proceed = window.confirm(`I cannot create auth users from the client.\n\nPlease open your Supabase dashboard -> Authentication -> Users and create/invite the user with email:\n\n${email}\n\nClick OK once you have created the user, then paste the new user id (UUID) in the next prompt. Click Cancel to abort.`);
        if (!proceed) return;

        const authId = window.prompt('Paste the new auth user id (UUID) here:');
        if (!authId) { showToast('error', 'No auth id', 'Operation canceled'); return; }

        // basic validation (loose)
        const uuidLike = /^[0-9a-fA-F\-]{36,}$/;
        if (!uuidLike.test(authId)) {
            const ok = window.confirm('The id you provided does not look like a UUID. Proceed anyway?');
            if (!ok) return;
        }

        setSaving(true);
        try {
            const { data, error } = await supabase.from('members').update({ auth_id: authId }).eq('id', member.id);
            if (error) { showDebug('link auth id error', error); showToast('error', 'Error', error.message || 'Failed to link auth id'); setSaving(false); return; }
            // reload members and selected member
            await loadMembers();
            const refreshed = (await supabase.from('members').select('*').eq('id', member.id).single());
            if (!refreshed.error) setSelectedMember(refreshed.data);

            // now set the role
            await handleSetMemberRole(member.id, role);
        } catch (e) { showDebug('promote flow exception', e); showToast('error', 'Error', e.message || e.toString()); }
        setSaving(false);
    };

    // Top-right small buttons: first click shows the auth hint if the member has no linked auth user;
    // second click (while hint is visible) proceeds with the promote flow.
    const handleTopPromoteClick = (member, role) => {
        if (!member) return;
        if (member.auth_id) return handlePromoteMember(member, role);
        if (!showAuthHint) { setShowAuthHint(true); return; }
        setShowAuthHint(false);
        return handlePromoteMember(member, role);
    };

    const handleSelectEvent = async (event) => { setSelectedEvent(event); await loadEventData(event.id); };

    const handleCreateEvent = async () => {
        if (!eventName || !eventDate || !eventServer) { showToast('error', 'Missing Info', 'Fill all fields'); return; }
        const endDate = eventEndDate || (() => { const d = new Date(eventDate); d.setDate(d.getDate() + 6); return d.toISOString().split('T')[0]; })();
        setSaving(true);
        try {
            // Get next event ID
            const events = await apiCall('get', 'events');
            const maxEventId = events.reduce((max, e) => Math.max(max, parseInt(e.id) || 0), 0);
            const eventId = String(maxEventId + 1);
            
            // Add timestamps
            const now = new Date().toISOString();
            
            // Create event
            const eventResponse = await apiCall('insert', 'events', {
                data: {
                    id: eventId,
                    name: eventName,
                    opponent: opponentName || null,
                    server: eventServer,
                    event_date: eventDate,
                    end_date: endDate,
                    created_at: now,
                    updated_at: now
                }
            });
            
            if (!eventResponse) throw new Error('Failed to create event');
            
            // Get next daily_scores ID
            const dailyScores = await apiCall('get', 'daily_scores');
            let nextScoreId = dailyScores.reduce((max, s) => Math.max(max, parseInt(s.id) || 0), 0) + 1;
            
            // Create daily_scores entries with sequential IDs
            const dailyScoresData = [];
            for (let i = 1; i <= 6; i++) {
                dailyScoresData.push({
                    id: String(nextScoreId++),
                    event_id: eventId,
                    day_number: i,
                    your_guild_score: 0,
                    opponent_score: 0,
                    created_at: now,
                    updated_at: now
                });
            }
            
            await apiCall('insert', 'daily_scores', { data: dailyScoresData });
            
            await loadEvents();
            setEventName(''); setOpponentName(''); setEventServer(''); setEventDate(''); setEventEndDate('');
            showToast('success', 'Event Created', `"${eventName}" created!`);
        } catch (e) { showToast('error', 'Error', e.message); }
        setSaving(false);
    };

    const handleAddMembers = async () => {
        if (!memberInput.trim()) { showToast('error', 'No Members', 'Enter member names'); return; }
        setSaving(true);
        try {
            // Support splitting by comma, newline, or both
            const names = memberInput.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0);
            const now = new Date().toISOString();
            const inserts = names.map(name => {
                // Canonical name: remove spaces/special chars, lowercase
                const canonical = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
                return {
                    name,
                    canonical_name: canonical,
                    display_name: name,
                    aliases: '{}',
                    is_active: true,
                    is_active: true,
                    joined_date: new Date().toISOString().split('T')[0],
                    left_date: null,
                    created_at: now,
                    updated_at: now,
                    role: 'user',
                    is_alt: false
                };
            });
            const response = await apiCall('insert', 'members', { data: inserts });
            if (!Array.isArray(response)) throw new Error('Failed to add members');
            await loadMembers();
            setMemberInput('');
            showToast('success', 'Members Added', `${inserts.length} member(s) added!`);
        } catch (e) { 
            if (e.message && e.message.includes('23505')) { 
                showToast('warning', 'Duplicates', 'Some members already exist'); 
                await loadMembers(); 
                setMemberInput(''); 
            } else {
                showToast('error', 'Error', e.message); 
            }
        }
        setSaving(false);
    };

    // Create a new member from error modal and retry adding their score
    const createAndRetryMember = async (failedIndex, memberName, score) => {
        if (!memberName.trim()) {
            showToast('error', 'Empty Name', 'Please enter a member name');
            return;
        }

        setSaving(true);
        try {
            // Create new member with same logic as handleAddMembers
            const canonical = memberName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
            const now = new Date().toISOString();
            
            const newMember = {
                name: memberName,
                canonical_name: canonical,
                display_name: memberName,
                aliases: '{}',
                is_active: true,
                joined_date: new Date().toISOString().split('T')[0],
                left_date: null,
                created_at: now,
                updated_at: now,
                role: 'user',
                is_alt: false
            };

            const response = await apiCall('insert', 'members', { data: [newMember] });
            if (!Array.isArray(response) || response.length === 0) throw new Error('Failed to create member');
            
            // Reload members to get the new member in the list
            await loadMembers();
            
            // Get the newly created member
            const createdMember = response[0];
            
            // Now retry adding the score with the new member
            if (!selectedEvent) {
                showToast('error', 'No Event', 'Event not selected');
                setSaving(false);
                return;
            }

            // Check if record already exists
            const existing = await apiCall('get', 'member_contributions', { 
                filters: { 
                    event_id: selectedEvent.id,
                    member_id: createdMember.id,
                    day_number: selectedDay
                }
            });
            
            let existingRecords = Array.isArray(existing) ? existing : (existing?.data || []);
            
            if (existingRecords.length > 0) {
                await apiCall('update', 'member_contributions', {
                    id: existingRecords[0].id,
                    data: { score: score, updated_at: new Date().toISOString() }
                });
            } else {
                await apiCall('insert', 'member_contributions', {
                    data: {
                        event_id: selectedEvent.id,
                        member_id: createdMember.id,
                        day_number: selectedDay,
                        score: score,
                        updated_at: new Date().toISOString()
                    }
                });
            }

            // Remove from failed list
            const updatedNotFound = uploadErrorModal.notFound.filter((_, i) => i !== failedIndex);
            
            if (updatedNotFound.length === 0) {
                setUploadErrorModal(null);
                setErrorModalEdits({});
                await loadEventData(selectedEvent.id);
                showToast('success', 'Member Created', `${memberName} added and score recorded!`);
            } else {
                setUploadErrorModal({...uploadErrorModal, notFound: updatedNotFound, found: uploadErrorModal.found + 1});
                setErrorModalEdits({...errorModalEdits, [memberName]: undefined});
                showToast('success', 'Member Created', `${memberName} added! ${updatedNotFound.length} remaining`);
            }

        } catch (e) {
            if (e.message && e.message.includes('23505')) {
                showToast('warning', 'Member Exists', 'This member already exists. Please use Retry instead.');
            } else {
                showToast('error', 'Error', e.message);
            }
        }
        setSaving(false);
    };

    const handleUpdateDayScores = async () => {
        if (!selectedEvent) { showToast('error', 'No Event', 'Select event first'); return; }
        if (DEBUG) console.log('[Day Scores] selectedEvent:', selectedEvent);
        setSaving(true);
        try {
            const updates = [];
            for (let i = 0; i < 6; i++) { if (dayScores[i] || opponentDayScores[i]) { const u = { event_id: selectedEvent.id, day_number: i + 1 }; if (dayScores[i]) u.your_guild_score = parseScore(dayScores[i]); if (opponentDayScores[i]) u.opponent_score = parseScore(opponentDayScores[i]); updates.push(u); } }
            if (DEBUG) console.log('[Day Scores] Updates to send:', updates);
            if (updates.length === 0) { showToast('warning', 'No Changes', 'No scores entered'); setSaving(false); return; }
            for (const u of updates) {
                const data = { updated_at: new Date().toISOString() };
                if (u.your_guild_score !== undefined) data.your_guild_score = u.your_guild_score;
                if (u.opponent_score !== undefined) data.opponent_score = u.opponent_score;
                if (DEBUG) console.log('[Day Scores] Sending update:', { data, filters: { event_id: u.event_id, day_number: u.day_number } });
                await apiCall('update', 'daily_scores', {
                    data,
                    filters: {
                        event_id: u.event_id,
                        day_number: u.day_number
                    }
                });
            }
            await loadEventData(selectedEvent.id);
            setDayScores(Array(6).fill('')); setOpponentDayScores(Array(6).fill(''));
            showToast('success', 'Scores Updated', `${updates.length} updated!`);
        } catch (e) { console.error('[Day Scores] Error:', e); showToast('error', 'Error', e.message); }
        setSaving(false);
    };

    const handleUpdateMemberScores = async () => {
        if (!selectedEvent) { showToast('error', 'No Event', 'Select event first'); return; }
        if (!memberScoresInput.trim()) { showToast('error', 'No Data', 'Enter member scores'); return; }
        setSaving(true);
        try {
            const lines = memberScoresInput.split('\n').map(l => l.trim()).filter(l => l);
            let found = 0, notFound = [];
            if (DEBUG) console.log('🔧 Processing', lines.length, 'lines. Members available:', members.map(m => m.name).join(', '));
            for (const line of lines) {
                // Split by tab first to handle "Name\tScore" format better
                let parts = line.split('\t').map(p => p.trim()).filter(p => p);
                
                // If no tab found, fall back to whitespace/semicolon splitting
                if (parts.length < 2) {
                    parts = line.split(/[;\s]+/).map(p => p.trim()).filter(p => p);
                }
                
                if (parts.length >= 2) {
                    // Last part is always the score
                    const score = parseScore(parts[parts.length - 1]);
                    // Everything else is the member name
                    const memberName = parts.slice(0, parts.length - 1).join(' ');
                    
                    if (DEBUG) console.log(`📝 Looking for member: "${memberName}", score: ${score}`);
                    const member = members.find(m => m.display_name === memberName || m.name === memberName || (m.aliases && m.aliases.includes(memberName)));
                    if (!member) { 
                        console.warn(`❌ Member not found: "${memberName}"`);
                        notFound.push({name: memberName, score: score}); 
                        continue; 
                    }
                    if (DEBUG) console.log(`✓ Found member: ${member.name} (ID: ${member.id})`);
                    found++;
                    try {
                        // Check if record already exists for this day/player/event
                        const existing = await apiCall('get', 'member_contributions', { 
                            filters: { 
                                event_id: selectedEvent.id,
                                member_id: member.id,
                                day_number: selectedDay
                            }
                        });
                        
                        if (DEBUG) console.log(`Query response for ${member.name}:`, existing, 'Type:', typeof existing, 'IsArray:', Array.isArray(existing));
                        
                        let existingRecords = Array.isArray(existing) ? existing : (existing?.data || []);
                        if (DEBUG) console.log(`Existing records found: ${existingRecords.length}`);
                        
                        if (existingRecords.length > 0) {
                            // Record exists - check if score is the same
                            const existingRecord = existingRecords[0];
                            const existingScore = parseFloat(existingRecord.score);
                            if (DEBUG) console.log(`Found existing: ID=${existingRecord.id}, Existing Score=${existingScore}, New Score=${score}, Types: ${typeof existingScore} vs ${typeof score}`);
                            if (existingScore === score && score !== 0) {
                                if (DEBUG) console.log(`⏭️ Skipping ${member.name} - same non-zero score (${score}) already exists`);
                                continue; // Skip only if both scores are the same AND non-zero
                            }
                            // Different score - update it

                            const updateResult = await apiCall('update', 'member_contributions', {
                                id: existingRecord.id,
                                data: {
                                    score: score,
                                    updated_at: new Date().toISOString()
                                }
                            });

                        } else {
                            // Record doesn't exist - insert it
                            await apiCall('insert', 'member_contributions', {
                                data: {
                                    event_id: selectedEvent.id,
                                    member_id: member.id,
                                    day_number: selectedDay,
                                    score: score,
                                    updated_at: new Date().toISOString()
                                }
                            });

                        }
                    } catch (err) {
                        console.error(`⚠️ Failed for ${member.name}:`, err);
                        showToast('error', `Failed for ${member.name}`, err.message);
                    }
                }
            }
            await loadEventData(selectedEvent.id);
            setMemberScoresInput('');
            if (notFound.length > 0) {
                setUploadErrorModal({ found, notFound, editedNames: {}, retryAttempts: 0 });
                showToast('warning', `${found} Updated, ${notFound.length} Not Found`, 'See error list below');
            } else {
                showToast('success', 'Scores Updated', `${found} scores updated!`);
            }
        } catch (e) { 
            console.error('🔴 Error:', e);
            showToast('error', 'Error', e.message); 
        }
        setSaving(false);
    };

    const getMemberSuggestions = (searchTerm) => {
        if (!searchTerm.trim()) return [];
        const term = searchTerm.toLowerCase();
        return members.filter(m => 
            m.display_name?.toLowerCase().includes(term) || 
            m.name?.toLowerCase().includes(term) || 
            (m.aliases && m.aliases.some(a => a.toLowerCase().includes(term)))
        ).slice(0, 8); // Limit to 8 suggestions
    };

    const retryFailedMember = async (index, editedName) => {
        if (!selectedEvent || !uploadErrorModal) return;
        const failedEntry = uploadErrorModal.notFound[index];
        const memberName = editedName || failedEntry.name;
        const score = failedEntry.score;
        
        if (!memberName.trim()) {
            showToast('error', 'Empty Name', 'Please enter a member name');
            return;
        }

        try {
            const member = members.find(m => m.display_name === memberName || m.name === memberName || (m.aliases && m.aliases.includes(memberName)));
            if (!member) {
                showToast('error', 'Not Found', `Member "${memberName}" still not in database`);
                return;
            }

            // Check if record already exists
            const existing = await apiCall('get', 'member_contributions', { 
                filters: { 
                    event_id: selectedEvent.id,
                    member_id: member.id,
                    day_number: selectedDay
                }
            });
            
            let existingRecords = Array.isArray(existing) ? existing : (existing?.data || []);
            
            if (existingRecords.length > 0) {
                await apiCall('update', 'member_contributions', {
                    id: existingRecords[0].id,
                    data: { score: score, updated_at: new Date().toISOString() }
                });
            } else {
                await apiCall('insert', 'member_contributions', {
                    data: {
                        event_id: selectedEvent.id,
                        member_id: member.id,
                        day_number: selectedDay,
                        score: score,
                        updated_at: new Date().toISOString()
                    }
                });
            }

            // Remove from failed list - use original name as key to remove from state objects
            const originalName = failedEntry.name;
            const updatedNotFound = uploadErrorModal.notFound.filter((_, i) => i !== index);
            const newSearchInputs = {...errorSearchInputs};
            delete newSearchInputs[originalName];
            const newEdits = {...errorModalEdits};
            delete newEdits[originalName];
            
            if (updatedNotFound.length === 0) {
                setUploadErrorModal(null);
                setErrorSearchInputs({});
                setErrorModalEdits({});
                showToast('success', 'All Fixed!', 'All failed members have been updated');
                await loadEventData(selectedEvent.id);
            } else {
                setUploadErrorModal({...uploadErrorModal, notFound: updatedNotFound});
                setErrorSearchInputs(newSearchInputs);
                setErrorModalEdits(newEdits);
                showToast('success', 'Added', `${member.name} updated successfully!`);
            }
        } catch (err) {
            console.error('Retry error:', err);
            showToast('error', 'Error', err.message);
        }
    };

    const retryAllFailedMembers = async () => {
        if (!selectedEvent || !uploadErrorModal || uploadErrorModal.notFound.length === 0) return;
        
        let successCount = 0;
        let failedRemaining = [...uploadErrorModal.notFound];
        
        for (let i = 0; i < uploadErrorModal.notFound.length; i++) {
            const entry = uploadErrorModal.notFound[i];
            const memberName = errorSearchInputs[entry.name] || entry.name;
            const score = entry.score;
            
            try {
                const member = members.find(m => m.display_name === memberName || m.name === memberName || (m.aliases && m.aliases.includes(memberName)));
                if (!member) continue;

                const existing = await apiCall('get', 'member_contributions', { 
                    filters: { 
                        event_id: selectedEvent.id,
                        member_id: member.id,
                        day_number: selectedDay
                    }
                });
                
                let existingRecords = Array.isArray(existing) ? existing : (existing?.data || []);
                
                if (existingRecords.length > 0) {
                    await apiCall('update', 'member_contributions', {
                        id: existingRecords[0].id,
                        data: { score: score, updated_at: new Date().toISOString() }
                    });
                } else {
                    await apiCall('insert', 'member_contributions', {
                        data: {
                            event_id: selectedEvent.id,
                            member_id: member.id,
                            day_number: selectedDay,
                            score: score,
                            updated_at: new Date().toISOString()
                        }
                    });
                }
                
                successCount++;
                failedRemaining = failedRemaining.filter(e => e.name !== entry.name);
            } catch (err) {
                if (DEBUG) console.error('Retry all error:', err);
            }
        }
        
        if (failedRemaining.length === 0) {
            setUploadErrorModal(null);
            setErrorSearchInputs({});
            setErrorModalEdits({});
            showToast('success', 'All Fixed!', `All ${successCount} members updated successfully!`);
            await loadEventData(selectedEvent.id);
        } else {
            setUploadErrorModal({...uploadErrorModal, found: uploadErrorModal.found + successCount, notFound: failedRemaining});
            showToast('success', 'Partial Success', `${successCount} members fixed, ${failedRemaining.length} still need attention`);
        }
    };

    const calculateTotals = useCallback(() => {
        if (!selectedEvent) return { yourTotal: 0, opponentTotal: 0, guildDailyTotals: Array(6).fill(0) };
        const guildDailyTotals = Array(6).fill(0);
        const safeDailyScores = Array.isArray(dailyScores) ? dailyScores : [];
        safeDailyScores.forEach(d => { if (d.day_number >= 1 && d.day_number <= 6) guildDailyTotals[d.day_number - 1] = parseFloat(d.your_guild_score) || 0; });
        const yourTotal = guildDailyTotals.reduce((s, v) => Number(s) + Number(v), 0);
        const opponentTotal = safeDailyScores.reduce((s, d) => s + (parseFloat(d.opponent_score) || 0), 0);
        return { yourTotal: parseFloat(yourTotal) || 0, opponentTotal: parseFloat(opponentTotal) || 0, guildDailyTotals };
    }, [selectedEvent, dailyScores]);

    const getDayResults = useCallback(() => {
        const results = Array(6).fill('draw');
        const safeDailyScores = Array.isArray(dailyScores) ? dailyScores : [];
        safeDailyScores.forEach(d => {
            if (d.day_number >= 1 && d.day_number <= 6) {
                const idx = d.day_number - 1;
                const guildScore = parseFloat(d.your_guild_score) || 0;
                const oppScore = parseFloat(d.opponent_score) || 0;
                results[idx] = guildScore > oppScore ? 'won' : guildScore < oppScore ? 'lost' : 'draw';
            }
        });
        return results;
    }, [dailyScores]);

    const getDaysDisplay = useCallback(() => {
        const results = getDayResults();
        return results.map((result, idx) => ({
            day: idx + 1,
            result
        }));
    }, [getDayResults]);
    const getMemberRankings = useCallback(() => {
        if (!selectedEvent || members.length === 0) return [];
        const memberScores = {};
        const safeMembers = Array.isArray(members) ? members : [];
        const safeContributions = Array.isArray(memberContributions) ? memberContributions : [];
        safeMembers.forEach(m => { memberScores[m.id] = { id: m.id, name: m.display_name || m.name, status: m.is_active === false ? 'inactive' : 'active', isAlt: m.is_alt, rank: m.rank, scores: Array(6).fill(0), total: 0, lowPerformanceDays: 0, joinedDate: m.joined_date, leftDate: m.left_date }; });
        safeContributions.forEach(c => { if (memberScores[c.member_id] && c.day_number >= 1 && c.day_number <= 6) memberScores[c.member_id].scores[c.day_number - 1] = parseFloat(c.score) || 0; });
        let rankings = Object.values(memberScores).map(m => { m.total = m.scores.reduce((s, v) => Number(s) + Number(v), 0); m.lowPerformanceDays = m.scores.filter(s => { const score = parseFloat(s) || 0; return score > 0 && score < MINIMUM_SCORE; }).length; return m; });
        if (filterLowPerformers) rankings = rankings.filter(r => r.lowPerformanceDays > 0 || r.total === 0);
        if (filterActiveOnly && selectedEvent) {
            rankings = rankings.filter(r => {
                const member = members.find(m => (m.display_name || m.name) === r.name);
                if (!member) return true;
                
                // For "All Events", show only members who are currently active
                if (selectedEvent.id === 'ALL') {
                    // If member has periods in the new system, check if currently active
                    const periods = memberPeriods[member.id];
                    if (periods && periods.length > 0) {
                        const now = new Date();
                        return periods.some(period => {
                            const start = new Date(period.start_date);
                            const end = period.end_date ? new Date(period.end_date) : null;
                            // Currently active if: started in past AND (no end date OR end date is in future)
                            return start <= now && (!end || end >= now);
                        });
                    }
                    // Fallback: check if member has no left_date or left_date is in future
                    if (member.left_date) {
                        return new Date(member.left_date) >= new Date();
                    }
                    return true;
                }
                
                // For specific events, check membership during that event
                const eventDate = new Date(selectedEvent.event_date);
                const eventEndDate = selectedEvent.end_date ? new Date(selectedEvent.end_date) : new Date(eventDate.getTime() + 6 * 24 * 60 * 60 * 1000);
                
                // If member has periods in the new system, use those
                const periods = memberPeriods[member.id];
                if (periods && periods.length > 0) {
                    return isMemberActiveInEvent(periods, eventDate, eventEndDate);
                }
                
                // Fallback to old joined_date/left_date if no periods exist
                if (!member.joined_date) return true;
                const joinedDate = new Date(member.joined_date);
                if (joinedDate > eventEndDate) return false;
                if (member.left_date && new Date(member.left_date) < eventDate) return false;
                return true;
            });
        }
        // Apply search filter
        if (searchQueryRankings.trim()) {
            const q = searchQueryRankings.toLowerCase();
            rankings = rankings.filter(r => r.name.toLowerCase().includes(q));
        }
        rankings = Array.isArray(rankings) ? rankings : [];
        rankings.sort((a, b) => {
            let result = 0;
            if (sortBy === 'total') {
                result = b.total - a.total;
            } else if (sortBy.startsWith('day')) {
                const i = parseInt(sortBy.replace('day', '')) - 1;
                result = b.scores[i] - a.scores[i];
            }
            return sortAscending ? -result : result;
        });
        return Array.isArray(rankings) ? rankings : [];
    }, [selectedEvent, members, memberContributions, filterLowPerformers, filterActiveOnly, searchQueryRankings, sortBy, sortAscending, memberPeriods]);

    // Get member's total score across all events
    const getMemberAllEventsTotal = useCallback((memberId) => {
        return memberContributions
            .filter(c => c.member_id === memberId)
            .reduce((sum, c) => sum + (Number(c.score) || 0), 0);
    }, [memberContributions]);

    // Get member's average score per event
    const getMemberAveragePerEvent = useCallback((memberId) => {
        const allScores = memberContributions.filter(c => c.member_id === memberId);
        if (allScores.length === 0) return 0;
        const total = allScores.reduce((sum, c) => sum + (Number(c.score) || 0), 0);
        return total / allScores.length;
    }, [memberContributions]);

    // Get top 10 individual scores across all events by day type
    const getTopScoresAllEventsPerDay = useCallback(() => {
        const topByDay = Array(6).fill(null).map(() => ({}));
        
        // Collect all individual scores by day
        memberContributions.forEach(c => {
            if (c.day_number >= 1 && c.day_number <= 6 && c.score > 0) {
                const dayIndex = c.day_number - 1;
                const memberObj = members.find(m => m.id === c.member_id);
                const memberName = memberObj?.display_name || memberObj?.name || 'Unknown';
                const scoreKey = `${memberName}_${c.score}`;
                
                if (!topByDay[dayIndex][scoreKey]) {
                    topByDay[dayIndex][scoreKey] = { name: memberName, score: c.score };
                }
            }
        });
        
        // Convert to arrays and sort for each day
        return topByDay.map(dayScores => {
            return Object.values(dayScores)
                .sort((a, b) => b.score - a.score)
                .slice(0, 10);
        });
    }, [memberContributions, members]);
    
    // Open performance details modal for a player
    const openPerformanceModal = (playerName, playerScores, underperformanceDays) => {
        const playerRanking = rankings.find(r => r.name === playerName);
        const eventName = selectedEvent.name || 'Unknown Event';
        const opponent = selectedEvent.opponent || extractOpponentFromName(eventName) || 'Unknown';
        const eventDate = selectedEvent.event_date ? new Date(selectedEvent.event_date).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'}) : 'Unknown Date';
        
        // Get HOT and opponent totals for the event
        const hotTotal = rankings.reduce((sum, r) => sum + r.total, 0);
        const opponentTotal = dailyScores.reduce((sum, d) => sum + (d.opponent_score || 0), 0);
        
        // If "All Events" is selected, find the specific events where this player underperformed
        let eventBreakdown = [];
        if (selectedEvent.id === 'ALL') {
            // Get all member's contributions
            const memberContribsForPlayer = memberContributions.filter(c => {
                const member = members.find(m => m.id === c.member_id);
                return (member?.display_name || member?.name) === playerName;
            });
            
            // Group by event_id and find which ones had underperformance
            const eventMap = {};
            memberContribsForPlayer.forEach(contrib => {
                if (!eventMap[contrib.event_id]) {
                    eventMap[contrib.event_id] = [];
                }
                eventMap[contrib.event_id].push(contrib);
            });
            
            // Build breakdown for each event
            Object.entries(eventMap).forEach(([eventId, contribs]) => {
                const event = events.find(e => e.id === parseInt(eventId));
                if (event) {
                    const eventContribs = contribs.sort((a, b) => (a.day_number || 0) - (b.day_number || 0));
                    const scores = Array(6).fill(0);
                    eventContribs.forEach(c => {
                        if (c.day_number >= 1 && c.day_number <= 6) {
                            scores[c.day_number - 1] = Number(c.score) || 0;
                        }
                    });
                    
                    // Check if this event has underperformance
                    const zeroDays = scores.map((s, i) => s === 0 ? i + 1 : null).filter(d => d !== null);
                    const belowDays = scores.map((s, i) => (s > 0 && s < MINIMUM_SCORE) ? i + 1 : null).filter(d => d !== null);
                    const hasIssues = zeroDays.length > 0 || belowDays.length > 0;
                    
                    if (hasIssues) {
                        const eventDate = event.event_date ? new Date(event.event_date).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'}) : 'Unknown';
                        const eventOpponent = event.opponent || extractOpponentFromName(event.name) || 'Unknown';
                        const eventTotal = scores.reduce((sum, s) => sum + s, 0);
                        
                        eventBreakdown.push({
                            eventName: event.name,
                            eventDate,
                            opponent: eventOpponent,
                            scores,
                            zeroDays,
                            belowDays,
                            eventTotal
                        });
                    }
                }
            });
        }
        
        // Calculate overall stats for the player
        let overallStats = {};
        if (selectedEvent.id === 'ALL') {
            // All events: total across all events
            const memberContribsForPlayer = memberContributions.filter(c => {
                const member = members.find(m => m.id === c.member_id);
                return (member?.display_name || member?.name) === playerName;
            });
            const totalScore = memberContribsForPlayer.reduce((sum, c) => sum + (Number(c.score) || 0), 0);
            const avgScore = memberContribsForPlayer.length > 0 ? totalScore / memberContribsForPlayer.length : 0;
            overallStats = {
                totalScore,
                averageScore: avgScore,
                eventCount: new Set(memberContribsForPlayer.map(c => c.event_id)).size,
                contributionCount: memberContribsForPlayer.length
            };
        } else {
            // Single event: just use the ranking data
            overallStats = {
                totalScore: playerRanking?.total || 0,
                averageScore: (playerRanking?.total || 0) / 6
            };
        }
        
        setPerformanceModalData({
            playerName,
            playerScores,
            underperformanceDays,
            eventName,
            opponent,
            eventDate,
            hotTotal,
            opponentTotal,
            playerRanking,
            isAllEvents: selectedEvent.id === 'ALL',
            eventBreakdown,
            overallStats
        });
    };

    const openMemberDetailsModal = async (rankingMember) => {
        // Use daySums from ranking member if available (these are aggregated across events), otherwise use scores
        const initialScores = rankingMember.daySums || rankingMember.scores || [0, 0, 0, 0, 0, 0];
        if (DEBUG) console.log('Opening member modal for:', rankingMember.name, 'with scores:', initialScores);
        
        setMemberDetailsModal(rankingMember);
        setMemberDetailsScores(initialScores);
        
        // Initialize form with basic data from ranking
        setMemberDetailsFormData({
            displayName: rankingMember.name || '',
            status: rankingMember.status === 'inactive' ? 'inactive' : 'active',
            joinedDate: '',
            leftDate: '',
            newAliases: '',
            isAlt: rankingMember.is_alt || rankingMember.isAlt || false
        });
        
        try {
            // Use the ID from the ranking member to fetch full database data
            if (rankingMember.id) {
                if (DEBUG) console.log('Fetching member data by ID:', rankingMember.id);
                const { data: memberData, error: memberError } = await supabase.from('members').select('*').eq('id', rankingMember.id).maybeSingle();
                
                if (DEBUG) console.log('Member database response:', { memberData, memberError });
                if (memberError) {
                    console.error('Supabase member error:', memberError.message);
                }
                
                if (memberData) {
                    if (DEBUG) console.log('Member data fetched from database:', memberData);
                    setMemberDetailsFullData(memberData);
                    setMemberDetailsFormData({
                        displayName: memberData.display_name || memberData.canonical_name || rankingMember.name || '',
                        status: memberData.is_active === false ? 'inactive' : 'active',
                        joinedDate: memberData.joined_date || '',
                        leftDate: memberData.left_date || '',
                        newAliases: (Array.isArray(memberData.aliases) ? memberData.aliases : []).join(', '),
                        isAlt: memberData.is_alt === true
                    });
                } else {
                    console.warn('No member data found for ID:', rankingMember.id);
                    setMemberDetailsFullData(rankingMember);
                }
            }
        } catch (err) {
            console.error('Error loading member details:', err);
            setMemberDetailsFullData(rankingMember);
        }
    };
    

    // Calculate daily medals (gold/silver/bronze for top 3 each day)
    const getDailyMedals = useCallback((rankingsData) => {
        const medals = {}; // { playerName: { gold: 0, silver: 0, bronze: 0 } }
        for (let dayIndex = 0; dayIndex < 6; dayIndex++) {
            const dayScores = rankingsData.map(r => ({ name: r.name, score: r.scores[dayIndex] })).filter(x => x.score > 0).sort((a, b) => b.score - a.score);
            if (dayScores[0]) { medals[dayScores[0].name] = medals[dayScores[0].name] || { gold: 0, silver: 0, bronze: 0 }; medals[dayScores[0].name].gold++; }
            if (dayScores[1]) { medals[dayScores[1].name] = medals[dayScores[1].name] || { gold: 0, silver: 0, bronze: 0 }; medals[dayScores[1].name].silver++; }
            if (dayScores[2]) { medals[dayScores[2].name] = medals[dayScores[2].name] || { gold: 0, silver: 0, bronze: 0 }; medals[dayScores[2].name].bronze++; }
        }
        return medals;
    }, []);

    // Calculate medals across all events for All Events view
    const getDailyMedalsAllEvents = useCallback(() => {
        const medals = {}; // { playerName: { gold: 0, silver: 0, bronze: 0 } }
        const allEventsList = events.filter(e => e.id !== 'ALL');
        if (DEBUG) console.log('🏆 getDailyMedalsAllEvents called');
        if (DEBUG) console.log('   Events array:', events.map(e => `${e.id}:${e.name}`));
        if (DEBUG) console.log('   Filtered events (excluding ALL):', allEventsList.map(e => `${e.id}:${e.name}`));
        if (DEBUG) console.log('   Total contributions:', memberContributions.length);
        if (DEBUG) console.log('   Total members:', members.length);
        
        if (allEventsList.length === 0) {
            if (DEBUG) console.log('⚠️  No events found to process');
            return medals;
        }
        
        // Create a map of memberId to member for quick lookup
        const memberMap = {};
        members.forEach(m => {
            memberMap[m.id] = m;
        });
        
        allEventsList.forEach(event => {
            // Get all contributions for this event - ensure ID comparison works for both string and number
            const eventIdNum = parseInt(event.id);
            const eventContribs = memberContributions.filter(c => {
                const contribIdNum = parseInt(c.event_id);
                return contribIdNum === eventIdNum;
            });
            if (DEBUG) console.log(`   Event ID ${event.id} (parsed: ${eventIdNum}): ${eventContribs.length} contributions`);
            
            if (eventContribs.length > 0) {
                if (DEBUG) console.log(`     Sample contrib:`, eventContribs[0]);
            }
            
            // For each day, find top 3 scorers in that event
            for (let dayIndex = 1; dayIndex <= 6; dayIndex++) {
                const dayContribs = eventContribs.filter(c => parseInt(c.day_number) === dayIndex && (parseFloat(c.score) || 0) > 0);
                if (DEBUG) console.log(`     Day ${dayIndex}: ${dayContribs.length} contributions with scores > 0`);
                
                const dayScores = dayContribs
                    .reduce((acc, c) => {
                        const member = memberMap[c.member_id];
                        const memberName = member?.display_name || member?.name || 'Unknown';
                        const scoreNum = parseFloat(c.score) || 0;
                        const existing = acc.find(x => x.name === memberName && x.memberId === c.member_id);
                        if (existing) {
                            existing.score = Math.max(existing.score, scoreNum);
                        } else {
                            acc.push({ name: memberName, memberId: c.member_id, score: scoreNum });
                        }
                        return acc;
                    }, [])
                    .sort((a, b) => parseFloat(b.score) - parseFloat(a.score));
                
                // Award medals
                if (dayScores[0]) { 
                    const key = dayScores[0].name;
                    medals[key] = medals[key] || { gold: 0, silver: 0, bronze: 0 }; 
                    medals[key].gold++; 
                }
                if (dayScores[1]) { 
                    const key = dayScores[1].name;
                    medals[key] = medals[key] || { gold: 0, silver: 0, bronze: 0 }; 
                    medals[key].silver++; 
                }
                if (dayScores[2]) { 
                    const key = dayScores[2].name;
                    medals[key] = medals[key] || { gold: 0, silver: 0, bronze: 0 }; 
                    medals[key].bronze++; 
                }
            }
        });
        if (DEBUG) console.log('🏆 Final medals object:', medals);
        return medals;
    }, [events, memberContributions, members]);

    const getCumulativeMedalsForMember = (memberName, memberContributions) => {
        const medals = { gold: 0, silver: 0, bronze: 0 };
        if (!memberContributions || memberContributions.length === 0) return medals;
        
        // For each event the member participated in, calculate their daily rankings
        memberContributions.forEach(eventData => {
            if (eventData.scores && eventData.event) {
                eventData.scores.forEach((score, dayIdx) => {
                    if (score > 0) {
                        // We need to compare against all members for that event
                        // For now, count high scores as potential medals
                        // This is approximate - for true calculation we'd need full event data
                    }
                });
            }
        });
        return medals;
    };

    // Load comparison data for two players
    const loadCompareData = async () => {
        if (!comparePlayerA.trim()) {
            showToast('error', 'Missing Player', 'Please enter Player A name');
            return;
        }
        setCompareLoading(true);
        try {
            // Find member A
            const memberA = members.find(m => {
                const aliases = parseAliases(m.aliases);
                return (m.display_name || m.name).toLowerCase() === comparePlayerA.toLowerCase() ||
                       aliases.some(a => a.toLowerCase() === comparePlayerA.toLowerCase());
            });
            if (!memberA) {
                showToast('error', 'Player Not Found', `Could not find player "${comparePlayerA}"`);
                setCompareLoading(false);
                return;
            }

            // Find member B (optional)
            let memberB = null;
            if (comparePlayerB.trim()) {
                memberB = members.find(m => {
                    const aliases = parseAliases(m.aliases);
                    return (m.display_name || m.name).toLowerCase() === comparePlayerB.toLowerCase() ||
                           aliases.some(a => a.toLowerCase() === comparePlayerB.toLowerCase());
                });
                if (!memberB) {
                    showToast('error', 'Player Not Found', `Could not find player "${comparePlayerB}"`);
                    setCompareLoading(false);
                    return;
                }
            }

            // Load all contributions for both players
            let contribsA = [], contribsB = [];
            try {
                const allContribsA = await apiCall('get', 'member_contributions', { filters: { member_id: memberA.id } });
                const allContribsB = memberB ? await apiCall('get', 'member_contributions', { filters: { member_id: memberB.id } }) : [];
                // Ensure all scores are numbers, not strings
                contribsA = (Array.isArray(allContribsA) ? allContribsA : []).map(c => ({ ...c, score: Number(c.score) || 0 }));
                contribsB = (Array.isArray(allContribsB) ? allContribsB : []).map(c => ({ ...c, score: Number(c.score) || 0 }));
            } catch (error) {
                console.error('Error loading contributions:', error);
                contribsA = [];
                contribsB = [];
            }

            // Get unique event IDs and fetch events
            let allEventIds = [...new Set([...contribsA.map(c => String(c.event_id)), ...contribsB.map(c => String(c.event_id))])];
            
            // Fetch all related events
            let eventsData = [];
            if (allEventIds.length > 0) {
                try {
                    const allEvents = await apiCall('get', 'events');
                    eventsData = Array.isArray(allEvents) ? allEvents.filter(e => allEventIds.includes(String(e.id))) : [];
                } catch (error) {
                    console.error('Error loading events:', error);
                    eventsData = [];
                }
            }

            // Apply scope filter
            if (compareOpponent !== 'ALL') {
                let filterEventIds = [];
                if (compareOpponent === 'LATEST') {
                    // Get the most recent event
                    const latestEvent = eventsData.sort((a, b) => new Date(b.event_date || 0) - new Date(a.event_date || 0))[0];
                    if (latestEvent) filterEventIds = [String(latestEvent.id)];
                } else {
                    // Specific event ID
                    filterEventIds = [String(compareOpponent)];
                }
                
                if (filterEventIds.length > 0) {
                    contribsA = contribsA.filter(c => filterEventIds.includes(String(c.event_id)));
                    contribsB = contribsB.filter(c => filterEventIds.includes(String(c.event_id)));
                    eventsData = eventsData.filter(e => filterEventIds.includes(String(e.id)));
                }
            }

            // Build comparison data
            const processPlayerData = (contribs, member) => {
                const byEvent = {};
                (contribs || []).forEach(c => {
                    if (!byEvent[c.event_id]) byEvent[c.event_id] = { scores: Array(6).fill(0), total: 0 };
                    const convertedScore = Number(c.score) || 0;
                    if (c.day_number >= 1 && c.day_number <= 6) {
                        byEvent[c.event_id].scores[c.day_number - 1] = convertedScore;
                    }
                });
                
                Object.values(byEvent).forEach((e, idx) => {
                    const before = e.scores.slice();
                    e.total = e.scores.reduce((s, v) => {
                        const result = Number(s) + Number(v);
                        return result;
                    }, 0);
                });
                
                const eventTotals = Object.entries(byEvent).map(([eventId, data]) => {
                    const event = eventsData.find(e => String(e.id) === eventId);
                    return { eventId: parseInt(eventId), event, ...data };
                }).sort((a, b) => new Date(b.event?.event_date || 0) - new Date(a.event?.event_date || 0));

                const overallTotal = eventTotals.reduce((s, e) => Number(s) + Number(e.total), 0);
                const avgPerDay = eventTotals.length > 0 ? overallTotal / (eventTotals.length * 6) : 0;
                const allDayScores = eventTotals.flatMap(e => e.scores.map(s => parseFloat(s) || 0).filter(s => s > 0));
                const maxDay = allDayScores.length > 0 ? Math.max(...allDayScores) : 0;
                const minDay = allDayScores.length > 0 ? Math.min(...allDayScores) : 0;
                const reliability = eventTotals.length > 0 ? (eventTotals.flatMap(e => e.scores).filter(s => parseFloat(s) || 0 > 0).length / (eventTotals.length * 6) * 100) : 0;

                return {
                    member,
                    name: member.display_name || member.name,
                    eventTotals,
                    overallTotal: parseFloat(overallTotal) || 0,
                    avgPerDay: parseFloat(avgPerDay) || 0,
                    maxDay: parseFloat(maxDay) || 0,
                    minDay: parseFloat(minDay) || 0,
                    reliability: parseFloat(reliability) || 0,
                    eventsCount: eventTotals.length,
                    latestTotal: parseFloat(eventTotals[0]?.total || 0) || 0
                };
            };

            const playerAData = processPlayerData(contribsA, memberA);
            const playerBData = memberB ? processPlayerData(contribsB, memberB) : null;

            // Calculate head-to-head wins
            let aWins = 0, bWins = 0, ties = 0;
            if (playerBData) {
                const sharedEvents = playerAData.eventTotals.filter(ea => 
                    playerBData.eventTotals.some(eb => eb.eventId === ea.eventId)
                );
                sharedEvents.forEach(ea => {
                    const eb = playerBData.eventTotals.find(e => e.eventId === ea.eventId);
                    if (eb) {
                        if (ea.total > eb.total) aWins++;
                        else if (eb.total > ea.total) bWins++;
                        else ties++;
                    }
                });
            }

            setCompareData({
                playerA: playerAData,
                playerB: playerBData,
                events: eventsData,
                headToHead: { aWins, bWins, ties, shared: aWins + bWins + ties }
            });
            showToast('success', 'Comparison Loaded', playerBData ? `Comparing ${playerAData.name} vs ${playerBData.name}` : `Loaded data for ${playerAData.name}`);
        } catch (err) {
            console.error('Compare error:', err);
            showToast('error', 'Compare Failed', err.message || 'Failed to load comparison data');
        }
        setCompareLoading(false);
    };

    // Render comparison charts
    useEffect(() => {
        if (!compareData || !compareCanvasRef.current || !window.Chart) return;

        const canvas = compareCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Destroy existing chart if it exists
        if (canvas.chart) {
            canvas.chart.destroy();
        }

        const playerAName = compareData.playerA.name;
        const playerBName = compareData.playerB?.name || playerAName;
        const playerAColor = '#FF6B6B';
        const playerBColor = '#4ECDC4';

        let chartConfig = {};

        if (compareChartType === 'total') {
            const data = [compareData.playerA.overallTotal];
            const labels = [playerAName];
            if (compareData.playerB) {
                data.push(compareData.playerB.overallTotal);
                labels.push(playerBName);
            }

            chartConfig = {
                type: 'bar',
                data: {
                    labels,
                    datasets: [{
                        label: 'Total Score',
                        data,
                        backgroundColor: [playerAColor, playerBColor],
                        borderRadius: 6,
                        borderWidth: 0
                    }]
                },
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { display: false },
                        title: { display: true, text: 'Total Scores', color: 'rgba(255,255,255,0.8)', font: { size: 14, weight: '600' } }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            grid: { color: 'rgba(255,255,255,0.1)' },
                            ticks: { color: 'rgba(255,255,255,0.7)' }
                        },
                        y: {
                            grid: { display: false },
                            ticks: { color: 'rgba(255,255,255,0.7)' }
                        }
                    }
                }
            };
        } else if (compareChartType === 'daily') {
            const getDaily = (contributions) => {
                const daily = [0, 0, 0, 0, 0, 0];
                contributions.forEach(c => {
                    if (c.day_number >= 1 && c.day_number <= 6) {
                        daily[c.day_number - 1] += parseFloat(c.score) || 0;
                    }
                });
                return daily;
            };

            const contribsA = memberContributions.filter(c => c.member_id === compareData.playerA.memberId);
            const dailyA = getDaily(contribsA);
            
            const datasets = [{
                label: playerAName,
                data: dailyA.map(d => d / Math.max(...dailyA, 1)),
                borderColor: playerAColor,
                backgroundColor: playerAColor + '33',
                borderWidth: 2,
                tension: 0.4,
                fill: true
            }];

            if (compareData.playerB) {
                const contribsB = memberContributions.filter(c => c.member_id === compareData.playerB.memberId);
                const dailyB = getDaily(contribsB);
                datasets.push({
                    label: playerBName,
                    data: dailyB.map(d => d / Math.max(...dailyB, 1)),
                    borderColor: playerBColor,
                    backgroundColor: playerBColor + '33',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                });
            }

            chartConfig = {
                type: 'line',
                data: {
                    labels: ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6'],
                    datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { labels: { color: 'rgba(255,255,255,0.8)' }, display: true },
                        title: { display: true, text: 'Daily Average Performance (Normalized)', color: 'rgba(255,255,255,0.8)', font: { size: 14, weight: '600' } }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            max: 1,
                            grid: { color: 'rgba(255,255,255,0.1)' },
                            ticks: { color: 'rgba(255,255,255,0.7)' }
                        },
                        x: {
                            grid: { display: false },
                            ticks: { color: 'rgba(255,255,255,0.7)' }
                        }
                    }
                }
            };
        } else if (compareChartType === 'trend') {
            const getTrendData = (contributions) => {
                const eventTotals = {};
                contributions.forEach(c => {
                    if (!eventTotals[c.event_id]) eventTotals[c.event_id] = 0;
                    eventTotals[c.event_id] += parseFloat(c.score) || 0;
                });
                return Object.values(eventTotals).slice(-10);
            };

            const contribsA = memberContributions.filter(c => c.member_id === compareData.playerA.memberId);
            const trendA = getTrendData(contribsA);

            const datasets = [{
                label: playerAName,
                data: trendA,
                borderColor: playerAColor,
                backgroundColor: playerAColor + '33',
                borderWidth: 2,
                tension: 0.4,
                fill: true
            }];

            if (compareData.playerB) {
                const contribsB = memberContributions.filter(c => c.member_id === compareData.playerB.memberId);
                const trendB = getTrendData(contribsB);
                datasets.push({
                    label: playerBName,
                    data: trendB,
                    borderColor: playerBColor,
                    backgroundColor: playerBColor + '33',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                });
            }

            chartConfig = {
                type: 'line',
                data: {
                    labels: Array.from({ length: 10 }, (_, i) => `E${i + 1}`),
                    datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { labels: { color: 'rgba(255,255,255,0.8)' } },
                        title: { display: true, text: 'Score Trend (Last 10 Events)', color: 'rgba(255,255,255,0.8)', font: { size: 14, weight: '600' } }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: { color: 'rgba(255,255,255,0.1)' },
                            ticks: { color: 'rgba(255,255,255,0.7)' }
                        },
                        x: {
                            grid: { display: false },
                            ticks: { color: 'rgba(255,255,255,0.7)' }
                        }
                    }
                }
            };
        }

        canvas.chart = new Chart(ctx, chartConfig);
    }, [compareData, compareChartType, memberContributions, members]);

    const getFilteredMembersForManage = () => {
        if (!searchQueryManage.trim()) return members;
        const q = searchQueryManage.toLowerCase();
        return members.filter(m => {
            const aliases = parseAliases(m.aliases);
            return (m.display_name || m.name).toLowerCase().includes(q) || aliases.some(a => a.toLowerCase().includes(q));
        });
    };

    const getFilteredMembersByStatus = () => {
        const baseMembers = getFilteredMembersForManage();
        if (memberFilter === 'all') return baseMembers;
        if (memberFilter === 'alts') return baseMembers.filter(m => m.is_alt);
        if (memberFilter === 'active') return baseMembers.filter(m => !m.is_alt && m.is_active !== false);
        if (memberFilter === 'inactive') return baseMembers.filter(m => m.is_active === false);
        return baseMembers;
    };

    // Toggle selection for a single member id
    const toggleSelectMember = (id) => {
        setSelectedMemberIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Toggle select-all for currently visible (filtered) members
    const toggleSelectAll = () => {
        const visible = getFilteredMembersForManage();
        const ids = visible.map(m => m.id);
        if (ids.length === 0) return;
        const allSelected = ids.every(id => selectedMemberIds.includes(id));
        if (allSelected) { setSelectedMemberIds([]); setSelectAllChecked(false); }
        else { setSelectedMemberIds(ids); setSelectAllChecked(true); }
    };

    // Bulk set role for selected members (super-admin only)
    const handleBulkSetRole = async (role) => {
        if (!currentUser || userRole !== 'super_admin') { showToast('error', 'Not Allowed', 'Only super-admins can change roles'); return; }
        if (!selectedMemberIds.length) { showToast('error', 'No Selection', 'Select members to update'); return; }
        const ok = window.confirm(`Set role "${role}" for ${selectedMemberIds.length} members?`);
        if (!ok) return;
        setSaving(true);
        try {
            const { data, error } = await supabase.from('members').update({ role }).in('id', selectedMemberIds);
            if (error) { throw error; }
            await loadMembers();
            setSelectedMemberIds([]); setSelectAllChecked(false);
            showToast('success', 'Roles Updated', `${selectedMemberIds.length} members set to ${role}`);
        } catch (e) { showDebug('bulkSetRole error', e); showToast('error', 'Error', e.message || e.toString()); }
        setSaving(false);
    }; 

    useEffect(() => {
        if (activeTab === 'dashboard' && selectedEvent && dailyScores.length > 0 && chartRef.current) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                if (chartInstance.current) chartInstance.current.destroy();
                const yourScores = Array(6).fill(0), opponentScores = Array(6).fill(0);
                dailyScores.forEach(d => { yourScores[d.day_number - 1] = d.your_guild_score || 0; opponentScores[d.day_number - 1] = d.opponent_score || 0; });
                const ctx = chartRef.current.getContext('2d');
                
                // Create gradients for bars
                const orangeGradient = ctx.createLinearGradient(0, 0, 0, 300);
                orangeGradient.addColorStop(0, 'rgba(255, 150, 80, 1)');
                orangeGradient.addColorStop(1, 'rgba(255, 90, 58, 0.7)');
                
                const violetGradient = ctx.createLinearGradient(0, 0, 0, 300);
                violetGradient.addColorStop(0, 'rgba(200, 120, 255, 1)');
                violetGradient.addColorStop(1, 'rgba(157, 94, 255, 0.7)');
                
                chartInstance.current = new Chart(ctx, {
                    type: 'bar',
                    data: { labels: DAY_NAMES, datasets: [{ label: 'HOT', data: yourScores, backgroundColor: orangeGradient, borderColor: 'rgba(255, 90, 58, 1)', borderWidth: 3, borderRadius: 10 }, { label: selectedEvent?.opponent || 'Opponent', data: opponentScores, backgroundColor: violetGradient, borderColor: 'rgba(157, 94, 255, 1)', borderWidth: 3, borderRadius: 10 }] },
                    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { labels: { color: '#ffffff', font: { family: 'Poppins', size: 13, weight: '700' }, padding: 18, usePointStyle: false } } }, scales: { y: { beginAtZero: true, ticks: { color: '#a89bb0', callback: v => formatCompact(v), font: { size: 12, weight: '700' }, padding: 8 }, grid: { color: 'rgba(255, 255, 255, 0.06)', drawBorder: false } }, x: { ticks: { color: '#a89bb0', font: { size: 12, weight: '700' } }, grid: { display: false, drawBorder: false } } } }
                });
            }, 50);
            return () => { clearTimeout(timer); if (chartInstance.current) chartInstance.current.destroy(); };
        }
        return () => { if (chartInstance.current) chartInstance.current.destroy(); };
    }, [activeTab, selectedEvent, dailyScores]);

    // When user manually selects event from dropdown
    useEffect(() => {
        if (selectedEvent?.id) {
            loadEventData(selectedEvent.id);
        }
    }, [selectedEvent?.id]);

    const totals = useMemo(() => activeTab === 'dashboard' ? calculateTotals() : null, [activeTab, selectedEvent, memberContributions, dailyScores]);
    const rankings = useMemo(() => activeTab === 'members' ? getMemberRankings() : [], [activeTab, selectedEvent, members, memberContributions, filterLowPerformers, filterActiveOnly, searchQueryRankings, sortBy, sortAscending]);

    // Selected member stats (used in Hot Member Card POC)
    const selectedMemberEventCount = memberAllContributions ? memberAllContributions.length : 0;
    const selectedMemberTotalScore = useMemo(() => (memberAllContributions || []).reduce((acc, ev) => acc + (ev.scores ? ev.scores.reduce((s, v) => s + (v || 0), 0) : 0), 0), [memberAllContributions]);
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

    // Close sidebar on navigation (mobile)
    useEffect(() => { if (sidebarOpen && isMobile) setSidebarOpen(false); }, [activeTab]);

    if (loading) return (<div className="app-layout"><div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}><div className="loading"><div className="loading-spinner"></div><div>Loading Guild Dashboard...</div></div></div></div>);

    return (
        <div className="app-layout">
            {process.env.NODE_ENV !== 'production' && (<div style={{position: 'fixed', top: '5px', right: '100px', background: '#333', color: '#fff', padding: '5px 10px', zIndex: 10000, fontSize: '11px', borderRadius: '3px'}}>AUTH: {currentUser ? userRole || 'loading...' : 'not logged in'}</div>)}
            <div className={`sidebar${sidebarOpen && isMobile ? ' open' : ''}${isMobile ? ' mobile' : ''}`}>
            <div className="sidebar-logo"><img src="logo.png" alt="HOT" style={{width: '42px', height: '42px', objectFit: 'contain', borderRadius: '10px'}} onError={(e) => { e.target.style.display='none'; e.target.parentNode.innerText='HOT'; }} /></div>
                <div className="sidebar-nav">
                    <button className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => navigateTo('dashboard')}>📊<span className="nav-tooltip">Dashboard</span></button>
                    <button className={`nav-item ${activeTab === 'members' ? 'active' : ''}`} onClick={() => navigateTo('members')}>🏆<span className="nav-tooltip">Rankings</span></button>
                    <button className={`nav-item ${activeTab === 'compare' ? 'active' : ''}`} onClick={() => navigateTo('compare')}>⚔️<span className="nav-tooltip">Compare</span></button>
                    <button className={`nav-item ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => navigateTo('calendar')}>📅<span className="nav-tooltip">Calendar</span></button>
                    <button className={`nav-item ${activeTab === 'guides' ? 'active' : ''}`} onClick={() => navigateTo('guides')}>📖<span className="nav-tooltip">Guides</span></button>
                    {(userRole === 'admin' || userRole === 'super_admin') && (<>
                        <button className={`nav-item ${activeTab === 'member-mgmt' ? 'active' : ''}`} onClick={() => navigateTo('member-mgmt')}>👥<span className="nav-tooltip">Manage Members</span></button>
                    </>)}
                </div>
                {!isMobile && (
                    <div className="sidebar-bottom">
                        {(userRole === 'admin' || userRole === 'super_admin') && (<>
                            <button className={`nav-item ${activeTab === 'data-entry' ? 'active' : ''}`} onClick={() => navigateTo('data-entry')}>✏️<span className="nav-tooltip">Data Entry</span></button>
                            <button className={`nav-item ${activeTab === 'setup' ? 'active' : ''}`} onClick={() => navigateTo('setup')}>⚙️<span className="nav-tooltip">Settings</span></button>
                        </>)}
                        <button className="nav-item" onClick={() => currentUser ? handleLogout() : setShowLoginModal(true)} style={{color: currentUser ? 'var(--accent-success)' : 'var(--text-secondary)'}}>{currentUser ? '🔓' : '🔒'}<span className="nav-tooltip">{currentUser ? 'Logout' : 'Admin Login'}</span></button>
                    </div>
                )}
                {isMobile && (
                    <div className="sidebar-kebab-container">
                        <button className="nav-item kebab-menu-button" onClick={() => setKebabMenuOpen(!kebabMenuOpen)} title="More options">⋮</button>
                        {kebabMenuOpen && (
                            <div className="kebab-dropdown">
                                {(userRole === 'admin' || userRole === 'super_admin') && (
                                    <>
                                        <div className="kebab-option" onClick={() => { navigateTo('data-entry'); setKebabMenuOpen(false); }}>
                                            ✏️ <span>Data Entry</span>
                                        </div>
                                        <div className="kebab-option" onClick={() => { navigateTo('setup'); setKebabMenuOpen(false); }}>
                                            ⚙️ <span>Settings</span>
                                        </div>
                                    </>
                                )}
                                <div className="kebab-option" onClick={() => { currentUser ? handleLogout() : setShowLoginModal(true); setKebabMenuOpen(false); }}>
                                    <span>{currentUser ? '🔓' : '🔒'}</span> <span>{currentUser ? 'Logout' : 'Admin Login'}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* Main Content */}
            <div className="main-content">
                <div className="header">
                    {isMobile && (
                        <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Open Menu">
                            <span className="hamburger-bar"></span>
                            <span className="hamburger-bar"></span>
                            <span className="hamburger-bar"></span>
                        </button>
                    )}
                    <div className="header-title">[HOT]DOMINATORS GAR TRACKER</div>
                    <div className="header-event-selector">
                        <div className="custom-event-dropdown">
                            <button className="event-dropdown-trigger" onClick={() => setEventDropdownOpen(!eventDropdownOpen)}>
                                📊 {selectedEvent?.id === 'ALL' ? 'All Events' : (selectedEvent ? `${selectedEvent.name} vs ${selectedEvent.opponent}` : 'All Events')}
                                <span className="dropdown-arrow">▼</span>
                            </button>
                            {eventDropdownOpen && (
                                <div className="event-dropdown-menu">
                                    <div className="event-dropdown-item" onClick={() => {
                                        setSelectedEvent({ id: 'ALL', name: 'All Events', opponent: 'All Opponents' });
                                        setDailyScores([]);
                                        setMemberContributions([]);
                                        setEventDropdownOpen(false);
                                    }}>
                                        <span className="event-item-icon">📊</span>
                                        <span className="event-item-name">All Events</span>
                                    </div>
                                    {[...events].sort((a, b) => new Date(b.event_date) - new Date(a.event_date)).map(ev => {
                                        const weekRange = ev.event_date ? getWeekRangeForDate(ev.event_date, ev.end_date) : 'Unknown';
                                        return (
                                            <div key={ev.id} className="event-dropdown-item" onClick={() => {
                                                handleSelectEvent(ev);
                                                setEventDropdownOpen(false);
                                            }}>
                                                <span className="event-item-icon">⚔️</span>
                                                <span className="event-item-name">{ev.name} vs {ev.opponent} • {weekRange}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="status-indicator"><span className="status-dot"></span>Connected</div>
                    </div>
                </div>

                <div className="content-area">
                    <div className="content-main">
                        {/* Dashboard Tab */}
                        {activeTab === 'dashboard' && selectedEvent && (
                            selectedEvent.id === 'ALL' ? (
                                // ALL EVENTS VIEW
                                <>
                                    {/* Events Summary - AT VERY TOP */}
                                    <div style={{marginBottom: '20px'}}>
                                        <h3 style={{color: 'var(--text-primary)', marginBottom: '15px', fontSize: '16px', fontWeight: '600'}}>⚔️ Event Results</h3>
                                        <div className="table-wrapper">
                                            <table className="data-table" style={{fontSize: '10px'}}>
                                                <thead>
                                                    <tr style={{background: 'rgba(255,255,255,0.08)'}}>
                                                        <th style={{padding: '6px 2px', textAlign: 'center', fontWeight: '600', fontSize: '10px'}}>Event</th>
                                                        <th style={{textAlign: 'center', padding: '6px 25px', fontWeight: '600', fontSize: '10px'}}>Date</th>
                                                        <th style={{textAlign: 'center', padding: '6px 1px', fontWeight: '600', fontSize: '10px'}}>Opponent</th>
                                                        <th style={{textAlign: 'center', padding: '6px 2px', fontWeight: '600', fontSize: '10px'}}>Server</th>
                                                        <th style={{textAlign: 'center', padding: '6px 2px', fontWeight: '600', fontSize: '10px'}}>HOT</th>
                                                        <th style={{textAlign: 'center', padding: '6px 2px', fontWeight: '600', fontSize: '10px'}}>Opp</th>
                                                        <th style={{textAlign: 'center', padding: '6px 2px', fontWeight: '600', fontSize: '10px'}}>Result</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {[...events].sort((a, b) => new Date(b.event_date) - new Date(a.event_date)).map(event => {
                                                        const eventScores = (Array.isArray(dailyScores) ? dailyScores : []).filter(s => s.event_id === event.id);
                                                        const hotTotal = eventScores.reduce((sum, s) => sum + (parseFloat(s.your_guild_score) || 0), 0);
                                                        const oppTotal = eventScores.reduce((sum, s) => sum + (parseFloat(s.opponent_score) || 0), 0);
                                                        const result = hotTotal > oppTotal ? 'WIN' : hotTotal < oppTotal ? 'LOSS' : 'DRAW';
                                                        
                                                        return (
                                                            <tr key={event.id} style={{height: '26px'}}>
                                                                <td style={{fontWeight: '600', fontSize: '10px', padding: '2px 2px', textAlign: 'center'}}>{event.name} {event.is_low_priority && <span style={{display: 'inline-block', backgroundColor: 'rgba(255, 107, 107, 0.3)', color: '#FF6B6B', fontSize: '8px', padding: '1px 3px', borderRadius: '2px', marginLeft: '3px', fontWeight: '700'}}>LOW PRIORITY</span>}</td>
                                                                <td style={{textAlign: 'center', fontSize: '9px', padding: '2px 2px', color: 'var(--text-muted)'}}>{event.event_date ? new Date(event.event_date).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}) + (event.end_date && event.end_date !== event.event_date ? ` - ${new Date(event.end_date).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}` : '') : 'N/A'}</td>
                                                                <td style={{textAlign: 'center', fontSize: '10px', padding: '2px 2px'}}>{event.opponent}</td>
                                                                <td style={{textAlign: 'center', fontSize: '10px', fontWeight: '500', padding: '2px 2px'}}>{event.server || 'N/A'}</td>
                                                                <td style={{color: 'var(--accent-cyan)', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '2px 2px'}}>{formatCompact(hotTotal)}</td>
                                                                <td style={{color: 'var(--accent-purple)', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '2px 2px'}}>{formatCompact(oppTotal)}</td>
                                                                <td style={{fontSize: '10px', fontWeight: '600', textAlign: 'center', padding: '2px 2px'}}>
                                                                    <span style={{
                                                                        background: result === 'WIN' ? 'rgba(46,204,113,0.2)' : result === 'LOSS' ? 'rgba(231,76,60,0.2)' : 'rgba(155,89,182,0.2)',
                                                                        padding: '2px 4px',
                                                                        borderRadius: '4px',
                                                                        fontWeight: '700',
                                                                        color: result === 'WIN' ? 'var(--accent-green)' : result === 'LOSS' ? 'var(--accent-red)' : 'var(--accent-purple)',
                                                                        fontSize: '10px'
                                                                    }}>
                                                                        {result === 'WIN' ? '🏆' : result === 'LOSS' ? '❌' : '🤝'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Stats Per Day Section */}
                                    <div style={{marginBottom: '20px'}}>
                                        <h3 style={{color: 'var(--text-primary)', marginBottom: '15px', fontSize: '18px', fontWeight: '600'}}>📈 Performance by Day Type (All Events)</h3>
                                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '22px', marginBottom: '20px'}}>
                                            {getStatsPerDay().map((dayStats, idx) => (
                                                <div key={idx} className="card" style={{padding: '12px', background: 'rgba(255,255,255,0.05)'}}>
                                                    <div style={{textAlign: 'center', marginBottom: '10px'}}>
                                                        <div style={{fontSize: '24px', marginBottom: '4px'}}>{dayStats.dayIcon}</div>
                                                        <div style={{fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500'}}>{dayStats.dayName}</div>
                                                    </div>
                                                    <div style={{fontSize: '11px', color: 'var(--text-muted)'}}>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>Median:</span><span style={{fontWeight: '600', color: 'var(--accent-cyan)'}}>{formatNumber(dayStats.median)}</span></div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>P90 (Top 10%):</span><span style={{fontWeight: '600', color: 'var(--accent-green)'}}>{formatNumber(dayStats.p90)}</span></div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>P10 (Below Avg):</span><span style={{fontWeight: '600', color: 'var(--accent-orange)'}}>{formatNumber(dayStats.p10)}</span></div>
                                                        <div style={{borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', marginTop: '6px'}}>
                                                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2px'}}><span>Min Score:</span><span>{formatNumber(dayStats.min)}</span></div>
                                                            <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Max Score:</span><span>{formatNumber(dayStats.max)}</span></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>


                                </>
                            ) : (
                                // INDIVIDUAL EVENT VIEW
                                <>
                                    <div className="card stats-overview-card" style={{marginBottom: '20px', padding: '15px 1px'}}>
                                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0px'}}>
                                            <div style={{textAlign: 'center', flex: '1', minWidth: '115px'}}>
                                                <div className="stat-label">HOT TOTAL</div>
                                                <div className="stat-value blue" style={{fontSize: '24px'}}>{formatCompact(totals.yourTotal)}</div>
                                                <div className="stat-subtitle" style={{fontSize: '11px'}}>{formatNumber(totals.yourTotal)}</div>
                                            </div>
                                            <div style={{fontSize: '24px', color: 'var(--text-muted)'}}>vs</div>
                                            <div style={{textAlign: 'center', flex: '1', minWidth: '100px'}}>
                                                <div className="stat-label">{(selectedEvent.opponent || extractOpponentFromName(selectedEvent.name) || 'OPPONENT').toUpperCase()} TOTAL</div>
                                                <div className="stat-value purple" style={{fontSize: '24px'}}>{formatCompact(totals.opponentTotal)}</div>
                                                <div className="stat-subtitle" style={{fontSize: '11px'}}>{formatNumber(totals.opponentTotal)}</div>
                                            </div>
                                            <div style={{width: '2px', height: '48px', background: 'var(--border-accent)', opacity: '0.3'}}></div>
                                            <div style={{textAlign: 'center', flex: '1', minWidth: '125px'}}>
                                                <div className="stat-label">Difference</div>
                                                <div className={`stat-value ${totals.yourTotal >= totals.opponentTotal ? 'green' : 'red'}`} style={{fontSize: '24px'}}>{totals.yourTotal >= totals.opponentTotal ? '+' : ''}{formatCompact(totals.yourTotal - totals.opponentTotal)}</div>
                                            </div>
                                            <div style={{width: '1px', height: '40px', background: 'var(--border-accent)', opacity: '0.3'}}></div>
                                            <div style={{textAlign: 'center', flex: '1', minWidth: '176px'}}>
                                                <div className={`stat-value ${totals.yourTotal >= totals.opponentTotal ? 'green' : 'red'}`} style={{fontSize: '20px', marginBottom: '4px'}}>{totals.yourTotal >= totals.opponentTotal ? '🏆 WIN' : '⚠️ LOSS'}</div>
                                                <div className="days-display">{getDaysDisplay().map((d, i) => (<span key={i} className={`day-indicator day-${d.result}`}>{d.day}</span>))}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card"><div className="card-header"><div className="card-title"><span className="card-icon">📊</span>Daily Score Comparison</div></div><div className="chart-container"><canvas ref={chartRef}></canvas></div></div>
                                    
                                    {/* Stats Per Day Section */}
                                    <div style={{marginTop: '20px'}}>
                                        <h3 style={{color: 'var(--text-primary)', marginBottom: '15px', fontSize: '18px', fontWeight: '600'}}>📈 Performance by Day Type</h3>
                                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px'}}>
                                            {getStatsPerDay().map((dayStats, idx) => (
                                                <div key={idx} className="card" style={{padding: '12px', background: 'rgba(255,255,255,0.05)'}}>
                                                    <div style={{textAlign: 'center', marginBottom: '10px'}}>
                                                        <div style={{fontSize: '24px', marginBottom: '4px'}}>{dayStats.dayIcon}</div>
                                                        <div style={{fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500'}}>{dayStats.dayName}</div>
                                                    </div>
                                                    <div style={{fontSize: '11px', color: 'var(--text-muted)'}}>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>Median:</span><span style={{fontWeight: '600', color: 'var(--accent-cyan)'}}>{formatNumber(dayStats.median)}</span></div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>P90 (Top 10%):</span><span style={{fontWeight: '600', color: 'var(--accent-green)'}}>{formatNumber(dayStats.p90)}</span></div>
                                                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}><span>P10 (Below Avg):</span><span style={{fontWeight: '600', color: 'var(--accent-orange)'}}>{formatNumber(dayStats.p10)}</span></div>
                                                        <div style={{borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', marginTop: '6px'}}>
                                                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2px'}}><span>Min Score:</span><span>{formatNumber(dayStats.min)}</span></div>
                                                            <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Max Score:</span><span>{formatNumber(dayStats.max)}</span></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )
                        )}

                        {/* Rankings Tab */}
                        {activeTab === 'members' && (<div className="card rankings-card compact">
                            <div className="rankings-toolbar">
                                <div className="rankings-search-inline">
                                    <span className="rankings-search-icon">🔍</span>
                                    <input type="text" className="rankings-search-input" placeholder="Search..." value={searchQueryRankings} onChange={(e) => setSearchQueryRankings(e.target.value)} />
                                    {searchQueryRankings && <button className="rankings-search-clear" onClick={() => setSearchQueryRankings('')}>✕</button>}
                                </div>
                                <button className={`filter-toggle-sm ${filterActiveOnly ? 'active' : ''}`} onClick={() => setFilterActiveOnly(!filterActiveOnly)}>👤 Active</button>
                                <button className={`filter-toggle-sm ${filterLowPerformers ? 'active' : ''}`} onClick={() => setFilterLowPerformers(!filterLowPerformers)}>⚠️ Low</button>
                                <span className="rankings-count-inline">{selectedEvent?.id === 'ALL' ? (() => {
                                    // Calculate all event rankings (for ALL EVENTS view)
                                    const allMemberRankings = members.map(m => ({ id: m.id, name: m.display_name || m.name, status: m.is_active === false ? 'inactive' : 'active', is_alt: m.is_alt, isAlt: m.is_alt, rank: m.rank, left_date: m.left_date }));
                                    const allOverallRankings = allMemberRankings.map(m => {
                                        const playerContribs = memberContributions.filter(c => { const member = members.find(mem => mem.id === c.member_id); return (member?.display_name || member?.name) === m.name; });
                                        const daySums = Array(6).fill(0);
                                        playerContribs.forEach(c => { if (c.day_number >= 1 && c.day_number <= 6) daySums[c.day_number - 1] += parseFloat(c.score) || 0; });
                                        const allEventTotal = daySums.reduce((sum, s) => Number(sum) + Number(s), 0);
                                        const lowPerformanceDays = daySums.filter(s => s > 0 && s < MINIMUM_SCORE).length;
                                        return { ...m, daySums, overallTotal: allEventTotal, lowPerformanceDays };
                                    }).sort((a, b) => b.overallTotal - a.overallTotal);
                                    
                                    // Apply filters to get the actual displayed count
                                    let filteredRankings = allOverallRankings;
                                    if (filterLowPerformers) filteredRankings = filteredRankings.filter(r => r.lowPerformanceDays > 0 || r.overallTotal === 0);
                                    if (filterActiveOnly) filteredRankings = filteredRankings.filter(r => r.status === 'active' && (!r.left_date || new Date(r.left_date) > new Date()));
                                    if (searchQueryRankings.trim()) filteredRankings = filteredRankings.filter(r => r.name.toLowerCase().includes(searchQueryRankings.toLowerCase()));
                                    
                                    return filteredRankings.length;
                                })() : rankings.length} players</span>
                                <div className="rankings-event-badge-sm">{selectedEvent?.name}</div>
                                {(compareSelectA || compareSelectB) && (
                                    <div className="compare-selection-indicator">
                                        <span>⚔️ {compareSelectA || '?'} vs {compareSelectB || '?'}</span>
                                        <button className="btn btn-primary btn-sm" onClick={async () => { 
                                            const pA = compareSelectA || ''; 
                                            const pB = compareSelectB || ''; 
                                            setComparePlayerA(pA); 
                                            setComparePlayerB(pB); 
                                            setCompareSelectA(null); 
                                            setCompareSelectB(null); 
                                            navigateTo('compare');
                                            // Trigger comparison directly
                                            if (pA) {
                                                setCompareLoading(true);
                                                try {
                                                    const memberA = members.find(m => {
                                                        const aliases = parseAliases(m.aliases);
                                                        return (m.display_name || m.name).toLowerCase() === pA.toLowerCase() || aliases.some(a => a.toLowerCase() === pA.toLowerCase());
                                                    });
                                                    let memberB = null;
                                                    if (pB) memberB = members.find(m => {
                                                        const aliases = parseAliases(m.aliases);
                                                        return (m.display_name || m.name).toLowerCase() === pB.toLowerCase() || aliases.some(a => a.toLowerCase() === pB.toLowerCase());
                                                    });
                                                    if (memberA) {
                                                        let contribsA = [], contribsB = [];
                                                        const { data: allContribsA } = await supabase.from('member_contributions').select('*').eq('member_id', memberA.id);
                                                        const { data: allContribsB } = memberB ? await supabase.from('member_contributions').select('*').eq('member_id', memberB.id) : { data: [] };
                                                        contribsA = allContribsA || []; contribsB = allContribsB || [];
                                                        let allEventIds = [...new Set([...contribsA.map(c => c.event_id), ...contribsB.map(c => c.event_id)])];
                                                        let eventsData = [];
                                                        if (allEventIds.length > 0) { const { data } = await supabase.from('events').select('*').in('id', allEventIds).order('event_date', { ascending: false }); eventsData = data || []; }
                                                        const processPlayerData = (contribs, member) => {
                                                            const byEvent = {};
                                                            (contribs || []).forEach(c => { 
                                                                if (!byEvent[c.event_id]) byEvent[c.event_id] = { scores: Array(6).fill(0), total: 0 }; 
                                                                if (c.day_number >= 1 && c.day_number <= 6) byEvent[c.event_id].scores[c.day_number - 1] = Number(c.score) || 0;
                                                            });
                                                            Object.values(byEvent).forEach(e => e.total = e.scores.reduce((s, v) => Number(s) + Number(v), 0));
                                                            const eventTotals = Object.entries(byEvent).map(([eventId, data]) => { const event = eventsData.find(e => String(e.id) === String(eventId)); return { eventId: parseInt(eventId), event, ...data }; }).sort((a, b) => new Date(b.event?.event_date || 0) - new Date(a.event?.event_date || 0));
                                                            const overallTotal = eventTotals.reduce((s, e) => Number(s) + Number(e.total), 0);
                                                            const avgPerDay = eventTotals.length > 0 ? overallTotal / (eventTotals.length * 6) : 0;
                                                            const allDayScores = eventTotals.flatMap(e => e.scores.filter(s => s > 0));
                                                            return { member, name: member.display_name || member.name, eventTotals, overallTotal, avgPerDay, maxDay: allDayScores.length > 0 ? Math.max(...allDayScores) : 0, minDay: allDayScores.length > 0 ? Math.min(...allDayScores) : 0, reliability: eventTotals.length > 0 ? (eventTotals.flatMap(e => e.scores).filter(s => s > 0).length / (eventTotals.length * 6) * 100) : 0, eventsCount: eventTotals.length, latestTotal: eventTotals[0]?.total || 0 };
                                                        };
                                                        const playerAData = processPlayerData(contribsA, memberA);
                                                        const playerBData = memberB ? processPlayerData(contribsB, memberB) : null;
                                                        let aWins = 0, bWins = 0, ties = 0;
                                                        if (playerBData) { playerAData.eventTotals.filter(ea => playerBData.eventTotals.some(eb => eb.eventId === ea.eventId)).forEach(ea => { const eb = playerBData.eventTotals.find(e => e.eventId === ea.eventId); if (eb) { if (ea.total > eb.total) aWins++; else if (eb.total > ea.total) bWins++; else ties++; } }); }
                                                        setCompareData({ playerA: playerAData, playerB: playerBData, events: eventsData, headToHead: { aWins, bWins, ties, shared: aWins + bWins + ties } });
                                                    }
                                                } catch (err) { console.error('Compare error:', err); }
                                                setCompareLoading(false);
                                            }
                                        }} disabled={!compareSelectA}>Compare</button>
                                        <button className="btn btn-secondary btn-sm" onClick={() => { setCompareSelectA(null); setCompareSelectB(null); }}>✕</button>
                                    </div>
                                )}
                            </div>
                            {selectedEvent?.id === 'ALL' ? (() => {
                                // Calculate filteredRankings for ALL EVENTS outside IIFE to allow Show More button access
                                
                                // Create overall rankings for All Events with day totals from ALL members (for rank calculation)
                                const allMemberRankings = members.map(m => ({
                                    id: m.id,
                                    name: m.display_name || m.name,
                                    status: m.is_active === false ? 'inactive' : 'active',
                                    is_alt: m.is_alt,
                                    isAlt: m.is_alt,
                                    rank: m.rank,
                                    left_date: m.left_date
                                }));
                                
                                const allOverallRankings = allMemberRankings.map(m => {
                                    const playerContribs = memberContributions.filter(c => {
                                        const member = members.find(mem => mem.id === c.member_id);
                                        return (member?.display_name || member?.name) === m.name;
                                    });
                                    
                                    // Sum scores by day across all events
                                    const daySums = Array(6).fill(0);
                                    playerContribs.forEach(c => {
                                        if (c.day_number >= 1 && c.day_number <= 6) {
                                            daySums[c.day_number - 1] += parseFloat(c.score) || 0;
                                        }
                                    });
                                    
                                    const allEventTotal = daySums.reduce((sum, s) => Number(sum) + Number(s), 0);
                                    const lowPerformanceDays = daySums.filter(s => s > 0 && s < MINIMUM_SCORE).length;
                                    return { ...m, daySums, overallTotal: allEventTotal, lowPerformanceDays };
                                }).sort((a, b) => b.overallTotal - a.overallTotal);
                                
                                // Create rank lookup map from ALL members
                                const rankMap = {};
                                allOverallRankings.forEach((player, index) => {
                                    rankMap[player.name] = index + 1;
                                });
                                
                                // Apply filters to the display list
                                let filteredRankings = allOverallRankings;
                                
                                // Apply low performers filter
                                if (filterLowPerformers) {
                                    filteredRankings = filteredRankings.filter(r => r.lowPerformanceDays > 0 || r.overallTotal === 0);
                                }
                                
                                // Apply active only filter - check both is_active and left_date
                                if (filterActiveOnly) {
                                    filteredRankings = filteredRankings.filter(r => {
                                        // Member is active if: is_active !== false AND (no left_date OR left_date is in future)
                                        const isActive = r.status === 'active' && (!r.left_date || new Date(r.left_date) > new Date());
                                        return isActive;
                                    });
                                }
                                
                                // Apply search filter
                                if (searchQueryRankings.trim()) {
                                    const q = searchQueryRankings.toLowerCase();
                                    filteredRankings = filteredRankings.filter(r => r.name.toLowerCase().includes(q));
                                }
                                
                                // Apply sorting
                                filteredRankings = Array.isArray(filteredRankings) ? filteredRankings : [];
                                filteredRankings.sort((a, b) => {
                                    let result = 0;
                                    if (sortBy === 'total') {
                                        result = b.overallTotal - a.overallTotal;
                                    } else if (sortBy.startsWith('day')) {
                                        const i = parseInt(sortBy.replace('day', '')) - 1;
                                        result = b.daySums[i] - a.daySums[i];
                                    }
                                    return sortAscending ? -result : result;
                                });
                                
                                // Get medals across all events
                                const medalData = getDailyMedalsAllEvents();
                                
                                return (
                                    <>
                                    <div className="table-wrapper compact"><table className="data-table rankings-table compact"><thead><tr><th className="select-col"></th><th>#</th><th>Player</th><th>Rank</th><th>Medals</th>{DAY_NAMES.map((d, i) => (<th key={i} className="sortable" onClick={() => { if (sortBy === `day${i + 1}`) { setSortAscending(!sortAscending); } else { setSortBy(`day${i + 1}`); setSortAscending(false); } }}>{DAY_ICONS[i]}{sortBy === `day${i + 1}` && (sortAscending ? '▲' : '▼')}</th>))}<th className="sortable" onClick={() => { if (sortBy === 'total') { setSortAscending(!sortAscending); } else { setSortBy('total'); setSortAscending(false); } }}>Total{sortBy === 'total' && (sortAscending ? '▲' : '▼')}</th></tr></thead><tbody>
                                    
                                    {!filteredRankings || filteredRankings.length === 0 ? (<tr><td colSpan="12" className="no-results">No players found</td></tr>) : filteredRankings.slice(0, maxPlayersDisplayed).map((m, displayIndex) => {
                                        const normalizedStatus = (m.status === 'kicked' || m.status === 'quit') ? 'inactive' : m.status;
                                        const playerMedals = medalData[m.name] || { gold: 0, silver: 0, bronze: 0 };
                                        const displayRank = displayIndex + 1;
                                        const isSelectedA = compareSelectA === m.name;
                                        const isSelectedB = compareSelectB === m.name;
                                        const handleSelect = () => {
                                            if (isSelectedA) { setCompareSelectA(null); }
                                            else if (isSelectedB) { setCompareSelectB(null); }
                                            else if (!compareSelectA) { setCompareSelectA(m.name); }
                                            else if (!compareSelectB) { setCompareSelectB(m.name); }
                                            else { setCompareSelectA(m.name); setCompareSelectB(null); }
                                        };
                                        return (
                                            <tr key={m.id} className={isSelectedA ? 'selected-a' : isSelectedB ? 'selected-b' : ''}>
                                                <td className="select-col"><button className={`select-btn ${isSelectedA ? 'a' : isSelectedB ? 'b' : ''}`} onClick={handleSelect}>{isSelectedA ? 'A' : isSelectedB ? 'B' : '+'}</button></td>
                                                <td><span className={`rank-badge-sm ${displayRank === 1 ? 'gold' : displayRank === 2 ? 'silver' : displayRank === 3 ? 'bronze' : ''}`}>{displayRank}</span></td>
                                                <td className="player-name" style={{cursor: (userRole === 'admin' || userRole === 'super_admin') ? 'pointer' : 'default'}} onClick={() => { if(userRole === 'admin' || userRole === 'super_admin') { openMemberDetailsModal(m); }}}>{m.name}{m.isAlt === true && <span className="badge-mini alt">alt</span>}{normalizedStatus && normalizedStatus !== 'active' && <span className={`badge-mini ${normalizedStatus}`}>{normalizedStatus}</span>}</td>
                                                <td style={{textAlign: 'center'}}><span className={getRankClass(m.rank)}>{m.rank || 'R1'}</span></td>
                                                <td className="medals-cell">{playerMedals.gold > 0 && <span className="medal gold">🥇{playerMedals.gold}</span>}{playerMedals.silver > 0 && <span className="medal silver">🥈{playerMedals.silver}</span>}{playerMedals.bronze > 0 && <span className="medal bronze">🥉{playerMedals.bronze}</span>}</td>
                                                {m.daySums.map((s, j) => (<td key={j} className={`score-cell ${s === 0 ? 'zero' : s < MINIMUM_SCORE ? 'low' : ''}`}>{formatCompact(s)}</td>))}
                                                <td className="total-cell">{formatCompact(m.overallTotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody></table></div>
                                {filteredRankings && filteredRankings.length > maxPlayersDisplayed && (
                                    <div style={{textAlign: 'center', marginTop: '12px'}}>
                                        <button className="btn btn-primary btn-sm" onClick={() => setMaxPlayersDisplayed(prev => prev + 20)}>
                                            Show More ({maxPlayersDisplayed}/{filteredRankings.length})
                                        </button>
                                    </div>
                                )}
                                    </>
                                );
                            })() : (
                                // INDIVIDUAL EVENT: Show per-day breakdown
                                <>
                                <div className="table-wrapper compact"><table className="data-table rankings-table compact"><thead><tr><th className="select-col"></th><th>#</th><th>Player</th><th>Rank</th><th>Medals</th>{DAY_NAMES.map((d, i) => (<th key={i} className="sortable" onClick={() => { if (sortBy === `day${i + 1}`) { setSortAscending(!sortAscending); } else { setSortBy(`day${i + 1}`); setSortAscending(false); } }}>{DAY_ICONS[i]}{sortBy === `day${i + 1}` && (sortAscending ? '▲' : '▼')}</th>))}<th className="sortable" onClick={() => { if (sortBy === 'total') { setSortAscending(!sortAscending); } else { setSortBy('total'); setSortAscending(false); } }}>Total{sortBy === 'total' && (sortAscending ? '▲' : '▼')}</th></tr></thead><tbody>
                                {(() => { return !rankings || rankings.length === 0 ? (<tr><td colSpan="11" className="no-results">No players found</td></tr>) : rankings.slice(0, maxPlayersDisplayed).map((m, i) => {
                                    const normalizedStatus = (m.status === 'kicked' || m.status === 'quit') ? 'inactive' : m.status;
                                    const playerMedals = eventMedalData[m.name] || { gold: 0, silver: 0, bronze: 0 };
                                    const isSelectedA = compareSelectA === m.name;
                                    const isSelectedB = compareSelectB === m.name;
                                    const handleSelect = () => {
                                        if (isSelectedA) { setCompareSelectA(null); }
                                        else if (isSelectedB) { setCompareSelectB(null); }
                                        else if (!compareSelectA) { setCompareSelectA(m.name); }
                                        else if (!compareSelectB) { setCompareSelectB(m.name); }
                                        else { setCompareSelectA(m.name); setCompareSelectB(null); }
                                    };
                                    return (
                                        <tr key={m.id} className={isSelectedA ? 'selected-a' : isSelectedB ? 'selected-b' : ''}>
                                            <td className="select-col"><button className={`select-btn ${isSelectedA ? 'a' : isSelectedB ? 'b' : ''}`} onClick={handleSelect}>{isSelectedA ? 'A' : isSelectedB ? 'B' : '+'}</button></td>
                                            <td><span className={`rank-badge-sm ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : ''}`}>{i + 1}</span></td>
                                            <td className="player-name" style={{cursor: (userRole === 'admin' || userRole === 'super_admin') ? 'pointer' : 'default'}} onClick={() => { if(userRole === 'admin' || userRole === 'super_admin') { openMemberDetailsModal(m); }}}>{m.name}{m.isAlt === true && <span className="badge-mini alt">alt</span>}{normalizedStatus && normalizedStatus !== 'active' && <span className={`badge-mini ${normalizedStatus}`}>{normalizedStatus}</span>}</td>
                                            <td style={{textAlign: 'center'}}><span className={getRankClass(m.rank)}>{m.rank || 'R1'}</span></td>
                                            <td className="medals-cell">{playerMedals.gold > 0 && <span className="medal gold">🥇{playerMedals.gold}</span>}{playerMedals.silver > 0 && <span className="medal silver">🥈{playerMedals.silver}</span>}{playerMedals.bronze > 0 && <span className="medal bronze">🥉{playerMedals.bronze}</span>}</td>
                                            {m.scores.map((s, j) => (<td key={j} className={`score-cell ${s === 0 ? 'zero' : parseFloat(s) < MINIMUM_SCORE ? 'low' : ''}`}>{formatCompact(parseFloat(s) || 0)}</td>))}
                                            <td className="total-cell">{formatCompact(parseFloat(m.total) || 0)}</td>
                                        </tr>
                                    );
                                }); })()}
                                </tbody></table></div>
                                {rankings && rankings.length > maxPlayersDisplayed && (
                                    <div style={{textAlign: 'center', marginTop: '12px'}}>
                                        <button className="btn btn-primary btn-sm" onClick={() => setMaxPlayersDisplayed(prev => prev + 20)}>
                                            Show More ({maxPlayersDisplayed}/{rankings.length})
                                        </button>
                                    </div>
                                )}
                                </>
                            )}

                            {/* Daily Top 10 + Performance Issues */}
                            <div className="daily-leaders-section">
                                <div className="performance-grid-wrapper">
                                    {/* Left side: Top 10 per Day */}
                                    <div className="performance-column">
                                        <div className="daily-leaders-title">🏆 Top 10 per Day <span className="daily-leaders-subtitle">Top 3 highlighted</span></div>
                                        <div className="daily-top10-grid">
                                            {selectedEvent?.id === 'ALL' ? (
                                                // All Events: Show top individual scores across all events
                                                (() => {
                                                    const topScoresAllEvents = getTopScoresAllEventsPerDay();
                                                    return DAY_NAMES.map((dayName, dayIndex) => {
                                                        const allScoresForDay = topScoresAllEvents[dayIndex];
                                                        return (
                                                            <div key={dayIndex} className="daily-top10-card">
                                                                <div className="daily-top10-header">DAY {dayIndex + 1}</div>
                                                                <div className="daily-top10-table">
                                                                    <div className="daily-top10-row header"><span className="col-rank">#</span><span className="col-name">Name</span><span className="col-score">Score</span></div>
                                                                    {allScoresForDay.map((p, idx) => (
                                                                        <div key={idx} className={`daily-top10-row ${idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? 'bronze' : ''}`}>
                                                                            <span className="col-rank">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}</span>
                                                                            <span className="col-name">{p.name}</span>
                                                                            <span className="col-score">{formatCompact(p.score)}</span>
                                                                        </div>
                                                                    ))}
                                                                    {allScoresForDay.length === 0 && <div className="daily-top10-row empty">No data</div>}
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })()
                                            ) : (
                                                // Individual Event: Show per-day rankings
                                                DAY_NAMES.map((dayName, dayIndex) => {
                                                    const allScoresForDay = rankings.map(r => ({ name: r.name, score: r.scores[dayIndex] })).filter(x => x.score > 0).sort((a, b) => b.score - a.score).slice(0, 10);
                                                    return (
                                                        <div key={dayIndex} className="daily-top10-card">
                                                            <div className="daily-top10-header">DAY {dayIndex + 1}</div>
                                                            <div className="daily-top10-table">
                                                                <div className="daily-top10-row header"><span className="col-rank">#</span><span className="col-name">Name</span><span className="col-score">Score</span></div>
                                                                {allScoresForDay.map((p, idx) => (
                                                                    <div key={idx} className={`daily-top10-row ${idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? 'bronze' : ''}`}>
                                                                        <span className="col-rank">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}</span>
                                                                        <span className="col-name">{p.name}</span>
                                                                        <span className="col-score">{formatCompact(p.score)}</span>
                                                                    </div>
                                                                ))}
                                                                {allScoresForDay.length === 0 && <div className="daily-top10-row empty">No data</div>}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                    {/* Performance Concerns Section - Moved from Dashboard */}
                                    {(userRole === 'admin' || userRole === 'super_admin') && (
                                        <div style={{marginTop: '20px'}}>
                                            <h3 style={{color: 'var(--text-primary)', marginBottom: '12px', fontSize: '14px', fontWeight: '600'}}>⚠️ Performance Concerns</h3>
                                            {getBottomPerformers().length > 0 ? (
                                                <div className="table-wrapper">
                                                    <table className="data-table" style={{fontSize: '11px'}}>
                                                        <thead>
                                                            <tr>
                                                                <th>Player</th>
                                                                <th>Total Score</th>
                                                                <th>Zero Days</th>
                                                                <th>Below 1.5M</th>
                                                                <th>Avg Score</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {getBottomPerformers().map((performer, idx) => (
                                                                <tr key={performer.id} style={{opacity: performer.total === 0 ? '0.7' : '1'}}>
                                                                    <td style={{fontWeight: performer.total === 0 ? '700' : '500', color: performer.total === 0 ? 'var(--accent-red)' : 'inherit'}}>
                                                                        {performer.name}
                                                                    </td>
                                                                    <td style={{color: performer.total === 0 ? 'var(--accent-red)' : 'var(--text-muted)', fontWeight: '600'}}>{formatNumber(performer.total)}</td>
                                                                    <td><span style={{background: performer.zeroCount > 0 ? 'rgba(255,87,51,0.2)' : 'transparent', padding: '1px 4px', borderRadius: '4px', fontSize: '10px'}}>{performer.zeroCount}</span></td>
                                                                    <td><span style={{background: performer.belowThresholdCount > 0 ? 'rgba(255,165,0,0.2)' : 'transparent', padding: '1px 4px', borderRadius: '4px', fontSize: '10px'}}>{performer.belowThresholdCount}</span></td>
                                                                    <td>{formatNumber(Math.round(performer.avgScore))}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div style={{textAlign: 'center', padding: '12px', color: 'var(--text-muted)'}}>
                                                    <div style={{fontSize: '12px'}}>✅ No performance concerns detected!</div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Right side: Zero Scorers & Below Threshold - Admin Only */}
                                    {(userRole === 'admin' || userRole === 'super_admin') && (
                                    <div className="performance-column">
                                        <div className="performance-issues-grid">
                                            {/* Zero Scorers */}
                                            <div className="issues-card">
                                                <div className="issues-header">💀 Zero Scorers (Per Day)</div>
                                                <div className="issues-subtitle">Days column shows all days where score = 0.</div>
                                                <div className="issues-table">
                                                    <div className="issues-row header"><span className="col-num">#</span><span className="col-name">Name</span><span className="col-days">Days</span><span className="col-avg">Avg</span><span className="col-score">Score</span></div>
                                                    {(() => {
                                                        const bottomPerfs = getBottomPerformers().filter(p => p.zeroCount > 0);
                                                        return bottomPerfs.length === 0 ? (<div className="issues-row empty">No zero scorers 🎉</div>) : bottomPerfs.map((p, idx) => {
                                                            const zeroDays = p.allScores ? p.allScores.filter(s => s.score === 0).map(s => s.day) : [];
                                                            const belowThreshDays = p.allScores ? p.allScores.filter(s => s.score > 0 && s.score < MINIMUM_SCORE).map(s => s.day) : [];
                                                            return (
                                                            <div key={idx} className="issues-row zero">
                                                                <span className="col-num">{idx + 1}</span>
                                                                <span className="col-name" style={{cursor: 'pointer', textDecoration: 'underline'}} onClick={() => {
                                                                    const allProblemDays = [...zeroDays, ...belowThreshDays];
                                                                    const uniqueDays = [...new Set(allProblemDays)].sort((a, b) => a - b);
                                                                    // Convert back to scores for modal
                                                                    const scoreArray = p.allScores ? p.allScores.map(s => s.score) : [];
                                                                    openPerformanceModal(p.name, scoreArray, uniqueDays);
                                                                }}>{p.name}</span>
                                                                <span className="col-days">{zeroDays.join(', ')}</span>
                                                                <span className="col-avg">{formatCompact(p.avgScore)}</span>
                                                                <span className="col-score">0</span>
                                                            </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                                <div className="issues-footer">Unique players: {getBottomPerformers().filter(p => p.zeroCount > 0).length} (Total days: {(() => { let t = 0; getBottomPerformers().filter(p => p.zeroCount > 0).forEach(p => { t += p.zeroCount; }); return t; })()})</div>
                                            </div>

                                            {/* Below Threshold */}
                                            <div className="issues-card">
                                                <div className="issues-header">⚠️ Below Threshold (Per Day)</div>
                                                <div className="issues-subtitle">Days column shows all days where 0 &lt; score &lt; threshold.</div>
                                                <div className="issues-table">
                                                    <div className="issues-row header"><span className="col-num">#</span><span className="col-name">Name</span><span className="col-days">Days</span><span className="col-avg">Avg</span><span className="col-score">Scores</span></div>
                                                    {(() => {
                                                        const bottomPerfs = getBottomPerformers().filter(p => p.belowThresholdCount > 0);
                                                        return bottomPerfs.length === 0 ? (<div className="issues-row empty">Everyone above threshold 🎉</div>) : bottomPerfs.map((p, idx) => {
                                                            const lowDays = p.allScores ? p.allScores.filter(s => s.score > 0 && s.score < MINIMUM_SCORE).map(s => s.day) : [];
                                                            const lowScores = p.allScores ? p.allScores.filter(s => s.score > 0 && s.score < MINIMUM_SCORE).map(s => s.score) : [];
                                                            return (
                                                            <div key={idx} className="issues-row low">
                                                                <span className="col-num">{idx + 1}</span>
                                                                <span className="col-name" style={{cursor: 'pointer', textDecoration: 'underline'}} onClick={() => {
                                                                    const zeroDays = p.allScores.filter(s => s.score === 0).map(s => s.day);
                                                                    const allProblemDays = [...zeroDays, ...lowDays];
                                                                    const uniqueDays = [...new Set(allProblemDays)].sort((a, b) => a - b);
                                                                    openPerformanceModal(p.name, p.allScores.map(s => s.score), uniqueDays);
                                                                }}>{p.name}</span>
                                                                <span className="col-days">{lowDays.join(', ')}</span>
                                                                <span className="col-avg">{formatCompact(p.avgScore)}</span>
                                                                <span className="col-score">{lowScores.map(s => formatCompact(s)).join(', ')}</span>
                                                            </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                                <div className="issues-footer">Unique players: {getBottomPerformers().filter(p => p.belowThresholdCount > 0).length} (Total days: {(() => { let t = 0; getBottomPerformers().filter(p => p.belowThresholdCount > 0).forEach(p => { t += p.belowThresholdCount; }); return t; })()})</div>
                                            </div>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            </div>
                        </div>)}

                        {/* Compare Tab */}
                        {activeTab === 'compare' && (
                            <div className="compare-layout">
                                {/* Compact Control Bar */}
                                <div className="compare-controls">
                                    <div className="compare-input-group">
                                        <label>Player A</label>
                                        <input type="text" className="form-input" placeholder="Enter name..." value={comparePlayerA} onChange={(e) => setComparePlayerA(e.target.value)} list="compare-players-a" />
                                        <datalist id="compare-players-a">{members.map(m => <option key={m.id} value={m.display_name || m.name} />)}</datalist>
                                    </div>
                                    <div className="compare-input-group">
                                        <label>Player B <span className="optional">(optional)</span></label>
                                        <input type="text" className="form-input" placeholder="Enter name..." value={comparePlayerB} onChange={(e) => setComparePlayerB(e.target.value)} list="compare-players-b" />
                                        <datalist id="compare-players-b">{members.map(m => <option key={m.id} value={m.display_name || m.name} />)}</datalist>
                                    </div>
                                    <div className="compare-input-group">
                                        <label>Scope</label>
                                        <select className="form-select" value={compareOpponent} onChange={(e) => setCompareOpponent(e.target.value)}>
                                            <option value="ALL">All Events</option>
                                            <option value="LATEST">Latest Only</option>
                                            {[...events].sort((a, b) => new Date(b.event_date) - new Date(a.event_date)).map(ev => {
                                                const weekRange = ev.event_date ? getWeekRangeForDate(ev.event_date, ev.end_date) : 'Unknown';
                                                return <option key={ev.id} value={ev.id}>vs {ev.opponent || 'Unknown'} • {weekRange}</option>;
                                            })}
                                        </select>
                                    </div>
                                    <div className="compare-actions">
                                        <button className="btn btn-primary" onClick={loadCompareData} disabled={compareLoading}>
                                            {compareLoading ? '⏳' : '🔍'} {comparePlayerB.trim() ? 'Compare' : 'Load'}
                                        </button>
                                    </div>
                                </div>

                                {/* View Toggle - Text vs Visual */}
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', padding: '0 0 16px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                    <button
                                        onClick={() => setCompareView('text')}
                                        style={{
                                            padding: '10px 16px',
                                            backgroundColor: compareView === 'text' ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)',
                                            color: 'var(--text-primary)',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        📋 Text View
                                    </button>
                                    <button
                                        onClick={() => setCompareView('visual')}
                                        style={{
                                            padding: '10px 16px',
                                            backgroundColor: compareView === 'visual' ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)',
                                            color: 'var(--text-primary)',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        📊 Visual Comparison
                                    </button>
                                </div>

                                {/* Empty State */}
                                {!compareData && (
                                    <div className="compare-empty">
                                        <div className="compare-empty-icon">⚔️</div>
                                        <div className="compare-empty-title">Player Comparison Hub</div>
                                        <div className="compare-empty-text">Enter a player name above to view stats, or add a second player to compare head-to-head.</div>
                                    </div>
                                )}

                                {/* Visual Comparison View */}
                                {compareView === 'visual' && (
                                    <div style={{ marginTop: '24px' }}>
                                        <PlayerComparison 
                                            members={members} 
                                            memberContributions={memberContributions} 
                                            onClose={() => {}} 
                                            initialMembers={[
                                                ...(
                                                    comparePlayerA 
                                                    ? [members.find(m => (m.display_name || m.name) === comparePlayerA)?.id].filter(Boolean) 
                                                    : []
                                                ),
                                                ...(
                                                    comparePlayerB 
                                                    ? [members.find(m => (m.display_name || m.name) === comparePlayerB)?.id].filter(Boolean) 
                                                    : []
                                                )
                                            ]}
                                        />
                                    </div>
                                )}

                                {/* Results - Text View */}
                                {compareView === 'text' && compareData && (
                                    <>
                                        {/* Head to Head Banner (only if comparing two players) */}
                                        {compareData.playerB && (
                                            <div className="h2h-banner">
                                                <div className={`h2h-player a ${compareData.headToHead.aWins > compareData.headToHead.bWins ? 'winner' : ''}`}>
                                                    {compareData.headToHead.aWins > compareData.headToHead.bWins && <div className="h2h-winner-crown">👑</div>}
                                                    <div className="h2h-player-name">{compareData.playerA.name}</div>
                                                    <div className="h2h-wins">{compareData.headToHead.aWins}</div>
                                                    <div className="h2h-wins-label">Wins</div>
                                                </div>
                                                <div className="h2h-vs">
                                                    <div className="h2h-vs-icon">⚔️</div>
                                                    <div className="h2h-ties">{compareData.headToHead.ties} ties</div>
                                                </div>
                                                <div className={`h2h-player b ${compareData.headToHead.bWins > compareData.headToHead.aWins ? 'winner' : ''}`}>
                                                    {compareData.headToHead.bWins > compareData.headToHead.aWins && <div className="h2h-winner-crown">👑</div>}
                                                    <div className="h2h-player-name">{compareData.playerB.name}</div>
                                                    <div className="h2h-wins">{compareData.headToHead.bWins}</div>
                                                    <div className="h2h-wins-label">Wins</div>
                                                </div>
                                            </div>
                                        )}

                                        {/* KPI Comparison Row */}
                                        <div className="kpi-comparison-row">
                                            <div className="kpi-compare-card">
                                                <div className="kpi-compare-label">Total Score</div>
                                                <div className="kpi-compare-values">
                                                    <span className={`kpi-compare-value a ${!compareData.playerB || compareData.playerA.overallTotal >= compareData.playerB.overallTotal ? 'winner' : ''}`}>{formatCompact(compareData.playerA.overallTotal)}</span>
                                                    {compareData.playerB && <span className={`kpi-compare-value b ${compareData.playerB.overallTotal > compareData.playerA.overallTotal ? 'winner' : ''}`}>{formatCompact(compareData.playerB.overallTotal)}</span>}
                                                </div>
                                            </div>
                                            <div className="kpi-compare-card">
                                                <div className="kpi-compare-label">Avg / Day</div>
                                                <div className="kpi-compare-values">
                                                    <span className={`kpi-compare-value a ${!compareData.playerB || compareData.playerA.avgPerDay >= compareData.playerB.avgPerDay ? 'winner' : ''}`}>{formatCompact(compareData.playerA.avgPerDay)}</span>
                                                    {compareData.playerB && <span className={`kpi-compare-value b ${compareData.playerB.avgPerDay > compareData.playerA.avgPerDay ? 'winner' : ''}`}>{formatCompact(compareData.playerB.avgPerDay)}</span>}
                                                </div>
                                            </div>
                                            <div className="kpi-compare-card">
                                                <div className="kpi-compare-label">Max Day</div>
                                                <div className="kpi-compare-values">
                                                    <span className={`kpi-compare-value a ${!compareData.playerB || compareData.playerA.maxDay >= compareData.playerB.maxDay ? 'winner' : ''}`}>{formatCompact(compareData.playerA.maxDay)}</span>
                                                    {compareData.playerB && <span className={`kpi-compare-value b ${compareData.playerB.maxDay > compareData.playerA.maxDay ? 'winner' : ''}`}>{formatCompact(compareData.playerB.maxDay)}</span>}
                                                </div>
                                            </div>
                                            <div className="kpi-compare-card">
                                                <div className="kpi-compare-label">Min Day</div>
                                                <div className="kpi-compare-values">
                                                    <span className={`kpi-compare-value a ${!compareData.playerB || compareData.playerA.minDay <= compareData.playerB.minDay ? 'winner' : ''}`}>{formatCompact(compareData.playerA.minDay)}</span>
                                                    {compareData.playerB && <span className={`kpi-compare-value b ${compareData.playerB.minDay < compareData.playerA.minDay ? 'winner' : ''}`}>{formatCompact(compareData.playerB.minDay)}</span>}
                                                </div>
                                            </div>
                                            <div className="kpi-compare-card">
                                                <div className="kpi-compare-label">Reliability</div>
                                                <div className="kpi-compare-values">
                                                    <span className={`kpi-compare-value a ${!compareData.playerB || compareData.playerA.reliability >= compareData.playerB.reliability ? 'winner' : ''}`}>{compareData.playerA.reliability.toFixed(0)}%</span>
                                                    {compareData.playerB && <span className={`kpi-compare-value b ${compareData.playerB.reliability > compareData.playerA.reliability ? 'winner' : ''}`}>{compareData.playerB.reliability.toFixed(0)}%</span>}
                                                </div>
                                            </div>
                                            <div className="kpi-compare-card">
                                                <div className="kpi-compare-label">Events</div>
                                                <div className="kpi-compare-values">
                                                    <span className={`kpi-compare-value a ${!compareData.playerB || compareData.playerA.eventsCount >= compareData.playerB.eventsCount ? 'winner' : ''}`}>{compareData.playerA.eventsCount}</span>
                                                    {compareData.playerB && <span className={`kpi-compare-value b ${compareData.playerB.eventsCount > compareData.playerA.eventsCount ? 'winner' : ''}`}>{compareData.playerB.eventsCount}</span>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Player Profile Cards */}
                                        {compareData.playerB ? (
                                            /* Fused Comparison View */
                                            <div className="fused-comparison">
                                                <div className="fused-header">
                                                    <div className="fused-player a">
                                                        <div className="fused-avatar">A</div>
                                                        <div className="fused-name">{compareData.playerA.name}</div>
                                                    </div>
                                                    <div className="fused-vs">VS</div>
                                                    <div className="fused-player b">
                                                        <div className="fused-name">{compareData.playerB.name}</div>
                                                        <div className="fused-avatar">B</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="fused-stats">
                                                    <div className="fused-stat-row">
                                                        <span className={`fused-value a ${compareData.playerA.overallTotal > compareData.playerB.overallTotal ? 'better' : compareData.playerA.overallTotal < compareData.playerB.overallTotal ? 'worse' : ''}`}>{formatCompact(compareData.playerA.overallTotal)}</span>
                                                        <span className="fused-indicator">{compareData.playerA.overallTotal > compareData.playerB.overallTotal ? '>' : compareData.playerA.overallTotal < compareData.playerB.overallTotal ? '<' : '='}</span>
                                                        <span className="fused-label">Total</span>
                                                        <span className="fused-indicator">{compareData.playerB.overallTotal > compareData.playerA.overallTotal ? '<' : compareData.playerB.overallTotal < compareData.playerA.overallTotal ? '>' : '='}</span>
                                                        <span className={`fused-value b ${compareData.playerB.overallTotal > compareData.playerA.overallTotal ? 'better' : compareData.playerB.overallTotal < compareData.playerA.overallTotal ? 'worse' : ''}`}>{formatCompact(compareData.playerB.overallTotal)}</span>
                                                    </div>
                                                    <div className="fused-stat-row">
                                                        <span className={`fused-value a ${compareData.playerA.avgPerDay > compareData.playerB.avgPerDay ? 'better' : compareData.playerA.avgPerDay < compareData.playerB.avgPerDay ? 'worse' : ''}`}>{formatCompact(compareData.playerA.avgPerDay)}</span>
                                                        <span className="fused-indicator">{compareData.playerA.avgPerDay > compareData.playerB.avgPerDay ? '>' : compareData.playerA.avgPerDay < compareData.playerB.avgPerDay ? '<' : '='}</span>
                                                        <span className="fused-label">Avg/Day</span>
                                                        <span className="fused-indicator">{compareData.playerB.avgPerDay > compareData.playerA.avgPerDay ? '<' : compareData.playerB.avgPerDay < compareData.playerA.avgPerDay ? '>' : '='}</span>
                                                        <span className={`fused-value b ${compareData.playerB.avgPerDay > compareData.playerA.avgPerDay ? 'better' : compareData.playerB.avgPerDay < compareData.playerA.avgPerDay ? 'worse' : ''}`}>{formatCompact(compareData.playerB.avgPerDay)}</span>
                                                    </div>
                                                    <div className="fused-stat-row">
                                                        <span className={`fused-value a ${compareData.playerA.reliability > compareData.playerB.reliability ? 'better' : compareData.playerA.reliability < compareData.playerB.reliability ? 'worse' : ''}`}>{compareData.playerA.reliability.toFixed(0)}%</span>
                                                        <span className="fused-indicator">{compareData.playerA.reliability > compareData.playerB.reliability ? '>' : compareData.playerA.reliability < compareData.playerB.reliability ? '<' : '='}</span>
                                                        <span className="fused-label">Reliability</span>
                                                        <span className="fused-indicator">{compareData.playerB.reliability > compareData.playerA.reliability ? '<' : compareData.playerB.reliability < compareData.playerA.reliability ? '>' : '='}</span>
                                                        <span className={`fused-value b ${compareData.playerB.reliability > compareData.playerA.reliability ? 'better' : compareData.playerB.reliability < compareData.playerA.reliability ? 'worse' : ''}`}>{compareData.playerB.reliability.toFixed(0)}%</span>
                                                    </div>
                                                </div>

                                                <div className="fused-events">
                                                    <div className="fused-events-title">📊 Event History</div>
                                                    <div className="fused-events-header">
                                                        <span className="fused-eh-player">Player A</span>
                                                        <span className="fused-eh-event">Opponent</span>
                                                        <span className="fused-eh-player">Player B</span>
                                                    </div>
                                                    {compareData.playerA.eventTotals.slice(0, 6).map((et, idx) => {
                                                        const bEvent = compareData.playerB.eventTotals.find(e => e.eventId === et.eventId);
                                                        const aWins = bEvent ? et.total > bEvent.total : null;
                                                        return (
                                                            <div key={idx} className="fused-event-row">
                                                                <div className="fused-event-score a">
                                                                    <span className={`fused-score ${aWins === true ? 'win' : aWins === false ? 'loss' : ''}`}>{formatCompact(et.total)}</span>
                                                                    {aWins === true && <span className="fused-arrow">◀</span>}
                                                                </div>
                                                                <div className="fused-event-name">vs {et.event?.opponent || 'Unknown'}</div>
                                                                <div className="fused-event-score b">
                                                                    {aWins === false && <span className="fused-arrow">▶</span>}
                                                                    <span className={`fused-score ${aWins === false ? 'win' : aWins === true ? 'loss' : ''}`}>{bEvent ? formatCompact(bEvent.total) : '-'}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            /* Single Player View */
                                            <div className="player-profile player-a">
                                                <div className="player-profile-header">
                                                    <div className="player-avatar">A</div>
                                                    <div className="player-info">
                                                        <div className="player-name">{compareData.playerA.name}</div>
                                                        <div className="player-label">Player A</div>
                                                    </div>
                                                </div>
                                                <div className="player-stats-grid">
                                                    <div className="player-stat">
                                                        <div className="player-stat-value">{formatCompact(compareData.playerA.overallTotal)}</div>
                                                        <div className="player-stat-label">Total</div>
                                                    </div>
                                                    <div className="player-stat">
                                                        <div className="player-stat-value">{formatCompact(compareData.playerA.avgPerDay)}</div>
                                                        <div className="player-stat-label">Avg/Day</div>
                                                    </div>
                                                    <div className="player-stat highlight">
                                                        <div className="player-stat-value">{compareData.playerA.reliability.toFixed(0)}%</div>
                                                        <div className="player-stat-label">Reliability</div>
                                                    </div>
                                                </div>
                                                <div className="player-events">
                                                    <div className="player-events-title">📊 Recent Events</div>
                                                    {compareData.playerA.eventTotals.slice(0, 5).map((et, idx) => (
                                                        <div key={idx} className="player-event-row">
                                                            <span className="player-event-name">vs {et.event?.opponent || 'Unknown'}</span>
                                                            <span className="player-event-score">{formatCompact(et.total)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}


                                    </>
                                )}
                            </div>
                        )}

                        {/* Calendar Tab */}
                        {activeTab === 'calendar' && (
                            <div className="calendar-layout">
                                <div className="card calendar-card">
                                    <div className="calendar-header-top">
                                        <div className="card-header">
                                            <div className="card-title"><span className="card-icon">📅</span>Event Calendar</div>
                                        </div>
                                        {/* Week Navigation - Compact */}
                                        <div className="calendar-week-nav compact">
                                            <button 
                                                className="btn-nav-week" 
                                                onClick={() => setCalendarWeekOffset(prev => prev - 1)}
                                                title="Previous week"
                                            >
                                                ◀
                                            </button>
                                            <div className="calendar-week-label">
                                                {(() => {
                                                    const now = new Date();
                                                    const targetDate = new Date(now.getTime() + calendarWeekOffset * 7 * 24 * 60 * 60 * 1000);
                                                    const dayOfWeek = targetDate.getUTCDay();
                                                    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                                                    const startOfWeek = new Date(targetDate);
                                                    startOfWeek.setUTCDate(targetDate.getUTCDate() + diffToMonday);
                                                    const endOfWeek = new Date(startOfWeek);
                                                    endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6);
                                                    const startStr = startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                                                    const endStr = endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                                                    return `${startStr} - ${endStr}`;
                                                })()}
                                            </div>
                                            <button 
                                                className="btn-nav-week" 
                                                onClick={() => setCalendarWeekOffset(prev => prev + 1)}
                                                title="Next week"
                                            >
                                                ▶
                                            </button>
                                        </div>
                                    </div>
                                    <div className="calendar-subtitle">Weekly recurring events with UTC times</div>

                                    <div className="calendar-grid visual">
                                        {(() => {
                                            const now = new Date();
                                            const targetDate = new Date(now.getTime() + calendarWeekOffset * 7 * 24 * 60 * 60 * 1000);
                                            const dayOfWeek = targetDate.getUTCDay();
                                            const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                                            const startOfWeek = new Date(targetDate);
                                            startOfWeek.setUTCDate(targetDate.getUTCDate() + diffToMonday);
                                            startOfWeek.setUTCHours(0, 0, 0, 0);
                                            
                                            // Get rotating events for this week
                                            const rotatingEventsThisWeek = getRotatingEvents().filter(ev => {
                                                const endOfWeek = new Date(startOfWeek);
                                                endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 7);
                                                return ev.date >= startOfWeek && ev.date < endOfWeek;
                                            });
                                            
                                            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                            
                                            return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                                                // Calculate the date for this day
                                                const dayIndex = dayNames.indexOf(day);
                                                const dayDate = new Date(startOfWeek);
                                                const daysOffset = dayIndex === 0 ? 6 : dayIndex - 1;
                                                dayDate.setUTCDate(startOfWeek.getUTCDate() + daysOffset);
                                                const dateNum = dayDate.getUTCDate();
                                                const dateStr = dayDate.toISOString().split('T')[0];
                                                
                                                // Filter recurring events for this day
                                                const dayEvents = (Array.isArray(calendarEvents) ? calendarEvents : []).filter(e => {
                                                    // INCLUDE six-day events here so they show in calendar grid
                                                    if (e.frequency === 'six-day' && e.startDate) {
                                                        const startDate = new Date(e.startDate + 'T00:00:00Z');
                                                        const currentDate = new Date(dateStr + 'T00:00:00Z');
                                                        const diffTime = currentDate - startDate;
                                                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                                        return diffDays >= 0 && diffDays < 6;
                                                    }
                                                    
                                                    if (e.frequency === 'one-time') return e.date === dateStr;
                                                    if (e.frequency === 'daily') {
                                                        // For daily events, check if the event has started
                                                        if (e.startDate && e.startDate > dateStr) return false;
                                                        return true;
                                                    }
                                                    
                                                    // Handle monthly: every 4 weeks (28 days) from start_date
                                                    if (e.frequency === 'monthly') {
                                                        if (e.startDate && e.startDate > dateStr) return false;
                                                        // Monthly events recur every 4 weeks (28 days) from start_date
                                                        if (e.startDate) {
                                                            const startDate = new Date(e.startDate + 'T00:00:00Z');
                                                            const currentDate = new Date(dateStr + 'T00:00:00Z');
                                                            const diffTime = currentDate - startDate;
                                                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                                            return diffDays % 28 === 0;
                                                        }
                                                        return false;
                                                    }
                                                    
                                                    // For weekly/bi-weekly, check if day matches and event has started
                                                    const dayMatches = e.day === day;
                                                    if (!dayMatches) return false;
                                                    if (e.startDate && e.startDate > dateStr) return false;
                                                    
                                                    // Handle weekly: only show on the correct weeks from start date
                                                    if (e.frequency === 'weekly' && e.startDate) {
                                                        const startDate = new Date(e.startDate + 'T00:00:00Z');
                                                        const currentDate = new Date(dateStr + 'T00:00:00Z');
                                                        const diffTime = currentDate - startDate;
                                                        const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
                                                        return diffWeeks >= 0;
                                                    }
                                                    
                                                    // Handle bi-weekly: only show every 2 weeks from start date
                                                    if (e.frequency === 'bi-weekly' && e.startDate) {
                                                        const startDate = new Date(e.startDate + 'T00:00:00Z');
                                                        const currentDate = new Date(dateStr + 'T00:00:00Z');
                                                        const diffTime = currentDate - startDate;
                                                        const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
                                                        return diffWeeks % 2 === 0;
                                                    }
                                                    
                                                    return true;
                                                });
                                                // Get rotating events for this day
                                                const rotatingForDay = rotatingEventsThisWeek.filter(ev => dayNames[ev.date.getUTCDay()] === day);
                                                
                                                // Separate regular events from six-day events
                                                const regularEvents = dayEvents.filter(e => e.frequency !== 'six-day');
                                                const sixDayEvents = dayEvents.filter(e => e.frequency === 'six-day');
                                                
                                                const allEvents = [...regularEvents, ...rotatingForDay, ...sixDayEvents];
                                                const hasEvents = allEvents.length > 0;
                                                
                                                return (
                                                    <div key={day} className="calendar-day-cell">
                                                        <div className="calendar-day-label">
                                                            <span className="day-abbr">{day.substring(0, 3).toUpperCase()}</span>
                                                            <span className="day-num">{dateNum}</span>
                                                        </div>
                                                        <div className="calendar-day-container">
                                                            {/* Fixed-height container for regular events (above red separator) */}
                                                            <div style={{ 
                                                                maxHeight: '320px', 
                                                                overflowY: 'auto',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '6px',
                                                                paddingRight: '3px'
                                                            }}>
                                                                {!hasEvents && (
                                                                    <div className="calendar-day-empty">
                                                                        <span>-</span>
                                                                    </div>
                                                                )}
                                                                {/* Regular events (not six-day) */}
                                                                {regularEvents.map(ev => {
                                                                    return (
                                                                    <div 
                                                                        key={`ev-${ev.id}`} 
                                                                        className="calendar-event-card"
                                                                        style={{ 
                                                                            borderLeftColor: ev.color,
                                                                            backgroundImage: ev.backgroundImage && typeof ev.backgroundImage === 'string' && !ev.backgroundImage.startsWith('data:') ? `linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('${ev.backgroundImage}')` : `linear-gradient(135deg, ${ev.color}22, ${ev.color}11)`,
                                                                            backgroundSize: 'cover',
                                                                            backgroundPosition: 'center'
                                                                        }} 
                                                                        onClick={() => { setEditingEventId(ev.id); setEditingEventData({...ev}); }}
                                                                    >
                                                                    <div className="event-card-header">
                                                                        <div className="event-card-abbr" style={{ background: ev.color, color: '#fff' }}>
                                                                            {ev.abbr}
                                                                        </div>
                                                                        <div className="event-card-close">
                                                                            {(userRole === 'admin' || userRole === 'super_admin') && (
                                                                                <button 
                                                                                    className="event-card-btn-delete" 
                                                                                    onClick={async (e) => { 
                                                                                        e.stopPropagation();
                                                                                        
                                                                                        // Try to delete from backend first
                                                                                        const backendDeleted = await deleteEventFromBackend(ev.id, ev.frequency);
                                                                                        if (backendDeleted) {
                                                                                            // Success - remove from state and localStorage
                                                                                            const updatedEvents = (Array.isArray(calendarEvents) ? calendarEvents : []).filter(event => event.id !== ev.id);
                                                                                            setCalendarEvents(updatedEvents);
                                                                                            
                                                                                            try { 
                                                                                                localStorage.setItem('hot_calendar_events', JSON.stringify(updatedEvents)); 
                                                                                            } catch (e) { 
                                                                                                console.error('Failed to save to localStorage:', e); 
                                                                                            }
                                                                                            
                                                                                            showToast('success', 'Deleted', `Event "${ev.name}" deleted`);
                                                                                        } else {
                                                                                            // Failed - clear localStorage and reload from backend
                                                                                            showToast('error', 'Sync Error', `Event may have been already deleted. Refreshing...`);
                                                                                            try { 
                                                                                                localStorage.removeItem('hot_calendar_events');
                                                                                            } catch (e) { 
                                                                                                console.error('Failed to clear localStorage:', e); 
                                                                                            }
                                                                                            setTimeout(() => window.location.reload(), 1000);
                                                                                        }
                                                                                    }}
                                                                                    title="Delete event"
                                                                                >
                                                                                    ✕
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="event-card-title">
                                                                        {ev.name}
                                                                    </div>
                                                                    <div className="event-card-time">⏰ {ev.time} UTC</div>
                                                                    {(userRole === 'admin' || userRole === 'super_admin') && (
                                                                        <div className="event-card-actions">
                                                                            <button 
                                                                                className="event-card-btn" 
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    fetch(`/api/send-reminder-for-event/${ev.id}`, {
                                                                                        method: 'POST',
                                                                                        headers: { 'Content-Type': 'application/json' },
                                                                                        body: JSON.stringify({ 
                                                                                            frequency: ev.frequency,
                                                                                            reminderMessage: ev.reminderMessage || '',
                                                                                            guideUrl: ev.guideUrl || ''
                                                                                        })
                                                                                    }).then(r => r.json()).then(data => {
                                                                                        if (data.success) {
                                                                                            showToast('success', 'Reminder Sent', `Reminder for ${ev.name} sent to Discord!`);
                                                                                        } else {
                                                                                            showToast('error', 'Error', data.error || 'Failed to send reminder');
                                                                                        }
                                                                                    }).catch(e => showToast('error', 'Error', e.message));
                                                                                }}
                                                                                title="Send reminder to Discord now"
                                                                            >
                                                                                🔔
                                                                            </button>
                                                                            <button 
                                                                                className="event-card-btn" 
                                                                                onClick={() => {
                                                                                    setEditingEventId(ev.id);
                                                                                    setEditingEventData({...ev});
                                                                                }}
                                                                                title="Edit event"
                                                                            >
                                                                                ✏️
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                );
                                                            })}
                                                            {/* Rotating events (GB/DI) for this day */}
                                                            {rotatingForDay.map((ev, idx) => {
                                                                const timeStr = ev.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
                                                                const backgroundImage = calendarConfig.rotating_event_backgrounds?.[ev.type];
                                                                const reminder = calendarConfig.rotating_event_reminder_messages?.[ev.type];
                                                                const guideData = calendarConfig.rotating_event_guides?.[ev.type];
                                                                const linkedGuide = guideData ? (typeof guideData === 'object' ? guideData : guides.find(g => g.id === guideData)) : null;
                                                                return (
                                                                    <div 
                                                                        key={`rot-${idx}`} 
                                                                        className="calendar-event-card rotating" 
                                                                        style={{ 
                                                                            borderLeftColor: ev.color,
                                                                            backgroundImage: backgroundImage && typeof backgroundImage === 'string' && !backgroundImage.startsWith('data:') ? `linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('${backgroundImage}')` : `linear-gradient(135deg, ${ev.color}22, ${ev.color}11)`,
                                                                            backgroundSize: 'cover',
                                                                            backgroundPosition: 'center'
                                                                        }}
                                                                    >
                                                                        <div className="event-card-header">
                                                                            <div className="event-card-abbr" style={{ background: ev.color, color: '#fff' }}>{ev.type}</div>
                                                                            {ev.shifted && <div className="event-card-shift">+30m</div>}
                                                                        </div>
                                                                        <div className="event-card-title">{ev.name}</div>
                                                                        <div className="event-card-time">⏰ {timeStr} UTC</div>
                                                                        {reminder && <div className="event-card-subtitle" style={{ fontSize: '7px', marginTop: '4px', color: '#ffd700' }}>💬 Advanced discord reminder configured</div>}
                                                                        {linkedGuide && (linkedGuide.url || linkedGuide.guideUrl) && <a href={linkedGuide.url || linkedGuide.guideUrl} onClick={() => { if (linkedGuide.id) { setSelectedGuideId(linkedGuide.id); setActiveTab('guides'); window.location.hash = `guides-${linkedGuide.id}`; } }} className="event-card-subtitle" style={{ fontSize: '12px', marginTop: '4px', color: '#87ceeb', textDecoration: 'none', cursor: 'pointer', display: 'block', target: '_blank' }}>📖 {linkedGuide.title || 'View Guide'}</a>}
                                                                        {(userRole === 'admin' || userRole === 'super_admin') && (
                                                                            <div className="event-card-actions">
                                                                                <button 
                                                                                    className="event-card-btn" 
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        // Send manual reminder for rotating event
                                                                                        const timeStr = ev.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
                                                                                        fetch(`/api/send-reminder-for-rotating-event`, {
                                                                                            method: 'POST',
                                                                                            headers: { 'Content-Type': 'application/json' },
                                                                                            body: JSON.stringify({ 
                                                                                                type: ev.type,
                                                                                                date: ev.date.toISOString(),
                                                                                                name: ev.name,
                                                                                                time: timeStr,
                                                                                                color: ev.color,
                                                                                                shifted: ev.shifted || false
                                                                                            })
                                                                                        }).then(r => r.json()).then(data => {
                                                                                            if (data.success) {
                                                                                                showToast('success', 'Reminder Sent', `Reminder for ${ev.name} sent to Discord!`);
                                                                                            } else {
                                                                                                showToast('error', 'Error', data.error || 'Failed to send reminder');
                                                                                            }
                                                                                        }).catch(e => showToast('error', 'Error', e.message));
                                                                                    }}
                                                                                    title="Send reminder to Discord now"
                                                                                >
                                                                                    🔔
                                                                                </button>
                                                                                <button 
                                                                                    className="event-card-btn"
                                                                                    onClick={() => {
                                                                                        setEditingRotatingEvent({...ev, originalDate: new Date(ev.date), backgroundImage: backgroundImage, reminderMessage: reminder, guideId: linkedGuide ? String(linkedGuide.id) : '', guideText: linkedGuide?.title || '', guideUrl: linkedGuide?.url || linkedGuide?.guideUrl || '' });
                                                                                    }}
                                                                                    title="Edit time"
                                                                                >
                                                                                    ✏️
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                            </div>
                                                            {/* End of fixed-height container for regular events */}
                                                            
                                                            {/* Separator line above six-day events (always show for consistency) */}
                                                            {sixDayEvents.length > 0 && (
                                                                <div style={{ 
                                                                    width: '100%', 
                                                                    height: '3px', 
                                                                    background: 'linear-gradient(90deg, #FF0000, #8B0000, #FF0000)', 
                                                                    margin: '12px 0 8px 0',
                                                                    boxShadow: '0 0 10px rgba(255,0,0,0.6)',
                                                                    borderRadius: '2px'
                                                                }} />
                                                            )}
                                                            
                                                            {/* Six-Day Events (below red line) */}
                                                            {sixDayEvents.map(ev => {
                                                                // Calculate which day (1-6) this is
                                                                const startDate = new Date(ev.startDate + 'T00:00:00Z');
                                                                const currentDate = new Date(dateStr + 'T00:00:00Z');
                                                                const diffTime = currentDate - startDate;
                                                                const dayNum = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1; // 1-6
                                                                
                                                                // Get day theme from reminder_messages
                                                                const dayTheme = ev.reminderMessages?.[dayNum] || `Day ${dayNum}`;
                                                                const isWarDay = dayNum === 6;
                                                                
                                                                return (
                                                                    <div 
                                                                        key={`six-day-${ev.id}`} 
                                                                        className="calendar-event-card"
                                                                        style={{ 
                                                                            borderLeftColor: ev.color,
                                                                            borderWidth: isWarDay ? '3px' : '2px',
                                                                            borderLeftWidth: isWarDay ? '6px' : '4px',
                                                                            backgroundImage: ev.backgroundImage && typeof ev.backgroundImage === 'string' && !ev.backgroundImage.startsWith('data:') ? `linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('${ev.backgroundImage}')` : `linear-gradient(135deg, ${ev.color}22, ${ev.color}11)`,
                                                                            backgroundSize: 'cover',
                                                                            backgroundPosition: 'center',
                                                                            ...(isWarDay && { background: 'linear-gradient(135deg, rgba(255,0,0,0.2), rgba(139,0,0,0.1))' })
                                                                        }} 
                                                                        onClick={() => { setEditingEventId(ev.id); setEditingEventData({...ev}); }}
                                                                    >
                                                                        <div className="event-card-header">
                                                                            <div className="event-card-abbr" style={{ background: isWarDay ? '#8B0000' : ev.color, color: '#fff' }}>
                                                                                D{dayNum}
                                                                            </div>
                                                                            <div className="event-card-close">
                                                                                {(userRole === 'admin' || userRole === 'super_admin') && (
                                                                                    <button 
                                                                                        className="event-card-btn-delete" 
                                                                                        onClick={async (e) => { 
                                                                                            e.stopPropagation();
                                                                                            
                                                                                            // Try to delete from backend first
                                                                                            const backendDeleted = await deleteEventFromBackend(ev.id, ev.frequency);
                                                                                            if (backendDeleted) {
                                                                                                // Success - remove from state and localStorage
                                                                                                const updatedEvents = (Array.isArray(calendarEvents) ? calendarEvents : []).filter(event => event.id !== ev.id);
                                                                                                setCalendarEvents(updatedEvents);
                                                                                                
                                                                                                try { 
                                                                                                    localStorage.setItem('hot_calendar_events', JSON.stringify(updatedEvents)); 
                                                                                                } catch (e) { 
                                                                                                    console.error('Failed to save to localStorage:', e); 
                                                                                                }
                                                                                                
                                                                                                showToast('success', 'Deleted', `Event "${ev.name}" deleted`);
                                                                                            } else {
                                                                                                // Failed - clear localStorage and reload from backend
                                                                                                showToast('error', 'Sync Error', `Event may have been already deleted. Refreshing...`);
                                                                                                try { 
                                                                                                    localStorage.removeItem('hot_calendar_events');
                                                                                                } catch (e) { 
                                                                                                    console.error('Failed to clear localStorage:', e); 
                                                                                                }
                                                                                                setTimeout(() => window.location.reload(), 1000);
                                                                                            }
                                                                                        }}
                                                                                        title="Delete event"
                                                                                    >
                                                                                        ✕
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="event-card-title">
                                                                            {dayTheme}
                                                                        </div>
                                                                        <div className="event-card-subtitle" style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                                                            {ev.name}
                                                                        </div>
                                                                        <div className="event-card-time">⏰ {ev.time} UTC</div>
                                                                        {isWarDay && (
                                                                            <div style={{ fontSize: '10px', color: '#ff6b6b', fontWeight: '700', marginTop: '4px' }}>
                                                                                ⚔️ WAR DAY
                                                                            </div>
                                                                        )}
                                                                        {(userRole === 'admin' || userRole === 'super_admin') && (
                                                                            <div className="event-card-actions">
                                                                                <button 
                                                                                    className="event-card-btn" 
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        fetch(`/api/send-reminder-for-event/${ev.id}`, {
                                                                                            method: 'POST',
                                                                                            headers: { 'Content-Type': 'application/json' },
                                                                                            body: JSON.stringify({ 
                                                                                                frequency: ev.frequency,
                                                                                                reminderMessage: dayTheme,
                                                                                                dayNum: dayNum
                                                                                            })
                                                                                        }).then(r => r.json()).then(data => {
                                                                                            if (data.success) {
                                                                                                showToast('success', 'Reminder Sent', `Day ${dayNum} reminder sent!`);
                                                                                            } else {
                                                                                                showToast('error', 'Error', data.error || 'Failed to send reminder');
                                                                                            }
                                                                                        }).catch(e => showToast('error', 'Error', e.message));
                                                                                    }}
                                                                                    title="Send reminder to Discord now"
                                                                                >
                                                                                    🔔
                                                                                </button>
                                                                                <button 
                                                                                    className="event-card-btn" 
                                                                                    onClick={() => {
                                                                                        setEditingEventId(ev.id);
                                                                                        setEditingEventData({...ev});
                                                                                    }}
                                                                                    title="Edit event"
                                                                                >
                                                                                    ✏️
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>

                                    {/* Six-Day Events Section (Separate from regular calendar) */}
                                    {(() => {
                                        const sixDayEvents = (Array.isArray(calendarEvents) ? calendarEvents : []).filter(e => e.frequency === 'six-day');
                                        if (sixDayEvents.length === 0) return null;
                                        
                                        return (
                                            <div style={{ marginTop: '30px', padding: '20px', background: 'linear-gradient(135deg, rgba(255,107,75,0.05), rgba(58,95,255,0.05))', borderRadius: '12px', border: '2px solid rgba(255,107,75,0.3)' }}>
                                                <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{ fontSize: '24px' }}>🏆</span>
                                                    <span>Six-Day Events (Mon-Sat)</span>
                                                </div>
                                                
                                                {sixDayEvents.map(event => {
                                                    if (!event.startDate) return null;
                                                    
                                                    return (
                                                        <div key={`six-day-${event.id}`} style={{ marginBottom: '30px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border-color)' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                                                <div>
                                                                    <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>{event.name}</div>
                                                                    <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                                                        Starts: {new Date(event.startDate + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} • {event.time} UTC
                                                                    </div>
                                                                </div>
                                                                {(userRole === 'admin' || userRole === 'super_admin') && (
                                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                                        <button 
                                                                            className="event-card-btn"
                                                                            onClick={() => { setEditingEventId(event.id); setEditingEventData({...event}); }}
                                                                            title="Edit event"
                                                                            style={{ padding: '8px 12px', fontSize: '14px' }}
                                                                        >
                                                                            ✏️ Edit
                                                                        </button>
                                                                        <button 
                                                                            className="event-card-btn-delete"
                                                                            onClick={async () => {
                                                                                const originalEvents = Array.isArray(calendarEvents) ? calendarEvents : [];
                                                                                const updatedEvents = originalEvents.filter(e => e.id !== event.id);
                                                                                setCalendarEvents(updatedEvents);
                                                                                
                                                                                try { 
                                                                                    localStorage.setItem('hot_calendar_events', JSON.stringify(updatedEvents)); 
                                                                                } catch (e) { 
                                                                                    console.error('Failed to save to localStorage:', e); 
                                                                                }
                                                                                
                                                                                const backendDeleted = await deleteEventFromBackend(event.id, event.frequency);
                                                                                if (backendDeleted) {
                                                                                    showToast('success', 'Deleted', `Event "${event.name}" deleted`);
                                                                                } else {
                                                                                    setCalendarEvents(originalEvents);
                                                                                    try { 
                                                                                        localStorage.setItem('hot_calendar_events', JSON.stringify(originalEvents)); 
                                                                                    } catch (e) { 
                                                                                        console.error('Failed to restore to localStorage:', e); 
                                                                                    }
                                                                                    showToast('error', 'Delete Failed', `Failed to delete "${event.name}"`);
                                                                                }
                                                                            }}
                                                                            title="Delete event"
                                                                            style={{ padding: '8px 12px', fontSize: '14px' }}
                                                                        >
                                                                            🗑️ Delete
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {/* All 6 days grid */}
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                                                                {[1, 2, 3, 4, 5, 6].map(dayNum => {
                                                                    const startDate = new Date(event.startDate + 'T00:00:00Z');
                                                                    const currentDate = new Date(startDate);
                                                                    currentDate.setUTCDate(startDate.getUTCDate() + dayNum - 1);
                                                                    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][currentDate.getUTCDay()];
                                                                    const dateStr = `${currentDate.getUTCMonth() + 1}/${currentDate.getUTCDate()}`;
                                                                    
                                                                    const dayTheme = event.reminderMessages?.[dayNum] || `Day ${dayNum}`;
                                                                    const isWarDay = dayNum === 6;
                                                                    
                                                                    return (
                                                                        <div 
                                                                            key={dayNum}
                                                                            style={{
                                                                                background: isWarDay ? 'linear-gradient(135deg, rgba(255,0,0,0.2), rgba(139,0,0,0.1))' : `linear-gradient(135deg, ${event.color}22, ${event.color}11)`,
                                                                                border: isWarDay ? '3px solid rgba(255,0,0,0.5)' : `2px solid ${event.color}44`,
                                                                                borderRadius: '10px',
                                                                                padding: '16px',
                                                                                position: 'relative',
                                                                                overflow: 'hidden',
                                                                                minHeight: '120px',
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                backgroundImage: event.backgroundImage ? `linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('${event.backgroundImage}')` : undefined,
                                                                                backgroundSize: 'cover',
                                                                                backgroundPosition: 'center'
                                                                            }}
                                                                        >
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                                                                <div style={{ 
                                                                                    background: isWarDay ? '#8B0000' : event.color, 
                                                                                    color: '#fff', 
                                                                                    padding: '6px 12px', 
                                                                                    borderRadius: '6px', 
                                                                                    fontSize: '14px', 
                                                                                    fontWeight: '700',
                                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                                                                }}>
                                                                                    D{dayNum}
                                                                                </div>
                                                                                <div style={{ fontSize: '12px', color: isWarDay ? '#ff6b6b' : 'var(--text-muted)', fontWeight: '600' }}>
                                                                                    {dayName} {dateStr}
                                                                                </div>
                                                                            </div>
                                                                            <div style={{ 
                                                                                fontSize: '14px', 
                                                                                fontWeight: '600', 
                                                                                color: 'var(--text-primary)', 
                                                                                marginBottom: '8px',
                                                                                flex: 1
                                                                            }}>
                                                                                {dayTheme}
                                                                            </div>
                                                                            {isWarDay && (
                                                                                <div style={{ 
                                                                                    fontSize: '12px', 
                                                                                    color: '#ff6b6b', 
                                                                                    fontWeight: '700',
                                                                                    background: 'rgba(255,0,0,0.2)',
                                                                                    padding: '4px 8px',
                                                                                    borderRadius: '4px',
                                                                                    marginTop: 'auto'
                                                                                }}>
                                                                                    ⚔️ WAR DAY
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}

                                    {/* Event List View */}
                                    <div className="calendar-list">
                                        <div className="calendar-list-title">📋 All Events</div>
                                        <div className="calendar-list-grid">
                                            {(Array.isArray(calendarEvents) ? calendarEvents : []).sort((a, b) => {
                                                const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                return days.indexOf(a.day) - days.indexOf(b.day);
                                            }).map(ev => (
                                                <div key={ev.id} className="calendar-list-item" style={{ borderLeftColor: ev.color }}>
                                                    <div className="calendar-list-abbr" style={{ background: ev.color }}>{ev.abbr}</div>
                                                    <div className="calendar-list-info">
                                                        <div className="calendar-list-name">{ev.name}</div>
                                                        <div className="calendar-list-schedule">{ev.day} @ {ev.time} UTC • {ev.frequency}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Two Column Layout: Rotating Events + Create Form */}
                                    <div className="calendar-bottom-layout">
                                        {/* Rotating Events (GB & DI) - Left Side */}
                                        <div className="rotating-events-section">
                                            <div className="rotating-events-title">🔄 Upcoming Rotating Events</div>
                                            <div className="rotating-events-subtitle">Guild Boss (every 3 days) & Dark Invasion (Thursdays)</div>
                                            <div className="rotating-events-grid">
                                                {getRotatingEvents().slice(0, 12).map((ev, idx) => {
                                                    const dateStr = ev.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
                                                    const timeStr = ev.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
                                                    const isToday = new Date().toDateString() === ev.date.toDateString();
                                                    const backgroundImage = calendarConfig.rotating_event_backgrounds?.[ev.type];
                                                    const reminder = calendarConfig.rotating_event_reminder_messages?.[ev.type];
                                                    const guideData = calendarConfig.rotating_event_guides?.[ev.type];
                                                    const guide = guideData ? (typeof guideData === 'object' ? guideData : guides.find(g => g.id === guideData)) : null;
                                                    return (
                                                        <div 
                                                            key={idx} 
                                                            className={`rotating-event ${isToday ? 'today' : ''}`} 
                                                            style={{ 
                                                                borderLeftColor: ev.color,
                                                                backgroundImage: backgroundImage ? `linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url(${backgroundImage})` : 'none',
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center'
                                                            }}
                                                        >
                                                            <div className="rotating-event-type" style={{ background: ev.color }}>{ev.type}</div>
                                                            <div className="rotating-event-info">
                                                                <div className="rotating-event-date">{dateStr}</div>
                                                                <div className="rotating-event-time">{timeStr} UTC{ev.shifted ? ' ⚠️' : ''}</div>
                                                                {reminder && <div style={{ fontSize: '7px', marginTop: '4px', color: '#ffd700' }}>💬 Advanced discord reminder configured</div>}
                                                                {guide && (guide.url || guide.guideUrl) && <a href={guide.url || guide.guideUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', marginTop: '2px', color: '#87ceeb', textDecoration: 'none', display: 'block' }}>📖 {guide.title || 'View Guide'}</a>}
                                                            </div>
                                                            {ev.shifted && <div className="rotating-event-note">+30m (DI conflict)</div>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Add Event Form (Admin only) - Right Side */}
                                        {(userRole === 'admin' || userRole === 'super_admin') && (
                                            <div className="calendar-add-form">
                                            <div className="calendar-add-header">
                                                <div className="calendar-add-icon">➕</div>
                                                <div>
                                                    <div className="calendar-add-title">Create New Event</div>
                                                    <div className="calendar-add-subtitle">Add a custom event to the calendar</div>
                                                </div>
                                            </div>
                                            
                                            <div className="calendar-add-row">
                                                <div className="calendar-input-group" style={{ width: '100%' }}>
                                                    <label>Event Type (Optional)</label>
                                                    <select 
                                                        className="form-select" 
                                                        value={newCalEvent.eventTypeId || ''}
                                                        onChange={(e) => {
                                                            if (e.target.value) {
                                                                const selectedType = eventTypes.find(et => et.id === parseInt(e.target.value));
                                                                if (selectedType) {
                                                                    setNewCalEvent(p => ({ 
                                                                        ...p, 
                                                                        eventTypeId: selectedType.id,
                                                                        color: selectedType.color,
                                                                        backgroundImage: selectedType.backgroundImage
                                                                    }));
                                                                }
                                                            } else {
                                                                setNewCalEvent(p => ({ 
                                                                    ...p, 
                                                                    eventTypeId: null,
                                                                    color: '#ff5a3a',
                                                                    backgroundImage: ''
                                                                }));
                                                            }
                                                        }}
                                                    >
                                                        <option value="">-- Select or Create Custom --</option>
                                                        {eventTypes.map(et => (
                                                            <option key={et.id} value={et.id}>{et.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="calendar-add-row">
                                                <div className="calendar-input-group">
                                                    <label>Event Name</label>
                                                    <input type="text" className="form-input" placeholder="e.g. Guild Arms Race" value={newCalEvent.name} onChange={(e) => setNewCalEvent(p => ({ ...p, name: e.target.value }))} />
                                                </div>
                                                <div className="calendar-input-group">
                                                    <label>Abbreviation</label>
                                                    <input type="text" className="form-input" placeholder="e.g. GAR" maxLength={4} value={newCalEvent.abbr} onChange={(e) => setNewCalEvent(p => ({ ...p, abbr: e.target.value.toUpperCase() }))} />
                                                </div>
                                                <div className="calendar-input-group">
                                                    <label>Color</label>
                                                    <input type="color" className="form-input color-input" value={newCalEvent.color} onChange={(e) => setNewCalEvent(p => ({ ...p, color: e.target.value }))} />
                                                </div>
                                            </div>

                                            <div className="calendar-add-row">
                                                <div className="calendar-input-group" style={{ width: '100%' }}>
                                                    <label>Background Image (Optional)</label>
                                                    <input 
                                                        type="file" 
                                                        className="form-input" 
                                                        accept="image/*"
                                                        onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                try {
                                                                    // Upload file to backend
                                                                    const formData = new FormData();
                                                                    formData.append('file', file);
                                                                    
// Use hash based on event abbr for consistent file path
                                                    const eventHash = newCalEvent.abbr || newCalEvent.name || 'event';
                                                    const response = await fetch(`/api/upload-event-background?eventId=${eventHash.toLowerCase()}`, {
                                                                        method: 'POST',
                                                                        body: formData
                                                                    });
                                                                    
                                                                    if (!response.ok) {
                                                                        const error = await response.json();
                                                                        showToast('error', 'Upload Failed', error.error || 'Failed to upload background image');
                                                                        return;
                                                                    }
                                                                    
                                                                    const data = await response.json();
                                                                    if (DEBUG) console.log('[Upload] Response:', data);
                                                                    setNewCalEvent(p => ({ ...p, backgroundImage: data.url }));
                                                                    showToast('success', 'Image Uploaded', `Image saved to: ${data.url}`);
                                                                } catch (err) {
                                                                    console.error('Upload error:', err);
                                                                    showToast('error', 'Upload Error', err.message || 'Failed to upload image');
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    {newCalEvent.backgroundImage && (
                                                        <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', height: '80px' }}>
                                                            <img src={newCalEvent.backgroundImage} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { console.error('Preview image failed to load:', newCalEvent.backgroundImage); e.target.style.display = 'none'; }} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="calendar-add-row">
                                                <div className="calendar-input-group">
                                                    <label>Frequency</label>
                                                    <select className="form-select" value={newCalEvent.frequency} onChange={(e) => setNewCalEvent(p => ({ ...p, frequency: e.target.value, day: e.target.value === 'one-time' ? '' : (p.day || 'Monday') }))}>
                                                        <option value="one-time">One-Time</option>
                                                        <option value="daily">Daily</option>
                                                        <option value="weekly">Weekly</option>
                                                        <option value="bi-weekly">Bi-Weekly</option>
                                                        <option value="monthly">Monthly</option>
                                                    </select>
                                                </div>
                                                <div className="calendar-input-group">
                                                    <label>Time (UTC)</label>
                                                    <input type="time" className="form-input" value={newCalEvent.time} onChange={(e) => setNewCalEvent(p => ({ ...p, time: e.target.value }))} />
                                                </div>
                                            </div>

                                            {newCalEvent.frequency === 'one-time' && (
                                                <div className="calendar-add-row">
                                                    <div className="calendar-input-group">
                                                        <label>Date</label>
                                                        <input type="date" className="form-input" value={newCalEvent.date} onChange={(e) => {
                                                            const dateStr = e.target.value;
                                                            let dayName = 'Monday';
                                                            if (dateStr) {
                                                                const date = new Date(dateStr + 'T00:00:00Z');
                                                                const dayIndex = date.getUTCDay();
                                                                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                                dayName = dayNames[dayIndex];
                                                            }
                                                            setNewCalEvent(p => ({ ...p, date: dateStr, day: dayName }));
                                                        }} />
                                                    </div>
                                                    {newCalEvent.date && (
                                                        <div className="calendar-input-group">
                                                            <label>Day of Week</label>
                                                            <input type="text" className="form-input" value={newCalEvent.day} disabled style={{ backgroundColor: 'rgba(255,107,75,0.1)', cursor: 'not-allowed' }} />
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {newCalEvent.frequency !== 'one-time' && newCalEvent.frequency !== 'daily' && (
                                                <div className="calendar-add-row">
                                                    <div className="calendar-input-group">
                                                        <label>Start Date</label>
                                                        <input 
                                                            type="date" 
                                                            className="form-input" 
                                                            value={newCalEvent.startDate} 
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    const date = new Date(e.target.value + 'T00:00:00Z');
                                                                    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                                    const dayOfWeek = days[date.getUTCDay()];
                                                                    setNewCalEvent(p => ({ ...p, startDate: e.target.value, day: dayOfWeek }));
                                                                } else {
                                                                    setNewCalEvent(p => ({ ...p, startDate: e.target.value }));
                                                                }
                                                            }} 
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {newCalEvent.frequency !== 'one-time' && newCalEvent.frequency !== 'daily' && newCalEvent.frequency !== 'monthly' && (
                                                <div className="calendar-add-row">
                                                    <div className="calendar-input-group" style={{ width: '100%' }}>
                                                        <label>Day of Week</label>
                                                        <div className="calendar-day-selector">
                                                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                                                                <button
                                                                    key={d}
                                                                    className={`day-btn ${newCalEvent.day === d ? 'active' : ''}`}
                                                                    onClick={() => setNewCalEvent(p => ({ ...p, day: d }))}
                                                                    style={{
                                                                        padding: '10px 12px',
                                                                        borderRadius: '8px',
                                                                        border: newCalEvent.day === d ? '2px solid var(--accent-primary)' : '1px solid rgba(255,107,75,0.3)',
                                                                        background: newCalEvent.day === d ? 'rgba(255,107,75,0.2)' : 'rgba(0,0,0,0.2)',
                                                                        color: 'var(--text-primary)',
                                                                        cursor: 'pointer',
                                                                        fontWeight: newCalEvent.day === d ? '600' : '500',
                                                                        fontSize: '12px',
                                                                        transition: 'all 0.2s',
                                                                        flex: 1,
                                                                        textAlign: 'center'
                                                                    }}
                                                                >
                                                                    {d.substring(0, 3)}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {newCalEvent.frequency === 'daily' && (
                                                <div className="calendar-add-row">
                                                    <div className="calendar-input-group">
                                                        <label>Start Date</label>
                                                        <input type="date" className="form-input" value={newCalEvent.startDate} onChange={(e) => setNewCalEvent(p => ({ ...p, startDate: e.target.value }))} />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="calendar-add-row" style={{ width: '100%' }}>
                                                <div className="calendar-input-group" style={{ width: '100%' }}>
                                                    <label>Discord Reminders</label>
                                                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                                        {[
                                                            { time: 5, label: '5 min' },
                                                            { time: 30, label: '30 min' },
                                                            { time: 60, label: '1 hour' },
                                                            { time: 240, label: '4 hours' }
                                                        ].map(({ time, label }) => (
                                                            <label key={time} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,107,75,0.3)', background: newCalEvent.reminders?.[time] ? 'rgba(255,107,75,0.15)' : 'rgba(0,0,0,0.2)', cursor: 'pointer', userSelect: 'none' }}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={newCalEvent.reminders?.[time] || false}
                                                                    onChange={(e) => setNewCalEvent(p => ({ 
                                                                        ...p, 
                                                                        reminders: { ...p.reminders, [time]: e.target.checked }
                                                                    }))}
                                                                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                                />
                                                                <span style={{ fontSize: '13px', fontWeight: newCalEvent.reminders?.[time] ? '600' : '500' }}>{label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Reminder Message and Guide Fields */}
                                            <div className="calendar-add-row" style={{ width: '100%' }}>
                                                <div className="calendar-input-group" style={{ width: '100%' }}>
                                                    <label>Reminder Instructions (Optional)</label>
                                                    <div style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
                                                        <strong>Markdown supported:</strong> **bold**, *italic*, `code`, [link](url)
                                                    </div>
                                                    <textarea 
                                                        className="form-input" 
                                                        placeholder="e.g., **Bring your best gear** and prepare for battle!" 
                                                        value={newCalEvent.reminderMessage || ''}
                                                        onChange={(e) => setNewCalEvent(p => ({ ...p, reminderMessage: e.target.value }))}
                                                        style={{ minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="calendar-add-row" style={{ width: '100%' }}>
                                                <div className="calendar-input-group" style={{ width: '100%' }}>
                                                    <label>Link a Guide (Optional)</label>
                                                    {guides && guides.length > 0 ? (
                                                        <>
                                                            <select 
                                                                className="form-select" 
                                                                value={newCalEvent.guideId ? String(newCalEvent.guideId) : ''}
                                                                onChange={(e) => {
                                                                    const guideIdStr = e.target.value;
                                                                    if (guideIdStr) {
                                                                        const selectedGuide = guides.find(g => String(g.id) === guideIdStr);
                                                                        console.log('[GUIDE_SELECT] Selected guide:', { id: guideIdStr, selectedGuide });
                                                                        if (selectedGuide) {
                                                                            console.log('[GUIDE_SELECT] Setting event guide to:', { guideId: selectedGuide.id, guideUrl: selectedGuide.guideUrl, guideText: selectedGuide.title });
                                                                            setNewCalEvent(p => ({ 
                                                                                ...p, 
                                                                                guideId: selectedGuide.id,
                                                                                guideText: selectedGuide.title,
                                                                                guideUrl: selectedGuide.guideUrl || ''
                                                                            }));
                                                                        }
                                                                    } else {
                                                                        setNewCalEvent(p => ({ 
                                                                            ...p, 
                                                                            guideId: null,
                                                                            guideText: '',
                                                                            guideUrl: ''
                                                                        }));
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">-- Select a guide --</option>
                                                                {guides.map(guide => (
                                                                    <option key={guide.id} value={String(guide.id)}>{guide.title}</option>
                                                                ))}
                                                            </select>
                                                            {newCalEvent.guideId && (
                                                                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: 'rgba(86,180,255,0.1)', borderRadius: '4px', fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid rgba(86,180,255,0.2)' }}>
                                                                    ✓ Guide linked: <strong>{newCalEvent.guideText}</strong>
                                                                    {newCalEvent.guideUrl && (
                                                                        <div style={{ marginTop: '4px', fontSize: '11px', wordBreak: 'break-all' }}>
                                                                            📖 <a href={newCalEvent.guideUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#87ceeb' }}>{newCalEvent.guideUrl}</a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div style={{ padding: '8px', backgroundColor: 'rgba(255,107,75,0.1)', borderRadius: '4px', fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid rgba(255,107,75,0.2)' }}>
                                                            No guides available. Create guides in the Guides tab first.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <button className="btn btn-primary calendar-add-btn" onClick={async () => {
                                                if (newCalEvent.name && newCalEvent.abbr && (newCalEvent.frequency === 'one-time' ? newCalEvent.date : true)) {
                                                    // Create event WITHOUT database ID - will be generated by backend
                                                    let eventToAdd = { ...newCalEvent };
                                                    
                                                    // Ensure day is calculated for one-time events
                                                    if (eventToAdd.frequency === 'one-time' && eventToAdd.date && !eventToAdd.day) {
                                                        const date = new Date(eventToAdd.date + 'T00:00:00Z');
                                                        const dayIndex = date.getUTCDay();
                                                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                        eventToAdd.day = dayNames[dayIndex];
                                                    }
                                                    
                                                    // Use a temporary ID for immediate display
                                                    let tempId = `temp-${Date.now()}`;
                                                    const displayEvent = { ...eventToAdd, id: tempId };
                                                    
                                                    const updatedEvents = [...calendarEvents, displayEvent];
                                                    setCalendarEvents(updatedEvents);
                                                    setNewCalEvent({ name: '', abbr: '', day: 'Monday', time: '00:00', frequency: 'weekly', color: '#ff5a3a', date: '', startDate: '', backgroundImage: '', eventTypeId: null, eventType: '', reminders: { 5: true, 30: true, 60: true, 240: true }, reminderMessage: '', reminderMessages: {}, guideId: null, guideText: '', guideUrl: '' });
                                                    showToast('success', 'Event Added', `${newCalEvent.name} added to calendar`);
                                                    
                                                    // Save to backend and update with real ID
                                                    const savedEvent = await saveEventToBackend(eventToAdd);
                                                    if (savedEvent) {
                                                        // Replace temp event with real database event, preserving all original fields
                                                        const finalEvents = await new Promise((resolve) => {
                                                            setCalendarEvents(prev => {
                                                                const updated = prev.map(ev => ev.id === tempId ? { 
                                                                    ...eventToAdd,  // Keep all original event data
                                                                    ...savedEvent,  // Override with backend response (has real ID and any db-generated fields)
                                                                    frequency: eventToAdd.frequency  // Ensure frequency is set
                                                                } : ev);
                                                                resolve(updated);
                                                                return updated;
                                                            });
                                                        });
                                                        
                                                        // Save to localStorage
                                                        try {
                                                            localStorage.setItem('hot_calendar_events', JSON.stringify(finalEvents));
                                                        } catch (e) {
                                                            console.error('Failed to save to localStorage:', e);
                                                        }
                                                    } else {
                                                        // Backend save failed - remove from state
                                                        setCalendarEvents(prev => prev.filter(ev => ev.id !== tempId));
                                                        showToast('error', 'Failed to Add', 'Event was not saved to backend. Try again.');
                                                        return;
                                                    }
                                                }
                                            }}>Add Event</button>
                                        </div>
                                        )}
                                    </div>

{/* Unified Event Templates Management (Admin only) - Replaces both Event Types and Six-Day Templates sections */}
{(userRole === 'admin' || userRole === 'super_admin') && (
    <div className="calendar-section" style={{ marginTop: '40px' }}>
        <div className="calendar-section-header" style={{ background: 'linear-gradient(135deg, rgba(255,107,75,0.1), rgba(58,95,255,0.1))', borderLeft: '4px solid #FF6B4B', cursor: 'pointer' }} onClick={() => setSixDayTemplateExpanded(!sixDayTemplateExpanded)}>
            <span style={{ fontSize: '20px' }}>🎨</span>
            <div style={{ flex: 1 }}>
                <div className="calendar-section-title">Event Templates</div>
                <div className="calendar-section-subtitle">Create templates for regular events or six-day events (GAR, KD, etc.) with custom messages</div>
            </div>
            <button 
                className="btn btn-secondary"
                style={{ padding: '8px 16px', fontSize: '14px' }}
                onClick={(e) => {
                    e.stopPropagation();
                    setSixDayTemplateExpanded(!sixDayTemplateExpanded);
                }}
            >
                {sixDayTemplateExpanded ? '▼ Collapse' : '▶ Expand'}
            </button>
        </div>
        
        <div style={{ padding: '24px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
            {/* Quick Add Six-Day Event Section (only show when six-day templates exist) */}
            {eventTemplates.length > 0 && (
                <div style={{ padding: '20px', background: 'var(--bg-primary)', borderRadius: '12px', border: '2px solid rgba(58,95,255,0.3)', marginBottom: '24px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>➕ Quick Add Six-Day Event</div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div className="calendar-input-group">
                            <label>Event Template</label>
                            <select 
                                className="form-select"
                                value={newCalEvent.eventType || ''}
                                onChange={(e) => {
                                    const templateType = e.target.value;
                                    if (!templateType) {
                                        setNewCalEvent(p => ({ 
                                            ...p, 
                                            name: '',
                                            abbr: '',
                                            eventType: '',
                                            frequency: 'weekly',
                                            reminderMessages: {}
                                        }));
                                        return;
                                    }
                                    
                                    const template = eventTemplates.find(t => t.template_type === templateType);
                                    if (template) {
                                        setNewCalEvent(p => ({ 
                                            ...p, 
                                            name: template.display_name,
                                            abbr: template.abbr,
                                            eventType: template.template_type,
                                            color: template.color,
                                            time: template.default_time,
                                            frequency: 'six-day',
                                            backgroundImage: template.background_image || '',
                                            reminderMessages: typeof template.day_messages === 'string' ? 
                                                JSON.parse(template.day_messages) : template.day_messages
                                        }));
                                    }
                                }}
                            >
                                <option value="">-- Select Template --</option>
                                {eventTemplates.map(template => (
                                    <option key={template.id} value={template.template_type}>
                                        {template.display_name} ({template.abbr})
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="calendar-input-group">
                            <label>Start Date (Monday)</label>
                            <input 
                                type="date" 
                                className="form-input"
                                value={newCalEvent.startDate}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        const date = new Date(e.target.value + 'T00:00:00Z');
                                        const dayOfWeek = date.getUTCDay();
                                        if (dayOfWeek !== 1) {
                                            showToast('warning', 'Invalid Date', 'Six-day events must start on Monday');
                                            return;
                                        }
                                        setNewCalEvent(p => ({ ...p, startDate: e.target.value, day: 'Monday' }));
                                    } else {
                                        setNewCalEvent(p => ({ ...p, startDate: '' }));
                                    }
                                }}
                                disabled={!newCalEvent.eventType}
                            />
                        </div>
                        
                        <div className="calendar-input-group">
                            <label>Event Time (UTC)</label>
                            <input 
                                type="time" 
                                className="form-input"
                                value={newCalEvent.time}
                                onChange={(e) => setNewCalEvent(p => ({ ...p, time: e.target.value }))}
                                disabled={!newCalEvent.eventType}
                            />
                        </div>
                    </div>
                    
                    {newCalEvent.eventType && newCalEvent.startDate && (
                        <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(58,95,255,0.05)', borderRadius: '8px', border: '1px solid rgba(58,95,255,0.2)' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>📅 Event Preview: {newCalEvent.name}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                                {[1, 2, 3, 4, 5, 6].map(dayNum => {
                                    const startDate = new Date(newCalEvent.startDate + 'T00:00:00Z');
                                    const currentDate = new Date(startDate);
                                    currentDate.setUTCDate(startDate.getUTCDate() + dayNum - 1);
                                    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][currentDate.getUTCDay()];
                                    const dateStr = `${currentDate.getUTCMonth() + 1}/${currentDate.getUTCDate()}`;
                                    const dayTheme = newCalEvent.reminderMessages?.[dayNum] || `Day ${dayNum}`;
                                    const isWarDay = dayNum === 6;
                                    
                                    return (
                                        <div 
                                            key={dayNum}
                                            style={{
                                                padding: '8px',
                                                background: isWarDay ? 'rgba(255,0,0,0.1)' : 'rgba(255,255,255,0.05)',
                                                border: isWarDay ? '2px solid rgba(255,0,0,0.5)' : '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                fontSize: '11px'
                                            }}
                                        >
                                            <div style={{ fontWeight: '700', color: newCalEvent.color }}>D{dayNum}</div>
                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{dayName} {dateStr}</div>
                                            <div style={{ fontSize: '9px', marginTop: '4px', color: 'var(--text-primary)' }}>{dayTheme}</div>
                                            {isWarDay && <div style={{ fontSize: '9px', color: '#ff6b6b', fontWeight: '700', marginTop: '4px' }}>⚔️ WAR</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    
                    <button 
                        className="btn btn-primary"
                        style={{ marginTop: '16px', width: '100%', padding: '12px', fontSize: '14px', fontWeight: '700' }}
                        disabled={!newCalEvent.eventType || !newCalEvent.startDate}
                        onClick={async () => {
                            if (newCalEvent.eventType && newCalEvent.startDate) {
                                let eventToAdd = { ...newCalEvent };
                                
                                let tempId = `temp-${Date.now()}`;
                                const displayEvent = { ...eventToAdd, id: tempId };
                                
                                const updatedEvents = [...calendarEvents, displayEvent];
                                setCalendarEvents(updatedEvents);
                                
                                const currentEventType = newCalEvent.eventType;
                                let currentTemplate = {};
                                
                                if (currentEventType) {
                                    const template = eventTemplates.find(t => t.template_type === currentEventType);
                                    if (template) {
                                        currentTemplate = {
                                            name: template.display_name,
                                            abbr: template.abbr,
                                            color: template.color,
                                            time: template.default_time,
                                            eventType: template.template_type,
                                            backgroundImage: template.background_image || '',
                                            reminderMessages: typeof template.day_messages === 'string' ? 
                                                JSON.parse(template.day_messages) : template.day_messages
                                        };
                                    }
                                }
                                
                                setNewCalEvent({ 
                                    name: '', 
                                    abbr: '', 
                                    day: 'Monday', 
                                    time: '00:00', 
                                    frequency: 'weekly', 
                                    color: '#ff5a3a', 
                                    date: '', 
                                    startDate: '', 
                                    backgroundImage: '', 
                                    eventTypeId: null, 
                                    eventType: currentEventType, 
                                    reminders: { 5: true, 30: true, 60: true, 240: true }, 
                                    reminderMessage: '', 
                                    reminderMessages: {}, 
                                    guideId: null, 
                                    guideText: '', 
                                    guideUrl: '',
                                    ...currentTemplate
                                });
                                showToast('success', 'Event Added', `${eventToAdd.name} added! Select another date to add more.`);
                                
                                const savedEvent = await saveEventToBackend(eventToAdd);
                                if (savedEvent) {
                                    const finalEvents = await new Promise((resolve) => {
                                        setCalendarEvents(prev => {
                                            const updated = prev.map(ev => ev.id === tempId ? { 
                                                ...eventToAdd,
                                                ...savedEvent,
                                                frequency: eventToAdd.frequency
                                            } : ev);
                                            resolve(updated);
                                            return updated;
                                        });
                                    });
                                    
                                    try {
                                        localStorage.setItem('hot_calendar_events', JSON.stringify(finalEvents));
                                    } catch (e) {
                                        console.error('Failed to save to localStorage:', e);
                                    }
                                } else {
                                    setCalendarEvents(prev => prev.filter(ev => ev.id !== tempId));
                                    showToast('error', 'Failed to Add', 'Event was not saved to backend. Try again.');
                                    return;
                                }
                            }
                        }}
                    >
                        ➕ Add {newCalEvent.name || 'Event'} to Calendar
                    </button>
                </div>
            )}
            
            {/* Template Management Section (Collapsible) */}
            {sixDayTemplateExpanded && (
                <>
                    {/* Template Creation Form */}
                    <div style={{ padding: '20px', background: 'var(--bg-primary)', borderRadius: '12px', border: '2px solid rgba(255,107,75,0.2)', marginBottom: '24px' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            {editingTemplateId ? '✏️ Edit Template' : '➕ Create New Template'}
                        </div>
                
                {/* Template Type Selector */}
                <div className="calendar-input-group" style={{ marginBottom: '16px' }}>
                    <label>Template Type</label>
                    <select 
                        className="form-select"
                        value={newTemplate.type}
                        onChange={(e) => setNewTemplate(p => ({ ...p, type: e.target.value }))}
                    >
                        <option value="six-day">Six-Day Event (GAR, KD, etc.)</option>
                        <option value="regular">Regular Event</option>
                    </select>
                </div>
                
                {/* Basic Information Row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div className="calendar-input-group">
                        <label>Template Name</label>
                        <input 
                            type="text" 
                            className="form-input" 
                            placeholder={newTemplate.type === 'six-day' ? "e.g., Guild Arms Race" : "e.g., Frostfield Battlefield"}
                            value={newTemplate.name} 
                            onChange={(e) => setNewTemplate(p => ({ ...p, name: e.target.value }))}
                        />
                    </div>
                    
                    <div className="calendar-input-group">
                        <label>Abbreviation</label>
                        <input 
                            type="text" 
                            className="form-input" 
                            placeholder={newTemplate.type === 'six-day' ? "e.g., GAR" : "e.g., FFB"}
                            maxLength="10"
                            value={newTemplate.abbr} 
                            onChange={(e) => setNewTemplate(p => ({ ...p, abbr: e.target.value }))}
                        />
                    </div>
                    
                    <div className="calendar-input-group">
                        <label>Color</label>
                        <input 
                            type="color" 
                            className="form-input color-input" 
                            value={newTemplate.color} 
                            onChange={(e) => setNewTemplate(p => ({ ...p, color: e.target.value }))}
                        />
                    </div>
                    
                    {newTemplate.type === 'six-day' && (
                        <div className="calendar-input-group">
                            <label>Default Time (UTC)</label>
                            <input 
                                type="time" 
                                className="form-input"
                                value={newTemplate.defaultTime}
                                onChange={(e) => setNewTemplate(p => ({ ...p, defaultTime: e.target.value }))}
                            />
                        </div>
                    )}
                </div>
                
                {/* Background Image Upload */}
                <div className="calendar-input-group" style={{ marginBottom: '16px' }}>
                    <label>Background Image (Optional)</label>
                    <input 
                        type="file" 
                        className="form-input" 
                        accept="image/*"
                        onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                                try {
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    const templateHash = newTemplate.name ? `${slugify(newTemplate.name)}-${Date.now()}` : `template-${Date.now()}`;
                                    const response = await fetch(`/api/upload-event-background?eventId=${templateHash}`, {
                                        method: 'POST',
                                        body: formData
                                    });
                                    
                                    if (!response.ok) {
                                        const error = await response.json();
                                        showToast('error', 'Upload Failed', error.error || 'Failed to upload background image');
                                        return;
                                    }
                                    
                                    const data = await response.json();
                                    setNewTemplate(p => ({ ...p, backgroundImage: data.url }));
                                    showToast('success', 'Image Uploaded', 'Background image uploaded successfully');
                                } catch (err) {
                                    console.error('Upload error:', err);
                                    showToast('error', 'Upload Error', err.message || 'Failed to upload image');
                                }
                            }
                        }}
                    />
                    {newTemplate.backgroundImage && (
                        <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', height: '60px' }}>
                            <img src={newTemplate.backgroundImage} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    )}
                </div>
                
                {/* Six-Day Event: Day Messages */}
                {newTemplate.type === 'six-day' && (
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>Day-Specific Messages</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                            {(() => {
                                const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                return [1, 2, 3, 4, 5, 6].map(dayNum => (
                                    <div key={dayNum} className="calendar-input-group">
                                        <label style={{ fontSize: '12px' }}>
                                            Day {dayNum} - {dayNames[dayNum-1]} {dayNum === 6 ? '(War Day)' : ''}
                                        </label>
                                        <textarea
                                            rows="3"
                                            className="form-input"
                                            placeholder={dayNum === 6 ? "e.g., War Day - Invasion" : `Day ${dayNum} activity`}
                                            value={newTemplate.dayMessages[dayNum] || ''}
                                            onChange={(e) => setNewTemplate(p => ({
                                                ...p,
                                                dayMessages: {
                                                    ...p.dayMessages,
                                                    [dayNum]: e.target.value
                                                }
                                            }))}
                                            style={{ fontSize: '12px', resize: 'vertical' }}
                                        />
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                )}
                
                {/* Description */}
                <div className="calendar-input-group" style={{ marginBottom: '16px' }}>
                    <label>Description (Optional)</label>
                    <textarea
                        className="form-input"
                        placeholder="Brief description of this template..."
                        rows="2"
                        value={newTemplate.description}
                        onChange={(e) => setNewTemplate(p => ({ ...p, description: e.target.value }))}
                    />
                </div>
                
                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        onClick={async () => {
                            if (!newTemplate.name || !newTemplate.abbr) {
                                showToast('warning', 'Missing Fields', 'Please provide template name and abbreviation');
                                return;
                            }
                            
                            if (newTemplate.type === 'six-day') {
                                // Save to database via API
                                try {
                                    const templateType = slugify(newTemplate.name).replace(/-/g, '_');
                                    const templateData = {
                                        template_type: templateType,
                                        display_name: newTemplate.name,
                                        abbr: newTemplate.abbr,
                                        default_time: newTemplate.defaultTime,
                                        color: newTemplate.color,
                                        background_image: newTemplate.backgroundImage || null,
                                        day_messages: newTemplate.dayMessages,
                                        description: newTemplate.description || ''
                                    };
                                    
                                    // Insert via direct INSERT (will need to add PUT endpoint for updates)
                                    const response = await fetch('/api/event-templates', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify(templateData)
                                    });
                                    
                                    if (!response.ok) {
                                        const error = await response.json();
                                        showToast('error', 'Failed', error.error || 'Failed to save template');
                                        return;
                                    }
                                    
                                    const result = await response.json();
                                    showToast('success', 'Template Saved', `${newTemplate.name} template created successfully`);
                                    
                                    // Reload templates
                                    const templatesResponse = await fetch('/api/event-templates');
                                    if (templatesResponse.ok) {
                                        const templatesData = await templatesResponse.json();
                                        setEventTemplates(templatesData.templates || []);
                                    }
                                    
                                    // Reset form
                                    setNewTemplate({
                                        type: 'six-day',
                                        name: '',
                                        abbr: '',
                                        color: '#ff5a3a',
                                        defaultTime: '14:00',
                                        backgroundImage: '',
                                        dayMessages: { "1": "", "2": "", "3": "", "4": "", "5": "", "6": "" },
                                        description: ''
                                    });
                                    setEditingTemplateId(null);
                                } catch (err) {
                                    console.error('Save template error:', err);
                                    showToast('error', 'Error', err.message || 'Failed to save template');
                                }
                            } else {
                                // Regular event type - save to eventTypes state (legacy)
                                const updatedTypes = editingEventTypeId 
                                    ? eventTypes.map(et => et.id === editingEventTypeId ? { 
                                        ...newTemplate, 
                                        id: editingEventTypeId,
                                        name: newTemplate.name,
                                        color: newTemplate.color,
                                        backgroundImage: newTemplate.backgroundImage
                                    } : et)
                                    : [...eventTypes, { 
                                        id: Date.now(),
                                        name: newTemplate.name,
                                        color: newTemplate.color,
                                        backgroundImage: newTemplate.backgroundImage
                                    }];
                                setEventTypes(updatedTypes);
                                showToast('success', 'Saved', `${newTemplate.name} saved`);
                                
                                // Reset form
                                setNewTemplate({
                                    type: 'six-day',
                                    name: '',
                                    abbr: '',
                                    color: '#ff5a3a',
                                    defaultTime: '14:00',
                                    backgroundImage: '',
                                    dayMessages: { "1": "", "2": "", "3": "", "4": "", "5": "", "6": "" },
                                    description: ''
                                });
                                setEditingTemplateId(null);
                            }
                        }}
                    >
                        {editingTemplateId ? '✏️ Update Template' : '➕ Create Template'}
                    </button>
                    
                    {editingTemplateId && (
                        <button 
                            className="btn btn-secondary"
                            onClick={() => {
                                setNewTemplate({
                                    type: 'six-day',
                                    name: '',
                                    abbr: '',
                                    color: '#ff5a3a',
                                    defaultTime: '14:00',
                                    backgroundImage: '',
                                    dayMessages: { "1": "", "2": "", "3": "", "4": "", "5": "", "6": "" },
                                    description: ''
                                });
                                setEditingTemplateId(null);
                            }}
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>
            
            {/* Existing Templates Display */}
            {(eventTemplates.length > 0 || eventTypes.length > 0) && (
                <div style={{ padding: '20px', background: 'rgba(0,0,0,0.2)', borderRadius: '12px', marginBottom: '24px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>📚 Existing Templates</div>
                    
                    {/* Six-Day Templates */}
                    {eventTemplates.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-muted)' }}>Six-Day Event Templates:</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                                {eventTemplates.map(template => (
                                    <div key={template.id} style={{ padding: '12px', background: 'var(--bg-primary)', borderRadius: '8px', border: `2px solid ${template.color}` }}>
                                        <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px', color: template.color }}>
                                            {template.display_name}
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                                            {template.abbr} • {template.default_time} UTC
                                        </div>
                                        {template.background_image && (
                                            <div style={{ height: '40px', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                                                <img src={template.background_image} alt={template.display_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                        {template.description && (
                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '8px', fontStyle: 'italic' }}>
                                                {template.description}
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button 
                                                className="btn btn-secondary"
                                                style={{ flex: 1, padding: '6px', fontSize: '12px' }}
                                                onClick={() => {
                                                    setNewTemplate({
                                                        type: 'six-day',
                                                        name: template.display_name,
                                                        abbr: template.abbr,
                                                        color: template.color,
                                                        defaultTime: template.default_time,
                                                        backgroundImage: template.background_image || '',
                                                        dayMessages: typeof template.day_messages === 'string' ? 
                                                            JSON.parse(template.day_messages) : template.day_messages,
                                                        description: template.description || ''
                                                    });
                                                    setEditingTemplateId(template.id);
                                                }}
                                            >
                                                ✏️
                                            </button>
                                            <button 
                                                className="btn btn-secondary"
                                                style={{ flex: 1, padding: '6px', fontSize: '12px' }}
                                                onClick={async () => {
                                                    if (confirm(`Delete template "${template.display_name}"?`)) {
                                                        try {
                                                            const response = await fetch(`/api/event-templates/${template.id}`, {
                                                                method: 'DELETE'
                                                            });
                                                            
                                                            if (!response.ok) {
                                                                showToast('error', 'Failed', 'Failed to delete template');
                                                                return;
                                                            }
                                                            
                                                            // Reload templates
                                                            const templatesResponse = await fetch('/api/event-templates');
                                                            if (templatesResponse.ok) {
                                                                const templatesData = await templatesResponse.json();
                                                                setEventTemplates(templatesData.templates || []);
                                                            }
                                                            
                                                            showToast('success', 'Deleted', `${template.display_name} template deleted`);
                                                        } catch (err) {
                                                            console.error('Delete error:', err);
                                                            showToast('error', 'Error', 'Failed to delete template');
                                                        }
                                                    }
                                                }}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Event Types */}
                    {eventTypes.length > 0 && (
                        <div>
                            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-muted)' }}>Regular Event Types:</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                                {eventTypes.map(et => (
                                    <div key={et.id} style={{ padding: '12px', background: 'var(--bg-primary)', borderRadius: '8px', border: `2px solid ${et.color}` }}>
                                        <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: et.color }}>{et.name}</div>
                                        {et.backgroundImage && (
                                            <div style={{ height: '40px', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                                                <img src={et.backgroundImage} alt={et.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button 
                                                className="btn btn-secondary"
                                                style={{ flex: 1, padding: '6px', fontSize: '12px' }}
                                                onClick={() => {
                                                    setNewTemplate({
                                                        type: 'regular',
                                                        name: et.name,
                                                        abbr: '',
                                                        color: et.color,
                                                        defaultTime: '14:00',
                                                        backgroundImage: et.backgroundImage || '',
                                                        dayMessages: { "1": "", "2": "", "3": "", "4": "", "5": "", "6": "" },
                                                        description: ''
                                                    });
                                                    setEditingEventTypeId(et.id);
                                                }}
                                            >
                                                ✏️
                                            </button>
                                            <button 
                                                className="btn btn-secondary"
                                                style={{ flex: 1, padding: '6px', fontSize: '12px' }}
                                                onClick={() => {
                                                    const updatedTypes = eventTypes.filter(e => e.id !== et.id);
                                                    setEventTypes(updatedTypes);
                                                }}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
                </>
            )}
        </div>
    </div>
)}


                                    {/* Edit Event Modal */}
                                    {editingEventId && editingEventData && (
                                        <div className="modal-overlay" onClick={() => setEditingEventId(null)}>
                                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                                <div className="modal-header">
                                                    <h2>Edit Event</h2>
                                                    <button className="modal-close" onClick={() => setEditingEventId(null)}>✕</button>
                                                </div>
                                                <div className="modal-body">
                                                    <div className="form-group">
                                                        <label>Event Type (Optional)</label>
                                                        <select 
                                                            className="form-select" 
                                                            value={editingEventData.eventTypeId || ''}
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    const selectedType = eventTypes.find(et => et.id === parseInt(e.target.value));
                                                                    if (selectedType) {
                                                                        setEditingEventData(p => ({ 
                                                                            ...p, 
                                                                            eventTypeId: selectedType.id,
                                                                            color: selectedType.color,
                                                                            backgroundImage: selectedType.backgroundImage
                                                                        }));
                                                                    }
                                                                } else {
                                                                    setEditingEventData(p => ({ 
                                                                        ...p, 
                                                                        eventTypeId: null
                                                                    }));
                                                                }
                                                            }}
                                                        >
                                                            <option value="">-- Select or Use Custom --</option>
                                                            {eventTypes.map(et => (
                                                                <option key={et.id} value={et.id}>{et.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Event Name</label>
                                                        <input 
                                                            type="text" 
                                                            className="form-input" 
                                                            value={editingEventData.name} 
                                                            onChange={(e) => setEditingEventData(p => ({ ...p, name: e.target.value }))} 
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Abbreviation</label>
                                                        <input 
                                                            type="text" 
                                                            className="form-input" 
                                                            maxLength={4}
                                                            value={editingEventData.abbr} 
                                                            onChange={(e) => setEditingEventData(p => ({ ...p, abbr: e.target.value.toUpperCase() }))} 
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Frequency</label>
                                                        <select 
                                                            className="form-select" 
                                                            value={editingEventData.frequency} 
                                                            onChange={(e) => {
                                                                const newFrequency = e.target.value;
                                                                const update = { frequency: newFrequency };
                                                                
                                                                // When changing to weekly or bi-weekly, set day to today's day if not already set
                                                                if ((newFrequency === 'weekly' || newFrequency === 'bi-weekly') && !editingEventData.day) {
                                                                    const today = new Date();
                                                                    const dayIndex = today.getUTCDay();
                                                                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                                    update.day = dayNames[dayIndex];
                                                                    // Also set startDate to today
                                                                    const year = today.getUTCFullYear();
                                                                    const month = String(today.getUTCMonth() + 1).padStart(2, '0');
                                                                    const dayStr = String(today.getUTCDate()).padStart(2, '0');
                                                                    update.startDate = `${year}-${month}-${dayStr}`;
                                                                }
                                                                
                                                                setEditingEventData(p => ({ ...p, ...update }));
                                                            }}
                                                        >
                                                            <option value="one-time">One-Time</option>
                                                            <option value="daily">Daily</option>
                                                            <option value="weekly">Weekly</option>
                                                            <option value="bi-weekly">Bi-Weekly</option>
                                                            <option value="monthly">Monthly</option>
                                                        </select>
                                                    </div>
                                                    {editingEventData.frequency === 'one-time' && (
                                                        <>
                                                            <div className="form-group">
                                                                <label>Date</label>
                                                                <input 
                                                                    type="date" 
                                                                    className="form-input" 
                                                                    value={editingEventData.date || ''} 
                                                                    onChange={(e) => {
                                                                        const dateStr = e.target.value;
                                                                        let dayName = 'Monday';
                                                                        if (dateStr) {
                                                                            const date = new Date(dateStr + 'T00:00:00Z');
                                                                            const dayIndex = date.getUTCDay();
                                                                            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                                            dayName = dayNames[dayIndex];
                                                                        }
                                                                        setEditingEventData(p => ({ ...p, date: dateStr, day: dayName }));
                                                                    }} 
                                                                />
                                                            </div>
                                                            {editingEventData.date && (
                                                                <div className="form-group">
                                                                    <label>Day of Week</label>
                                                                    <input 
                                                                        type="text" 
                                                                        className="form-input" 
                                                                        value={editingEventData.day || ''} 
                                                                        disabled 
                                                                        style={{ backgroundColor: 'rgba(255,107,75,0.1)', cursor: 'not-allowed' }} 
                                                                    />
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {editingEventData.frequency !== 'one-time' && editingEventData.frequency !== 'daily' && editingEventData.frequency !== 'monthly' && (
                                                        <div className="form-group">
                                                            <label>Day</label>
                                                            <select 
                                                                className="form-select" 
                                                                value={editingEventData.day || ''} 
                                                                onChange={(e) => setEditingEventData(p => ({ ...p, day: e.target.value }))}
                                                            >
                                                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => <option key={d} value={d}>{d}</option>)}
                                                            </select>
                                                        </div>
                                                    )}
                                                    {editingEventData.frequency !== 'one-time' && (
                                                        <div className="form-group">
                                                            <label>Start Date</label>
                                                            <input 
                                                                type="date" 
                                                                className="form-input" 
                                                                value={editingEventData.startDate || ''} 
                                                                onChange={(e) => setEditingEventData(p => ({ ...p, startDate: e.target.value }))} 
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="form-group">
                                                        <label>Time (UTC)</label>
                                                        <input 
                                                            type="time" 
                                                            className="form-input" 
                                                            value={editingEventData.time} 
                                                            onChange={(e) => setEditingEventData(p => ({ ...p, time: e.target.value }))} 
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Color</label>
                                                        <input 
                                                            type="color" 
                                                            className="color-input" 
                                                            value={editingEventData.color} 
                                                            onChange={(e) => setEditingEventData(p => ({ ...p, color: e.target.value }))} 
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Background Image (Optional)</label>
                                                        <input 
                                                            type="file" 
                                                            className="form-input" 
                                                            accept="image/*"
                                                            onChange={async (e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) {
                                                                    try {
                                                                        // Upload file to backend
                                                                        const formData = new FormData();
                                                                        formData.append('file', file);
                                                                        
// Use actual event ID or abbr for consistent file path
                                                        const eventId = editingEventData.id || editingEventData.abbr?.toLowerCase() || 'temp';
                                                        const response = await fetch(`/api/upload-event-background?eventId=${eventId}`, {
                                                                            method: 'POST',
                                                                            body: formData
                                                                        });
                                                                        
                                                                        if (!response.ok) {
                                                                            const error = await response.json();
                                                                            showToast('error', 'Upload Failed', error.error || 'Failed to upload background image');
                                                                            return;
                                                                        }
                                                                        
                                                                        const data = await response.json();
                                                                        setEditingEventData(p => ({ ...p, backgroundImage: data.url }));
                                                                        showToast('success', 'Image Uploaded', 'Background image uploaded successfully');
                                                                    } catch (err) {
                                                                        console.error('Upload error:', err);
                                                                        showToast('error', 'Upload Error', err.message || 'Failed to upload image');
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        {editingEventData.backgroundImage && (
                                                            <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', height: '80px' }}>
                                                                <img src={editingEventData.backgroundImage} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => setEditingEventData(p => ({ ...p, backgroundImage: '' }))}
                                                                    style={{ marginTop: '8px', padding: '8px 16px', background: 'rgba(255,107,75,0.2)', border: '1px solid rgba(255,107,75,0.3)', borderRadius: '6px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}
                                                                >
                                                                    Remove Image
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Reminder Instructions (Optional)</label>
                                                        <textarea 
                                                            className="form-input" 
                                                            placeholder="e.g., Bring your best gear and prepare for battle!" 
                                                            value={editingEventData.reminderMessage || ''}
                                                            onChange={(e) => setEditingEventData(p => ({ ...p, reminderMessage: e.target.value }))}
                                                            style={{ minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Link a Guide (Optional)</label>
                                                        {guides && guides.length > 0 ? (
                                                            <>
                                                                <select 
                                                                    className="form-select" 
                                                                    value={editingEventData.guideId ? String(editingEventData.guideId) : ''}
                                                                    onChange={(e) => {
                                                                        const guideIdStr = e.target.value;
                                                                        if (guideIdStr) {
                                                                            const selectedGuide = guides.find(g => String(g.id) === guideIdStr);
                                                                            if (selectedGuide) {
                                                                                setEditingEventData(p => ({ 
                                                                                    ...p, 
                                                                                    guideId: selectedGuide.id,
                                                                                    guideText: selectedGuide.title,
                                                                                    guideUrl: selectedGuide.guideUrl || ''
                                                                                }));
                                                                            }
                                                                        } else {
                                                                            setEditingEventData(p => ({ 
                                                                                ...p, 
                                                                                guideId: null,
                                                                                guideText: '',
                                                                                guideUrl: ''
                                                                            }));
                                                                        }
                                                                    }}
                                                                >
                                                                    <option value="">-- Select a guide --</option>
                                                                    {guides.map(guide => (
                                                                        <option key={guide.id} value={String(guide.id)}>{guide.title}</option>
                                                                    ))}
                                                                </select>
                                                                {editingEventData.guideId && (
                                                                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: 'rgba(86,180,255,0.1)', borderRadius: '4px', fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid rgba(86,180,255,0.2)' }}>
                                                                        ✓ Guide linked: <strong>{editingEventData.guideText}</strong>
                                                                        {editingEventData.guideUrl && (
                                                                            <div style={{ marginTop: '4px', fontSize: '11px', wordBreak: 'break-all' }}>
                                                                                📖 <a href={editingEventData.guideUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#87ceeb' }}>{editingEventData.guideUrl}</a>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div style={{ padding: '8px', backgroundColor: 'rgba(255,107,75,0.1)', borderRadius: '4px', fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid rgba(255,107,75,0.2)' }}>
                                                                No guides available. Create guides in the Guides tab first.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="modal-footer">
                                                    <button 
                                                        className="btn btn-secondary" 
                                                        onClick={() => setEditingEventId(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        className="btn btn-primary" 
                                                        onClick={async () => {
                                                            let updatedEvents = [];
                                                            setCalendarEvents(prev => {
                                                                updatedEvents = prev.map(ev => ev.id === editingEventId ? editingEventData : ev);
                                                                return updatedEvents;
                                                            });
                                                            setEditingEventId(null);
                                                            showToast('success', 'Event Updated', `${editingEventData.name} has been updated`);
                                                            
                                                            // Save to backend PostgreSQL
                                                            const saveResult = await saveEventToBackend(editingEventData);
                                                            
                                                            if (saveResult) {
                                                                // Save to localStorage only if backend succeeded
                                                                try {
                                                                    localStorage.setItem('hot_calendar_events', JSON.stringify(updatedEvents));
                                                                } catch (e) {
                                                                    console.error('Failed to save to localStorage:', e);
                                                                }
                                                            } else {
                                                                // Revert from state and show error if backend failed
                                                                setCalendarEvents(prev => prev.map(ev => ev.id === editingEventId ? ev : editingEventData));
                                                                setEditingEventId(null);
                                                                showToast('error', 'Failed to Update', 'Event changes were not saved to backend. Try again.');
                                                                return;
                                                            }
                                                        }}
                                                    >
                                                        Save Changes
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Edit Rotating Event Modal */}
                                    {editingRotatingEvent && (
                                        <div className="modal-overlay" style={{ pointerEvents: 'auto' }}>
                                            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ pointerEvents: 'auto' }}>
                                                <div className="modal-header">
                                                    <h2>Edit {editingRotatingEvent.type} Time</h2>
                                                    <button className="modal-close" onClick={() => setEditingRotatingEvent(null)}>✕</button>
                                                </div>
                                                <div className="modal-body">
                                                    <div className="form-group">
                                                        <label>Event Type</label>
                                                        <input 
                                                            type="text" 
                                                            className="form-input" 
                                                            value={editingRotatingEvent.type} 
                                                            disabled
                                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Event Date</label>
                                                        <input 
                                                            type="date" 
                                                            className="form-input" 
                                                            value={editingRotatingEvent.date.toISOString().split('T')[0]}
                                                            disabled
                                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Event Time (UTC)</label>
                                                        <select 
                                                            className="form-select" 
                                                            value={editingRotatingEvent.date.getUTCHours() + ':00'}
                                                            onChange={(e) => {
                                                                const hour = parseInt(e.target.value);
                                                                const newDate = new Date(editingRotatingEvent.date);
                                                                newDate.setUTCHours(hour, 0, 0, 0);
                                                                setEditingRotatingEvent(prev => ({ ...prev, date: newDate }));
                                                            }}
                                                        >
                                                            <option value="14:00">14:00 UTC</option>
                                                            <option value="19:00">19:00 UTC</option>
                                                        </select>
                                                    </div>
                                                    
                                                    {/* Reminder Times Editor - Checkboxes */}
                                                    <div className="form-group">
                                                        <label>Reminder Times Before Event ({editingRotatingEvent?.type})</label>
                                                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                                                            {[
                                                                { minutes: 240, label: '4h' },
                                                                { minutes: 60, label: '1h' },
                                                                { minutes: 30, label: '30m' },
                                                                { minutes: 5, label: '5m' }
                                                            ].map((item) => {
                                                                const eventType = editingRotatingEvent?.type;
                                                                const typeReminders = eventType === 'GB' 
                                                                    ? (Array.isArray(calendarConfig.gb_reminder_times_minutes) ? calendarConfig.gb_reminder_times_minutes : [])
                                                                    : (Array.isArray(calendarConfig.di_reminder_times_minutes) ? calendarConfig.di_reminder_times_minutes : []);
                                                                const isChecked = typeReminders.includes(item.minutes);
                                                                return (
                                                                    <label key={item.minutes} style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                                                        <input 
                                                                            type="checkbox" 
                                                                            checked={isChecked}
                                                                            onChange={(e) => {
                                                                                const eventType = editingRotatingEvent?.type;
                                                                                const isGB = eventType === 'GB';
                                                                                const currentField = isGB ? 'gb_reminder_times_minutes' : 'di_reminder_times_minutes';
                                                                                const current = isGB 
                                                                                    ? (Array.isArray(calendarConfig.gb_reminder_times_minutes) ? calendarConfig.gb_reminder_times_minutes : [])
                                                                                    : (Array.isArray(calendarConfig.di_reminder_times_minutes) ? calendarConfig.di_reminder_times_minutes : []);
                                                                                let newMinutes;
                                                                                if (e.target.checked) {
                                                                                    newMinutes = [...current, item.minutes].sort((a, b) => b - a);
                                                                                } else {
                                                                                    newMinutes = current.filter(m => m !== item.minutes);
                                                                                }
                                                                                // Update only this event type's reminder times
                                                                                setCalendarConfig(prev => ({
                                                                                    ...prev,
                                                                                    [currentField]: newMinutes
                                                                                }));
                                                                            }}
                                                                            style={{width: '16px', height: '16px', cursor: 'pointer'}}
                                                                        />
                                                                        <span style={{fontSize: '12px', color: 'var(--text-primary)'}}>{item.label}</span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="form-group">
                                                        <label>Background Image (Optional)</label>
                                                        <input 
                                                            type="file" 
                                                            className="form-input" 
                                                            accept="image/*"
                                                            onChange={async (e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) {
                                                                    try {
                                                                        // Upload file to backend
                                                                        const formData = new FormData();
                                                                        formData.append('file', file);
                                                                        
// Use event type hash for consistent file path
                                                        const eventTypeId = editingRotatingEvent.type ? slugify(editingRotatingEvent.type) : 'rotating';
                                                        const response = await fetch(`/api/upload-event-background?eventId=${eventTypeId}`, {
                                                                            method: 'POST',
                                                                            body: formData
                                                                        });
                                                                        
                                                                        if (!response.ok) {
                                                                            const error = await response.json();
                                                                            showToast('error', 'Upload Failed', error.error || 'Failed to upload background image');
                                                                            return;
                                                                        }
                                                                        
                                                                        const data = await response.json();
                                                                        setEditingRotatingEvent(p => ({ ...p, backgroundImage: data.url }));
                                                                        showToast('success', 'Image Uploaded', 'Background image uploaded successfully');
                                                                    } catch (err) {
                                                                        console.error('Upload error:', err);
                                                                        showToast('error', 'Upload Error', err.message || 'Failed to upload image');
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        {editingRotatingEvent.backgroundImage && (
                                                            <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', height: '80px' }}>
                                                                <img src={editingRotatingEvent.backgroundImage} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => setEditingRotatingEvent(p => ({ ...p, backgroundImage: '' }))}
                                                                    style={{ marginTop: '8px', padding: '8px 16px', background: 'rgba(255,107,75,0.2)', border: '1px solid rgba(255,107,75,0.3)', borderRadius: '6px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}
                                                                >
                                                                    Remove Image
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Reminder Instructions with Format Menu */}
                                                    <div className="form-group">
                                                        <label>Reminder Instructions (Optional)</label>
                                                        <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'nowrap', overflow: 'auto' }}>
                                                            <button type="button" onClick={() => {
                                                                const ta = document.querySelector('[data-reminder-textarea]');
                                                                const start = ta.selectionStart, end = ta.selectionEnd;
                                                                const selected = ta.value.substring(start, end);
                                                                const before = ta.value.substring(0, start);
                                                                const after = ta.value.substring(end);
                                                                const newText = before + '**' + (selected || 'bold') + '**' + after;
                                                                setEditingRotatingEvent(p => ({ ...p, reminderMessage: newText }));
                                                                setTimeout(() => ta.focus(), 0);
                                                            }} style={{ padding: '4px 8px', fontSize: '11px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: '600' }}>**Bold**</button>
                                                            <button type="button" onClick={() => {
                                                                const ta = document.querySelector('[data-reminder-textarea]');
                                                                const start = ta.selectionStart, end = ta.selectionEnd;
                                                                const selected = ta.value.substring(start, end);
                                                                const before = ta.value.substring(0, start);
                                                                const after = ta.value.substring(end);
                                                                const newText = before + '*' + (selected || 'italic') + '*' + after;
                                                                setEditingRotatingEvent(p => ({ ...p, reminderMessage: newText }));
                                                                setTimeout(() => ta.focus(), 0);
                                                            }} style={{ padding: '4px 8px', fontSize: '11px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontStyle: 'italic' }}>*Italic*</button>
                                                            <button type="button" onClick={() => {
                                                                const ta = document.querySelector('[data-reminder-textarea]');
                                                                const start = ta.selectionStart, end = ta.selectionEnd;
                                                                const selected = ta.value.substring(start, end);
                                                                const before = ta.value.substring(0, start);
                                                                const after = ta.value.substring(end);
                                                                const newText = before + '`' + (selected || 'code') + '`' + after;
                                                                setEditingRotatingEvent(p => ({ ...p, reminderMessage: newText }));
                                                                setTimeout(() => ta.focus(), 0);
                                                            }} style={{ padding: '4px 8px', fontSize: '11px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'monospace' }}>`Code`</button>
                                                        </div>
                                                        <textarea 
                                                            data-reminder-textarea
                                                            className="form-input" 
                                                            placeholder="e.g., **Bring your best gear!**"
                                                            value={editingRotatingEvent.reminderMessage || ''}
                                                            onChange={(e) => setEditingRotatingEvent(p => ({ ...p, reminderMessage: e.target.value }))}
                                                            style={{ minHeight: '80px', fontFamily: 'Arial, sans-serif', marginBottom: '8px' }}
                                                        />
                                                        {editingRotatingEvent.reminderMessage && (() => {
                                                            let previewHtml = String(editingRotatingEvent.reminderMessage || '');
                                                            try {
                                                                const codeStyle = 'background: rgba(255,255,255,0.1); padding: 2px 4px; border-radius: 2px; font-family: monospace;';
                                                                // Escape HTML first
                                                                previewHtml = previewHtml.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                                                                // Then apply markdown formatting
                                                                previewHtml = previewHtml
                                                                    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
                                                                    .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
                                                                    .replace(/`([^`]+?)`/g, '<code style="' + codeStyle + '">$1</code>');
                                                            } catch (e) {
                                                                console.error('Error rendering preview:', e);
                                                            }
                                                            return (
                                                                <div style={{ padding: '8px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                                                    <strong>Preview:</strong>
                                                                    <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>

                                                    {/* Guide Selection */}
                                                    <div className="form-group">
                                                        <label>Link a Guide (Optional)</label>
                                                        {guides.length > 0 ? (
                                                            <>
                                                                <select 
                                                                    className="form-select" 
                                                                    value={editingRotatingEvent.guideId || ''}
                                                                    onChange={(e) => {
                                                                        const guideIdStr = e.target.value;
                                                                        if (guideIdStr) {
                                                                            const selectedGuide = guides.find(g => String(g.id) === guideIdStr);
                                                                            if (selectedGuide) {
                                                                                setEditingRotatingEvent(p => ({ ...p, guideId: String(selectedGuide.id), guideUrl: selectedGuide.url || selectedGuide.guideUrl || '', guideText: selectedGuide.title }));
                                                                            }
                                                                        } else {
                                                                            setEditingRotatingEvent(p => ({ ...p, guideId: '', guideUrl: '', guideText: '' }));
                                                                        }
                                                                    }}
                                                                >
                                                                    <option value="">-- Select a guide --</option>
                                                                    {guides.map(guide => (
                                                                        <option key={guide.id} value={String(guide.id)}>{guide.title}</option>
                                                                    ))}
                                                                </select>
                                                                {editingRotatingEvent.guideId && (
                                                                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: 'rgba(86,180,255,0.1)', borderRadius: '4px', fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid rgba(86,180,255,0.2)' }}>
                                                                        ✓ Guide linked: <strong>{editingRotatingEvent.guideText}</strong>
                                                                        {editingRotatingEvent.guideUrl && (
                                                                            <div style={{ marginTop: '4px', fontSize: '11px' }}>
                                                                                Link: <a href={editingRotatingEvent.guideUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#87ceeb', wordBreak: 'break-all' }}>{editingRotatingEvent.guideUrl}</a>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div style={{ padding: '8px', backgroundColor: 'rgba(255,107,75,0.1)', borderRadius: '4px', fontSize: '12px', color: 'var(--text-secondary)', border: '1px solid rgba(255,107,75,0.2)' }}>
                                                                No guides available. Create guides in the Guides tab first.
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="form-group" style={{ marginTop: '20px', padding: '12px', background: 'rgba(255,215,0,0.1)', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.3)' }}>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                            <strong>Note:</strong> This is a pre-defined rotating event. You can only modify the time slot, background, reminders, and guides. The event type and date cannot be changed.
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="modal-footer">
                                                    <button 
                                                        className="btn btn-secondary" 
                                                        onClick={() => setEditingRotatingEvent(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        className="btn btn-primary" 
                                                        onClick={async () => {
                                                            console.log('[SAVE_CLICK]editingRotatingEvent:', editingRotatingEvent);
                                                            console.log('[SAVE_CLICK] reminderMessage value:', editingRotatingEvent.reminderMessage);
                                                            console.log('[SAVE_CLICK] reminderMessage type:', typeof editingRotatingEvent.reminderMessage);
                                                            const typeKey = editingRotatingEvent.type;
                                                            console.log('Saving rotating event:', { typeKey, guideId: editingRotatingEvent.guideId, guideUrl: editingRotatingEvent.guideUrl, guideText: editingRotatingEvent.guideText, reminderMessage: editingRotatingEvent.reminderMessage });
                                                            // Only update the specific rotating event type, let the database merge with existing data
                                                            const updatedConfig = {
                                                                ...calendarConfig,
                                                                event_time_overrides: {
                                                                    ...calendarConfig.event_time_overrides,
                                                                    [typeKey]: editingRotatingEvent.date.getUTCHours()
                                                                },
                                                                rotating_event_backgrounds: {
                                                                    ...calendarConfig.rotating_event_backgrounds,
                                                                    [typeKey]: editingRotatingEvent.backgroundImage || null
                                                                },
                                                                rotating_event_guides: {
                                                                    ...calendarConfig.rotating_event_guides,
                                                                    [typeKey]: editingRotatingEvent.guideId ? { id: parseInt(editingRotatingEvent.guideId), title: editingRotatingEvent.guideText, url: editingRotatingEvent.guideUrl } : null
                                                                },
                                                                rotating_event_reminder_messages: {
                                                                    ...calendarConfig.rotating_event_reminder_messages,
                                                                    [typeKey]: editingRotatingEvent.reminderMessage || null
                                                                }
                                                            };
                                                            console.log('Updated config guides before save:', updatedConfig.rotating_event_guides);
                                                            setCalendarConfig(updatedConfig);
                                                            
                                                            // Save to backend (GB uses id=1, DI uses id=2)
                                                            try {
                                                                console.log('[SAVE_EVENT] About to save rotating event config via backend');
                                                                
                                                                const eventType = editingRotatingEvent.type; // 'GB' or 'DI'
                                                                const configId = eventType === 'DI' ? '2' : '1';
                                                                
                                                                // Build saveData with type-specific data only
                                                                // JSONB fields should only contain data for THIS event type, not merged
                                                                const saveData = {
                                                                    id: configId,
                                                                    time_slots: updatedConfig.time_slots,
                                                                    reminder_times_minutes: updatedConfig.reminder_times_minutes,
                                                                    // Only include overrides for this type
                                                                    event_time_overrides: {
                                                                        [eventType]: updatedConfig.event_time_overrides?.[eventType]
                                                                    },
                                                                    // Only include backgrounds for this type
                                                                    rotating_event_backgrounds: {
                                                                        [eventType]: updatedConfig.rotating_event_backgrounds?.[eventType]
                                                                    },
                                                                    // Only include guides for this type
                                                                    rotating_event_guides: {
                                                                        [eventType]: updatedConfig.rotating_event_guides?.[eventType]
                                                                    },
                                                                    // Include reminder instructions
                                                                    rotating_event_reminder_messages: {
                                                                        [eventType]: editingRotatingEvent.reminderMessage || null
                                                                    }
                                                                };
                                                                console.log('[SAVE_EVENT] Sending type-specific data:', JSON.stringify(saveData, null, 2));
                                                                
                                                                const response = await fetch(API_BASE, {
                                                                    method: 'POST',
                                                                    headers: { 'Content-Type': 'application/json' },
                                                                    body: JSON.stringify({ action: 'upsert', table: 'calendar_config', data: saveData })
                                                                });
                                                                
                                                                const result = await response.json();
                                                                console.log('[SAVE_EVENT] API response, success:', response.ok, 'result:', result);
                                                                if (!response.ok || result.error) {
                                                                  const errMsg = result.error || 'Unknown error';
                                                                  console.error('[SAVE_EVENT] Failed to save to backend:', errMsg);
                                                                  showToast('error', 'Save Failed', errMsg);
                                                                } else {
                                                                  console.log('[SAVE_EVENT] Event saved successfully!');
                                                                  showToast('success', 'Time Updated', `${editingRotatingEvent.type} time has been changed to ${editingRotatingEvent.date.getUTCHours()}:00 UTC`);
                                                                }
                                                            } catch (e) { 
                                                              console.error('[SAVE_EVENT] Exception during save:', e);
                                                              showToast('error', 'Error', e.message);
                                                            }
                                                            
                                                            setEditingRotatingEvent(null);
                                                        }}
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Member Management Tab */}
                        {activeTab === 'member-mgmt' && (<div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                            
                            {/* Search Box - Separated at Top */}
                            <div className="search-container" style={{padding: '16px', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-accent)', borderRadius: 'var(--radius)', boxShadow: 'var(--hot-glow), inset 0 1px 2px rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)'}}>
                                <div className="search-input-wrapper"><span className="search-icon">🔍</span>
                                    <input className="search-input" type="text" placeholder="Search members..." value={searchQueryManage} onChange={(e) => { setSearchQueryManage(e.target.value); setShowAutocomplete(e.target.value.length > 0); }} onFocus={() => searchQueryManage && setShowAutocomplete(true)} onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)} />
                                </div>
                                {showAutocomplete && searchQueryManage && (<div className="autocomplete-dropdown">
                                    {getFilteredMembersForManage().slice(0, 10).map(m => (<div key={m.id} className="autocomplete-item" onClick={() => { handleSelectMember(m); setSearchQueryManage(''); setShowAutocomplete(false); setShowMembersList(false); }}><span className="autocomplete-item-name">{m.display_name || m.name}</span>{m.is_active === false && (<span className="autocomplete-item-status">inactive</span>)}</div>))}
                                    {getFilteredMembersForManage().length === 0 && (<div className="autocomplete-item" style={{cursor: 'default', color: 'var(--text-muted)'}}>No members found</div>)}
                                </div>)}
                            </div>

                            {/* Members List View with Collapsible Grid */}
                            <div className="card">
                                <div className="card-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap',flexDirection: 'column'}}>
                                        <div className="card-title" style={{fontSize: '16px'}}><span className="card-icon">📋</span>Members List</div>
                                        <span style={{fontSize: '12px', color: 'var(--text-secondary)'}}>{members.length} members</span>
                                    </div>
                                    <button 
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setShowMembersList(!showMembersList)}
                                        style={{fontSize: '12px', padding: '6px 12px'}}
                                    >
                                        {showMembersList ? '▼ Hide' : '▶ Show'} Members
                                    </button>
                                </div>

                                {/* Members Grid Display - Toggle Visibility */}
                                {showMembersList && (
                                    <>
                                        {/* Filter Buttons with Count */}
                                        <div style={{display: 'flex', gap: '7px', padding: '16px', paddingBottom: '0', flexWrap: 'nowrap', alignContent: 'flex-start', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '10px' }}>
                                            {['all', 'active', 'alts', 'inactive'].map(filter => {
                                                let count = 0;
                                                if (filter === 'all') count = members.length;
                                                else if (filter === 'alts') count = members.filter(m => m.is_alt).length;
                                                else if (filter === 'active') count = members.filter(m => m.is_active !== false).length;
                                                else if (filter === 'inactive') count = members.filter(m => m.is_active === false).length;
                                                
                                                return (
                                                    <button 
                                                        key={filter}
                                                        onClick={() => setMemberFilter(filter)}
                                                        style={{
                                                            padding: '8px 14px',
                                                            backgroundColor: memberFilter === filter ? 'var(--accent-primary)' : 'rgba(255,255,255,0.05)',
                                                            border: memberFilter === filter ? '1px solid var(--accent-primary)' : '1px solid rgba(255,255,255,0.1)',
                                                            borderRadius: '6px',
                                                            color: 'var(--text-primary)',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            transition: 'all 0.2s',
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {filter === 'all' ? 'All' : filter === 'alts' ? 'Alts' : filter === 'active' ? 'Active' : 'Inactive'} <span style={{fontSize: '10px', opacity: 0.7}}>({count})</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {/* Members Grid - Responsive Layout */}
                                        <div style={windowWidth <= 768 ? {display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 105px)', gap: '4px', padding: '0px', maxWidth: '100%', width: '100%', justifyContent: 'center'} : {display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 115px)', gap: '12px', padding: '12px', maxWidth: '100%', width: '100%'}}>
                                            {getFilteredMembersByStatus().sort((a, b) => {
                                                const rankOrder = { 'R5': 5, 'R4': 4, 'R3': 3, 'R2': 2, 'R1': 1 };
                                                const rankA = rankOrder[a.rank] || 0;
                                                const rankB = rankOrder[b.rank] || 0;
                                                return rankB - rankA;
                                            }).map(m => {
                                                const normalizedStatus = m.is_active === false ? 'inactive' : 'active';
                                                const isSelected = selectedMember?.id === m.id;
                                                const isInactive = m.is_active === false;
                                                return (
                                                    <div 
                                                        key={m.id} 
                                                        onClick={() => { handleSelectMember(m); setSearchQueryManage(''); setShowMembersList(false); }}
                                                        style={windowWidth <= 768 ? {
                                                            width: '100px',
                                                            height: '100px',
                                                            backgroundColor: isSelected ? 'var(--accent-primary)' : 'rgba(255,255,255,0.04)',
                                                            border: isSelected ? '2px solid var(--accent-primary)' : '1px solid rgba(255,255,255,0.08)',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '5px',
                                                            textAlign: 'center',
                                                            WebkitTouchCallout: 'none',
                                                            userSelect: 'none',
                                                            position: 'relative'
                                                        } : {
                                                            width: '115px',
                                                            height: '130px',
                                                            backgroundColor: isSelected ? 'var(--accent-primary)' : 'rgba(255,255,255,0.04)',
                                                            border: isSelected ? '2px solid var(--accent-primary)' : '1px solid rgba(255,255,255,0.08)',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '6px',
                                                            textAlign: 'center',
                                                            WebkitTouchCallout: 'none',
                                                            userSelect: 'none',
                                                            padding: '8px',
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        {m.rank && windowWidth > 768 && (
                                                            <div style={{position: 'absolute', top: '2px', right: '2px', fontSize: '12px', fontWeight: '900', color: m.rank === 'R1' ? '#64b5f6' : m.rank === 'R2' ? '#81c784' : m.rank === 'R3' ? '#ffd54f' : m.rank === 'R4' ? '#ff9800' : '#ff5252', backgroundColor: m.rank === 'R1' ? 'rgba(100, 181, 246, 0.15)' : m.rank === 'R2' ? 'rgba(129, 199, 132, 0.15)' : m.rank === 'R3' ? 'rgba(255, 213, 79, 0.15)' : m.rank === 'R4' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 82, 82, 0.2)', padding: '3px 6px', borderRadius: '4px', textShadow: `0 0 10px ${m.rank === 'R1' ? '#64b5f6' : m.rank === 'R2' ? '#81c784' : m.rank === 'R3' ? '#ffd54f' : m.rank === 'R4' ? '#ff9800' : '#ff5252'}`, boxShadow: `0 0 15px ${m.rank === 'R1' ? 'rgba(100, 181, 246, 0.5)' : m.rank === 'R2' ? 'rgba(129, 199, 132, 0.5)' : m.rank === 'R3' ? 'rgba(255, 213, 79, 0.6)' : m.rank === 'R4' ? 'rgba(255, 152, 0, 0.6)' : 'rgba(255, 82, 82, 0.7)'}, inset 0 0 8px ${m.rank === 'R1' ? 'rgba(100, 181, 246, 0.3)' : m.rank === 'R2' ? 'rgba(129, 199, 132, 0.3)' : m.rank === 'R3' ? 'rgba(255, 213, 79, 0.4)' : m.rank === 'R4' ? 'rgba(255, 152, 0, 0.4)' : 'rgba(255, 82, 82, 0.5)'}`}}>
                                                                {m.rank}
                                                            </div>
                                                        )}
                                                        <div style={windowWidth <= 768 ? {width: '48px', height: '48px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px', fontWeight: '700', flexShrink: 0} : {width: '60px', height: '60px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: '700', flexShrink: 0}}>
                                                            {(m.display_name || m.name).charAt(0).toUpperCase()}
                                                        </div>
                                        <div style={windowWidth <= 768 ? {fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', lineHeight: '1', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%'} : {fontSize: '11px', fontWeight: '600', color: 'var(--text-primary)', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', wordBreak: 'break-word'}}>
                                                            {(m.display_name || m.name).substring(0, 12)}
                                                        </div>
                                                        {windowWidth <= 768 && (
                                                            <div style={{position: 'absolute', top: '6px', right: '6px', display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center'}}>
                                                                <div style={{width: '10px', height: '10px', borderRadius: '50%', backgroundColor: isInactive ? '#888888' : '#2ecc71', boxShadow: isInactive ? 'none' : '0 0 10px #2ecc71', flexShrink: 0}}></div>
                                                                {m.is_alt && (
                                                                    <div style={{width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#d946ef', boxShadow: '0 0 10px #d946ef', flexShrink: 0}}></div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {!isInactive && windowWidth > 768 && (
                                                            <div style={{fontSize: '9px', fontWeight: '500', color: m.is_alt ? 'var(--accent-secondary)' : '#2ecc71', padding: '3px 6px', borderRadius: '3px', backgroundColor: 'rgba(255,255,255,0.1)'}}>
                                                            {m.is_alt ? 'Alt' : 'Active'}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Member Details Card */}
                            <div className="card">
                                <div className="card-header"><div className="card-title"><span className="card-icon">👤</span>Member Details</div></div>
                            
                                {selectedMember ? (
                                    <>
                                        <div className="member-details-grid">
                                            <div className="member-info-left">
                                                <div className="hot-member-card" style={{position: 'relative'}}>
                                                    <div style={{position: 'absolute', top: '12px', right: '12px', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end'}}>
                                                        <div className={`member-status-badge-compact ${selectedMember.is_active === false ? 'inactive' : 'active'}`}>{selectedMember.is_active === false ? 'inactive' : 'active'}</div>
                                                        {selectedMember.rank && <span className={getRankClass(selectedMember.rank)}>{selectedMember.rank}</span>}
                                                        {selectedMember.is_alt === true && <div className="member-status-badge-compact alt">alt</div>}
                                                    </div>
                                                    <div className="hot-card-grid-compact">
                                                        {/* Left Column: Avatar, Name, Canonical, Aliases, Joined */}
                                                        <div className="hot-left-column">
                                                            <div className="hot-header-tight">
                                                                <div className="hot-avatar">{(selectedMember.display_name || selectedMember.name).charAt(0).toUpperCase()}</div>
                                                                <div className="hot-name-compact">{selectedMember.display_name || selectedMember.name}</div>
                                                            </div>

                                                            {/* Canonical */}
                                                            {selectedMember.canonical_name && (
                                                                <div className="hot-meta-line"><span className="hot-label">Canonical:</span> <span className="hot-value">{selectedMember.canonical_name}</span></div>
                                                            )}

                                                            {/* Aliases - Defensive rendering */}
                                                            {selectedMember.aliases && Array.isArray(selectedMember.aliases) && selectedMember.aliases.length > 0 && (
                                                                <div className="hot-aliases-section">
                                                                    <span className="hot-label">Aliases:</span>
                                                                    <div className="hot-aliases-list">{selectedMember.aliases.map((alias, idx) => (<span key={idx} className="hot-alias-tag">{alias} <button className="hot-alias-delete" onClick={() => {const newAliases = selectedMember.aliases.filter((_, i) => i !== idx); setSelectedMember({...selectedMember, aliases: newAliases}); }} title="Remove alias" style={{cursor: 'pointer', marginLeft: '2px', opacity: 0.7}}>✕</button></span>))}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="hot-card-body">
{(() => { 
    let memberMedals = { gold: 0, silver: 0, bronze: 0 };
    
    if (selectedEvent && selectedEvent.id !== 'ALL') {
        // For individual events, calculate medals based on daily rankings
        const memberName = selectedMember.display_name || selectedMember.name;
        for (let dayIndex = 0; dayIndex < 6; dayIndex++) {
            const dayScores = memberContributions
                .filter(c => c.event_id === selectedEvent.id && c.day_number === dayIndex + 1 && c.score > 0)
                .map(c => {
                    const member = members.find(m => m.id === c.member_id);
                    return { name: member?.display_name || member?.name, score: c.score };
                })
                .sort((a, b) => b.score - a.score);
            
            if (dayScores[0]?.name === memberName) memberMedals.gold++;
            else if (dayScores[1]?.name === memberName) memberMedals.silver++;
            else if (dayScores[2]?.name === memberName) memberMedals.bronze++;
        }
    } else if (selectedEvent?.id === 'ALL') {
        // For all events, calculate medals across all events
        const memberName = selectedMember.display_name || selectedMember.name;
        const allEventsList = events.filter(e => e.id !== 'ALL');
        
        allEventsList.forEach(event => {
            const eventContribs = memberContributions.filter(c => c.event_id === event.id);
            for (let dayIndex = 1; dayIndex <= 6; dayIndex++) {
                const dayScores = eventContribs
                    .filter(c => c.day_number === dayIndex && c.score > 0)
                    .map(c => {
                        const member = members.find(m => m.id === c.member_id);
                        return { name: member?.display_name || member?.name, score: c.score };
                    })
                    .sort((a, b) => b.score - a.score);
                
                if (dayScores[0]?.name === memberName) memberMedals.gold++;
                else if (dayScores[1]?.name === memberName) memberMedals.silver++;
                else if (dayScores[2]?.name === memberName) memberMedals.bronze++;
            }
        });
    }
    
    const totalMedals = memberMedals.gold + memberMedals.silver + memberMedals.bronze;
    return totalMedals > 0 ? (
        <div className="hot-medals-display">
            <span className="medal-count gold">🥇 {memberMedals.gold}</span>
            <span className="medal-count silver">🥈 {memberMedals.silver}</span>
            <span className="medal-count bronze">🥉 {memberMedals.bronze}</span>
        </div>
    ) : null;
})()}
<div className="hot-stats-centered">
                                                        <div className="hot-stat"><div className="hot-stat-label">Events</div><div className="hot-stat-value">{selectedMemberEventCount}</div></div>
                                                        <div className="hot-stat"><div className="hot-stat-label">Total</div><div className="hot-stat-value">{formatCompact(selectedMemberTotalScore)}</div></div>
                                                    </div>

                                                    {/* Bottom button bar: Edit | X | Delete */}
                                                    <div className="hot-button-bar">
                                                        <button className="btn btn-secondary" onClick={handleUpdateMember} disabled={saving}>✏️ Edit</button>
                                                        <button className="btn btn-secondary hot-close" onClick={() => { setShowAuthHint(false); setSelectedMember(null); setMemberAllContributions([]); }} aria-label="Close">✕</button>
                                                        <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>🗑️ Delete</button>
                                                    </div>

                                                    </div>
                                                </div>
                                            </div>

                                            <div className="member-info-right">

                                                {/* Edit Form */}
                                                <div style={{ marginBottom: '16px' }}>
                                                    <div className="form-label" style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '700' }}>✏️ Edit Member Details</div>
                                                    <div className="form-group">
                                                        <label className="form-label">Add Aliases (comma or newline separated)</label>
                                                        <textarea className="form-textarea" placeholder="OldName1, OldName2" value={memberFormData.newAliases} onChange={(e) => setMemberFormData(p => ({ ...p, newAliases: e.target.value }))} disabled={saving} rows="2" />
                                                    </div>

                                                    <div className="form-grid">
                                                        <div className="form-group"><label className="form-label">Display Name</label><input className="form-input" type="text" value={memberFormData.displayName} onChange={(e) => setMemberFormData(p => ({ ...p, displayName: e.target.value }))} disabled={saving} /></div>
                                                        <div className="form-group"><label className="form-label">Status</label><select className="form-select" value={memberFormData.status} onChange={(e) => setMemberFormData(p => ({ ...p, status: e.target.value }))} disabled={saving}><option value="active">Active</option><option value="inactive">Not Active</option></select></div>
                                                        <div className="form-group"><label className="form-label">Rank</label><select className="form-select" value={memberFormData.rank} onChange={(e) => setMemberFormData(p => ({ ...p, rank: e.target.value }))} disabled={saving}><option value="R1">R1</option><option value="R2">R2</option><option value="R3">R3</option><option value="R4">R4</option><option value="R5">R5</option></select></div>
                                                        <div className="form-group"><label className="form-label">Joined Date</label><input className="form-input" type="date" value={memberFormData.joinedDate} onChange={(e) => setMemberFormData(p => ({ ...p, joinedDate: e.target.value }))} disabled={saving} /></div>
                                                        <div className="form-group"><label className="form-label">Left Date (optional)</label><input className="form-input" type="date" value={memberFormData.leftDate} onChange={(e) => setMemberFormData(p => ({ ...p, leftDate: e.target.value }))} disabled={saving} /></div>
                                                        <div className="form-group" style={{display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '24px'}}><input type="checkbox" id="isAlt" checked={memberFormData.isAlt} onChange={(e) => setMemberFormData(p => ({ ...p, isAlt: e.target.checked }))} disabled={saving} style={{width: '18px', height: '18px', accentColor: 'var(--accent-primary)'}} /><label htmlFor="isAlt" className="form-label" style={{margin: 0, cursor: 'pointer'}}>Alt Account</label></div>
                                                    </div>

                                                    {/* Membership Periods Editor */}
                                                    <div style={{ marginTop: '20px', padding: '12px', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.3)' }}>
                                                        {selectedMember ? (
                                                            <MemberPeriodsEditor
                                                                memberId={selectedMember.id}
                                                                periods={memberPeriods[selectedMember.id] || []}
                                                                onAddPeriod={handleAddPeriod}
                                                                onUpdatePeriod={handleUpdatePeriod}
                                                                onDeletePeriod={handleDeletePeriod}
                                                            />
                                                        ) : (
                                                            <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>Select a member to view periods</div>
                                                        )}
                                                    </div>

                                                    <button className="btn btn-primary" onClick={handleUpdateMember} disabled={saving} style={{ marginTop: '12px' }}>{saving ? '⏳ Saving...' : '💾 Save All Changes'}</button>
                                                </div>

                                                {/* Delete Section */}
                                                <div className="delete-section">
                                                    {showDeleteConfirm && (
                                                        <div className="delete-warning">
                                                            <div className="delete-warning-title">⚠️ Confirm Deletion</div>
                                                            <div className="delete-warning-text">This will permanently delete {selectedMember.display_name || selectedMember.name} and all their contribution data. This cannot be undone.</div>
                                                            <div className="btn-group" style={{ marginTop: '12px' }}>
                                                                <button className="btn btn-danger" onClick={handleDeleteMember} disabled={saving}>{saving ? '⏳ Deleting...' : '🗑️ Yes, Delete'}</button>
                                                                <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                            </div>
                                        </div>

                                        {/* Performance Across All Events */}
                                        <div style={{ marginTop: '20px' }}>
                                            <div className="form-label" style={{ marginBottom: '16px', fontSize: '14px', fontWeight: '700' }}>📊 Performance Across All Events</div>
                                            {memberAllContributions.length > 0 ? (
                                                <div className="performance-grid">
                                                    {memberAllContributions.map((ed, idx) => {
                                                        const total = ed.scores.reduce((s, v) => s + v, 0);
                                                        return (
                                                            <div key={idx} className="event-performance-card">
                                                                <div className="event-performance-header">
                                                                    <div className="event-performance-title-row">
                                                                        <span>{ed.event?.name || 'Unknown'}</span>
                                                                        {ed.event?.opponent && <span>vs {ed.event.opponent}</span>}
                                                                    </div>
                                                                    <div className="event-performance-date">{ed.event?.event_date ? new Date(ed.event.event_date).toLocaleDateString() : ''}{ed.event?.end_date ? ` - ${new Date(ed.event.end_date).toLocaleDateString()}` : ''}</div>
                                                                </div>
                                                                <div className="day-scores-grid">
                                                                    {ed.scores.map((s, i) => (<div key={i} className="day-score-item"><div className="day-score-label">Day {i + 1}</div><div className={`day-score-value ${s === 0 ? 'zero' : ''}`}>{formatCompact(s)}</div></div>))}
                                                                    <div className="day-score-item total-score"><div className="day-score-label">TOTAL</div><div className="day-score-value">{formatCompact(total)}</div></div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="empty-state"><div className="empty-state-icon">📊</div><div className="empty-state-title">No Performance Data</div><div className="empty-state-text">This member has no recorded scores yet.</div></div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="empty-state"><div className="empty-state-icon">🔍</div><div className="empty-state-title">No Member Selected</div><div className="empty-state-text">Select a member from the list above to view and edit their details</div></div>
                                )}
                            </div>

                            {/* Add New Members Card - At the Bottom */}
                            <div className="card">
                                <div className="card-header"><div className="card-title"><span className="card-icon">➕</span>Add New Members</div></div>
                                <div style={{padding: '16px'}}>
                                    <div className="form-group">
                                        <label className="form-label">
                                            Member Names (comma or newline separated)
                                            <div style={{
                                                fontWeight: 500,
                                                color: 'var(--text-secondary)',
                                                fontSize: '12px',
                                                marginTop: '4px',
                                                fontFamily: 'monospace',
                                                letterSpacing: '0.2px',
                                                background: 'rgba(255,255,255,0.04)',
                                                borderRadius: '4px',
                                                padding: '2px 8px',
                                                display: 'inline-block'
                                            }}>
                                                <span style={{fontFamily: 'monospace'}}>e.g. Player1, Player2, Player3</span>
                                                <span style={{margin: '0 8px', color: '#a89bb0'}}>or</span>
                                                <span style={{fontFamily: 'monospace'}}>Player1 [newline] Player2 [newline] Player3</span>
                                            </div>
                                        </label>
                                        <textarea 
                                            className="form-textarea" 
                                            placeholder="Player1, Player2, Player3"
                                            value={memberInput} 
                                            onChange={(e) => setMemberInput(e.target.value)} 
                                            disabled={saving} 
                                            rows="3"
                                        />
                                    </div>
                                    <button className="btn btn-primary" onClick={handleAddMembers} disabled={saving}>{saving ? '⏳ Adding...' : '➕ Add Members'}</button>
                                </div>
                            </div>
                        </div>)}

                        {/* Data Entry Tab */}
                        {activeTab === 'data-entry' && selectedEvent && (<>
                            <div className="card"><div className="card-header"><div className="card-title"><span className="card-icon">📊</span>Update Daily Totals - {selectedEvent.name}</div></div>
                                <p style={{color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px'}}>Enter scores with commas (e.g., 25,036). Leave blank to keep existing values.</p>
                                <h3 style={{color: 'var(--accent-primary)', marginBottom: '15px', fontSize: '16px', fontWeight: '700'}}>Your Guild</h3>
                                <div className="form-grid">{DAY_NAMES.map((d, i) => { const existing = dailyScores.find(x => x.day_number === i + 1); return (<div key={i} className="form-group"><label className="form-label">{DAY_ICONS[i]} Day {i + 1}: {d}</label><input className="form-input" type="text" placeholder={existing ? formatNumber(existing.your_guild_score || 0) : "0"} value={dayScores[i]} onChange={(e) => { const n = [...dayScores]; n[i] = e.target.value; setDayScores(n); }} disabled={saving} /></div>); })}</div>
                                <h3 style={{color: 'var(--accent-secondary)', marginTop: '30px', marginBottom: '15px', fontSize: '16px', fontWeight: '700'}}>Opponent: {selectedEvent.opponent}</h3>
                                <div className="form-grid">{DAY_NAMES.map((d, i) => { const existing = dailyScores.find(x => x.day_number === i + 1); return (<div key={i} className="form-group"><label className="form-label">{DAY_ICONS[i]} Day {i + 1}: {d}</label><input className="form-input" type="text" placeholder={existing ? formatNumber(existing.opponent_score || 0) : "0"} value={opponentDayScores[i]} onChange={(e) => { const n = [...opponentDayScores]; n[i] = e.target.value; setOpponentDayScores(n); }} disabled={saving} /></div>); })}</div>
                                <button className="btn btn-primary" onClick={handleUpdateDayScores} disabled={saving} style={{marginTop: '20px'}}>{saving ? '⏳ Updating...' : '💾 Update Daily Totals'}</button>
                            </div>
                            <div className="card"><div className="card-header"><div className="card-title"><span className="card-icon">✏️</span>Update Member Contributions</div></div>
                                <p style={{color: 'var(--text-secondary)', marginBottom: '15px', fontSize: '14px'}}>Paste from Excel (TAB-separated) or use semicolon.</p>
                                <div className="form-group" style={{marginBottom: '20px'}}><label className="form-label">Select Day</label><select className="form-select" value={selectedDay} onChange={(e) => setSelectedDay(parseInt(e.target.value))} disabled={saving}>{DAY_NAMES.map((d, i) => (<option key={i} value={i + 1}>{DAY_ICONS[i]} Day {i + 1}: {d}</option>))}</select></div>
                                <div className="form-group"><label className="form-label">Member Scores (Name[TAB]Score or Name;Score)</label><textarea className="form-textarea" placeholder="John Doe\t2,500,000\nJane Smith\t1,800,000" value={memberScoresInput} onChange={(e) => setMemberScoresInput(e.target.value)} disabled={saving} /></div>
                                <button className="btn btn-primary" onClick={handleUpdateMemberScores} disabled={saving}>{saving ? '⏳ Updating...' : `💾 Update Scores for Day ${selectedDay}`}</button>
                            </div>
                        </>)}

                        {/* Settings/Setup Tab */}
                        {activeTab === 'setup' && (<div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                            {/* Create New Event - Compact Horizontal Form */}
                            <div className="card">
                                <div className="card-header"><div className="card-title"><span className="card-icon">➕</span>Create New Event</div></div>
                                <div style={{display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '16px'}}>
                                    <div className="form-group" style={{flex: '1 1 150px', minWidth: '140px', margin: 0}}>
                                        <label className="form-label" style={{fontSize: '12px', marginBottom: '6px'}}>Event Name</label>
                                        <input className="form-input" type="text" placeholder="GAR" value={eventName} onChange={(e) => setEventName(e.target.value)} disabled={saving} style={{fontSize: '13px'}} />
                                    </div>
                                    <div className="form-group" style={{flex: '1 1 150px', minWidth: '140px', margin: 0}}>
                                        <label className="form-label" style={{fontSize: '12px', marginBottom: '6px'}}>Opponent Guild</label>
                                        <input className="form-input" type="text" placeholder="Guild Name" value={opponentName} onChange={(e) => setOpponentName(e.target.value)} disabled={saving} style={{fontSize: '13px'}} />
                                    </div>
                                    <div className="form-group" style={{flex: '1 1 100px', minWidth: '90px', margin: 0}}>
                                        <label className="form-label" style={{fontSize: '12px', marginBottom: '6px'}}>Server</label>
                                        <input className="form-input" type="text" placeholder="S10xxx" value={eventServer} onChange={(e) => setEventServer(e.target.value)} disabled={saving} style={{fontSize: '13px'}} />
                                    </div>
                                    <div className="form-group" style={{flex: '1 1 130px', minWidth: '120px', margin: 0}}>
                                        <label className="form-label" style={{fontSize: '12px', marginBottom: '6px'}}>Start Date</label>
                                        <input className="form-input" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} disabled={saving} style={{fontSize: '13px'}} />
                                    </div>
                                    <div className="form-group" style={{flex: '1 1 130px', minWidth: '120px', margin: 0}}>
                                        <label className="form-label" style={{fontSize: '12px', marginBottom: '6px'}}>End Date</label>
                                        <input className="form-input" type="date" value={eventEndDate} onChange={(e) => setEventEndDate(e.target.value)} disabled={saving} style={{fontSize: '13px'}} />
                                    </div>
                                    <button className="btn btn-primary" onClick={handleCreateEvent} disabled={saving} style={{flex: '0 0 auto', marginTop: 0, padding: '8px 16px', fontSize: '13px'}}>{saving ? '⏳' : '➕'}</button>
                                </div>
                            </div>

                            {/* Edit Events - List of Existing Events */}
                            {events && events.length > 0 && (
                                <div className="card">
                                    <div className="card-header"><div className="card-title"><span className="card-icon">✏️</span>Edit Events</div></div>
                                    <div style={{display: 'grid', gap: '12px', padding: '16px'}}>
                                        {[...events].sort((a, b) => new Date(b.event_date) - new Date(a.event_date)).map(event => (
                                            <div key={event.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)'}}>
                                                <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '4px'}}>
                                                    <div style={{fontWeight: '600', color: 'var(--text-primary)', fontSize: '14px'}}>{event.name} <span style={{color: 'var(--text-secondary)', fontSize: '12px'}}>vs {event.opponent || 'N/A'}</span> {event.is_low_priority && <span style={{display: 'inline-block', backgroundColor: 'rgba(255, 107, 107, 0.2)', color: '#FF6B6B', fontSize: '8px', padding: '1px 2x', borderRadius: '3px', fontWeight: '600'}}>LOW PRIORITY</span>}</div>
                                                    <div style={{fontSize: '11px', color: 'var(--text-muted)'}}>{formatDateDisplay(event.event_date)} {event.end_date && `• to ${formatDateDisplay(event.end_date)}`}</div>
                                                </div>
                                                <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                                                    <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedEvent(event); navigateTo('dashboard'); }} style={{fontSize: '12px', padding: '6px 12px'}}>View</button>
                                                    {(userRole === 'admin' || userRole === 'super_admin') && (
                                                        <>
                                                            <button className="btn btn-secondary btn-sm" onClick={() => { 
                                                                setEditingGuildEventId(event.id); 
                                                                setEditingGuildEventData({...event, event_date: extractDateForInput(event.event_date), end_date: extractDateForInput(event.end_date)}); 
                                                            }} style={{fontSize: '12px', padding: '6px 12px'}}>✏️ Edit</button>
                                                            <button className="btn btn-danger btn-sm" onClick={async () => { 
                                                                if(window.confirm(`Delete event "${event.name}"?`)) {
                                                                    try {
                                                                        await apiCall('delete', 'events', { id: event.id });
                                                                        setEvents(events.filter(e => e.id !== event.id));
                                                                    } catch (err) { console.error('Delete error:', err); }
                                                                }
                                                            }} style={{fontSize: '12px', padding: '6px 12px'}}>Delete</button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Edit Event Modal */}
                                    {editingGuildEventId && editingGuildEventData && (
                                        <div className="modal-overlay" onClick={() => setEditingGuildEventId(null)}>
                                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                                <div className="modal-header">
                                                    <h2 style={{margin: 0, fontSize: '18px', fontWeight: '700'}}>Edit Event</h2>
                                                    <button className="modal-close" onClick={() => setEditingGuildEventId(null)}>✕</button>
                                                </div>
                                                <div className="modal-body">
                                                    <div className="form-group">
                                                        <label className="form-label">Event Name</label>
                                                        <input className="form-input" type="text" value={editingGuildEventData.name || ''} onChange={(e) => setEditingGuildEventData(p => ({...p, name: e.target.value}))} />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">Opponent Guild</label>
                                                        <input className="form-input" type="text" value={editingGuildEventData.opponent || ''} onChange={(e) => setEditingGuildEventData(p => ({...p, opponent: e.target.value}))} />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">Server</label>
                                                        <input className="form-input" type="text" value={editingGuildEventData.server || ''} onChange={(e) => setEditingGuildEventData(p => ({...p, server: e.target.value}))} />
                                                    </div>
                                                    <div className="modal-form-row">
                                                        <div className="form-group">
                                                            <label className="form-label">Start Date</label>
                                                            <input className="form-input" type="date" value={editingGuildEventData.event_date || ''} onChange={(e) => setEditingGuildEventData(p => ({...p, event_date: e.target.value}))} />
                                                        </div>
                                                        <div className="form-group">
                                                            <label className="form-label">End Date</label>
                                                            <input className="form-input" type="date" value={editingGuildEventData.end_date || ''} onChange={(e) => setEditingGuildEventData(p => ({...p, end_date: e.target.value}))} />
                                                        </div>
                                                    </div>
                                                    <div className="form-group" style={{display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '6px', marginTop: '8px'}}>
                                                        <input type="checkbox" id="isLowPriority" checked={editingGuildEventData.is_low_priority || false} onChange={(e) => setEditingGuildEventData(p => ({...p, is_low_priority: e.target.checked}))} style={{width: '16px', height: '16px', accentColor: 'var(--accent-primary)', cursor: 'pointer'}} />
                                                        <label htmlFor="isLowPriority" className="form-label" style={{margin: 0, cursor: 'pointer', fontSize: '12px'}}>Low Priority Event</label>
                                                    </div>
                                                </div>
                                                <div className="modal-footer" style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                                                    <button className="btn btn-secondary" onClick={() => setEditingGuildEventId(null)}>Cancel</button>
                                                    <button className="btn btn-primary" onClick={async () => {
                                                        try {
                                                            setSaving(true);
                                                            const updateData = {
                                                                name: editingGuildEventData.name,
                                                                opponent: editingGuildEventData.opponent,
                                                                server: editingGuildEventData.server,
                                                                event_date: editingGuildEventData.event_date,
                                                                end_date: editingGuildEventData.end_date,
                                                                is_low_priority: editingGuildEventData.is_low_priority ? true : false
                                                            };
                                                            const result = await apiCall('update', 'events', { id: editingGuildEventId, data: updateData });
                                                            const updatedEvent = Array.isArray(result) ? result[0] : result;
                                                            setEvents(events.map(e => e.id === editingGuildEventId ? updatedEvent : e));
                                                            setEditingGuildEventId(null);
                                                            setEditingGuildEventData(null);
                                                        } catch (err) {
                                                            console.error('Update error:', err);
                                                            alert('Failed to update event: ' + err.message);
                                                        } finally {
                                                            setSaving(false);
                                                        }
                                                    }} disabled={saving}>💾 Save Changes</button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>)}

                        {!selectedEvent && activeTab === 'dashboard' && events.length === 0 && (<div className="card"><div className="empty-state"><div className="empty-state-icon">👋</div><div className="empty-state-title">Welcome!</div><div className="empty-state-text">Get started by creating your first event in Settings.</div></div></div>)}

                        {/* Guides Tab - Main Content Area */}
                        {activeTab === 'guides' && (
                            <>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        <span style={{fontSize: '24px'}}>📖</span>
                                        <div className="card-title" style={{margin: 0}}>Guild Guides</div>
                                    </div>
                                    {(userRole === 'admin' || userRole === 'super_admin') && !editingGuideId && !selectedGuideId && !isCreatingNewGuide && (
                                        <button 
                                            onClick={() => {
                                                setSelectedGuideId(null);
                                                setIsCreatingNewGuide(true);
                                                setNewGuide({ title: '', description: '', content: '', backgroundImage: '' });
                                            }}
                                            style={{padding: '6px 12px', fontSize: '11px', backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}
                                        >➕ Add Guide</button>
                                    )}
                                </div>

                                <div style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>

                                    {/* Selected Guide View - Expanded */}
                                    {selectedGuideId && !editingGuideId && (
                                        <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                                            {guides.find(g => g.id === selectedGuideId) && (
                                                <>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                                                        <div style={{flex: 1}}>
                                                            <h1 style={{margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', lineHeight: '1.2'}}>{guides.find(g => g.id === selectedGuideId).title}</h1>
                                                            <div style={{fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '12px'}}>{guides.find(g => g.id === selectedGuideId).description}</div>
                                                            <div style={{fontSize: '12px', color: 'var(--text-muted)'}}>Updated {new Date(guides.find(g => g.id === selectedGuideId).updatedAt).toLocaleDateString()}</div>
                                                        </div>
                                                        <div style={{display: 'flex', gap: '8px', marginLeft: '16px', flexDirection: 'column', alignItems: 'flex-end'}}>
                                                            {(userRole === 'admin' || userRole === 'super_admin') && (
                                                                <>
                                                                    <button onClick={() => togglePinGuide(selectedGuideId)} style={{padding: '8px 12px', fontSize: '13px', backgroundColor: guides.find(g => g.id === selectedGuideId)?.isPinned ? 'rgba(46,204,113,0.15)' : 'rgba(255,255,255,0.08)', color: guides.find(g => g.id === selectedGuideId)?.isPinned ? 'var(--accent-success)' : 'var(--text-primary)', border: '1px solid ' + (guides.find(g => g.id === selectedGuideId)?.isPinned ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.15)'), borderRadius: '6px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s'}}>{guides.find(g => g.id === selectedGuideId)?.isPinned ? '📌 Pinned' : '📌 Pin'}</button>
                                                                    <button onClick={() => setEditingGuideId(selectedGuideId)} style={{padding: '8px 12px', fontSize: '13px', backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}>✏️ Edit</button>
                                                                    <button onClick={() => setGuideToDelete(selectedGuideId)} style={{padding: '8px 12px', fontSize: '13px', backgroundColor: 'rgba(255,75,75,0.2)', color: 'var(--accent-red)', border: '1px solid var(--accent-red)', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}>🗑️ Delete</button>
                                                                </>
                                                            )}
                                                            <button onClick={() => {
                                                                setSelectedGuideId(null);
                                                                try { window.location.hash = 'guides'; } catch (e) {}
                                                            }} style={{padding: '8px 12px', fontSize: '13px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}>Close</button>
                                                        </div>
                                                    </div>
                                                    <div style={{backgroundColor: 'rgba(255,255,255,0.02)', padding: '20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.8'}} className="markdown-content" dangerouslySetInnerHTML={{__html: (() => {
                                                        try {
                                                            if (typeof marked !== 'undefined' && marked.parse) {
                                                                const html = marked.parse(guides.find(g => g.id === selectedGuideId).content || '');
                                                                return html;
                                                            }
                                                        } catch (e) {
                                                            console.error('Markdown parsing error:', e);
                                                        }
                                                        return (guides.find(g => g.id === selectedGuideId).content || '').replace(/\n/g, '<br>');
                                                    })()}} />
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* New Guide Form */}
                                    {isCreatingNewGuide && (
                                        <div style={{backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '16px'}}>
                                            <div>
                                                <h3 style={{margin: '0 0 16px 0', fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)'}}>📝 Create New Guide</h3>
                                            </div>
                                            <div>
                                                <label style={{display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Title *</label>
                                                <input 
                                                    type="text"
                                                    value={newGuide.title}
                                                    onChange={(e) => setNewGuide({...newGuide, title: e.target.value})}
                                                    style={{width: '100%', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                                    placeholder="Guide title"
                                                />
                                            </div>
                                            <div>
                                                <label style={{display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Description</label>
                                                <input 
                                                    type="text"
                                                    value={newGuide.description}
                                                    onChange={(e) => setNewGuide({...newGuide, description: e.target.value})}
                                                    style={{width: '100%', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                                    placeholder="Brief description"
                                                />
                                            </div>
                                            <div>
                                                <label style={{display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Background Image URL or Upload</label>
                                                <div style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
                                                    <input 
                                                        type="text"
                                                        value={newGuide.backgroundImage || ''}
                                                        onChange={(e) => setNewGuide({...newGuide, backgroundImage: e.target.value})}
                                                        style={{flex: 1, padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                                        placeholder="Image URL for guide card background"
                                                    />
                                                    <label style={{padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'}}>
                                                        📤 Upload
                                                        <input 
                                                            type="file" 
                                                            accept="image/*"
                                                            style={{display: 'none'}}
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) {
                                                                    // Use guide title if available, otherwise use temp slug
                                                                    const guideSlug = newGuide.title ? slugify(newGuide.title) : 'new-guide';
                                                                    uploadBackgroundImage(
                                                                        file,
                                                                        guideSlug,
                                                                        (url) => {
                                                                            setNewGuide({...newGuide, backgroundImage: url});
                                                                            showToast('success', 'Background Uploaded', 'Image uploaded to MinIO');
                                                                        },
                                                                        (error) => showToast('error', 'Upload Failed', error)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', flex: 1}}>
                                                <label style={{fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Content (Supports Markdown)</label>
                                                <textarea 
                                                    value={newGuide.content}
                                                    onChange={(e) => setNewGuide({...newGuide, content: e.target.value})}
                                                    style={{flex: 1, minHeight: '200px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px', boxSizing: 'border-box', resize: 'none'}}
                                                    placeholder="Guide content - supports markdown"
                                                />
                                                <div style={{fontSize: '11px', color: 'var(--text-muted)', padding: '8px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '4px'}}>
                                                    💡 Tip: Use **bold**, *italic*, # Headings, and ![alt](url) for images
                                                </div>
                                            </div>
                                            <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                                                <button 
                                                    onClick={async () => {
                                                        await createGuide();
                                                        setIsCreatingNewGuide(false);
                                                    }}
                                                    style={{padding: '10px 24px', backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'}}
                                                >✓ Create Guide</button>
                                                <button 
                                                    onClick={() => {
                                                        setIsCreatingNewGuide(false);
                                                        setNewGuide({ title: '', description: '', content: '' });
                                                    }}
                                                    style={{padding: '10px 24px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'}}
                                                >Cancel</button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Guide Editor */}
                                    {editingGuideId && (
                                        <div style={{backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '16px'}}>
                                            <div>
                                                <h3 style={{margin: '0 0 16px 0', fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)'}}>✏️ Edit Guide</h3>
                                            </div>
                                            <div>
                                                <label style={{display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Title</label>
                                                <input 
                                                    type="text"
                                                    value={editingGuideData.title}
                                                    onChange={(e) => debouncedUpdateGuide(editingGuideId, 'title', e.target.value)}
                                                    style={{width: '100%', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                                />
                                            </div>
                                            <div>
                                                <label style={{display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Description</label>
                                                <input 
                                                    type="text"
                                                    value={editingGuideData.description}
                                                    onChange={(e) => debouncedUpdateGuide(editingGuideId, 'description', e.target.value)}
                                                    style={{width: '100%', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                                />
                                            </div>
                                            <div>
                                                <label style={{display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Background Image URL or Upload</label>
                                                <div style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
                                                    <input 
                                                        type="text"
                                                        value={editingGuideData.backgroundImage || ''}
                                                        onChange={(e) => debouncedUpdateGuide(editingGuideId, 'backgroundImage', e.target.value)}
                                                        style={{flex: 1, padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                                        placeholder="Image URL for guide card background"
                                                    />
                                                    <label style={{padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'}}>
                                                        📤 Upload
                                                        <input 
                                                            type="file" 
                                                            accept="image/*"
                                                            style={{display: 'none'}}
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file && editingGuideId) {
                                                                    const guide = guides.find(g => g.id === editingGuideId);
                                                                    const guideSlug = guide ? slugify(guide.title) : 'unknown';
                                                                    uploadBackgroundImage(
                                                                        file,
                                                                        guideSlug,
                                                                        (url) => {
                                                                            debouncedUpdateGuide(editingGuideId, 'backgroundImage', url);
                                                                            showToast('success', 'Background Uploaded', 'Image uploaded to MinIO');
                                                                        },
                                                                        (error) => showToast('error', 'Upload Failed', error)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', flex: 1}}>
                                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                    <label style={{fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Content (Supports Markdown)</label>
                                                    <div style={{fontSize: '11px', color: 'var(--text-muted)'}}>
                                                        <label style={{cursor: 'pointer', padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)', display: 'inline-block'}}>
                                                            📸 Upload Image
                                                            <input 
                                                                type="file" 
                                                                accept="image/*"
                                                                style={{display: 'none'}}
                                                                onChange={async (e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file && editingGuideId) {
                                                                        try {
                                                                            const guide = guides.find(g => g.id === editingGuideId);
                                                                            const guideSlug = guide ? slugify(guide.title) : 'unknown';
                                                                            
                                                                            const formData = new FormData();
                                                                            formData.append('file', file);
                                                                            
                                                                            const response = await fetch(buildUploadEndpoint(guideSlug), {
                                                                                method: 'POST',
                                                                                body: formData
                                                                            });
                                                                            
                                                                            if (DEBUG) console.log('Upload response status:', response.status, response.statusText);
                                                                            
                                                                            if (!response.ok) {
                                                                                const text = await response.text();
                                                                                console.error('Upload error response:', text.substring(0, 500));
                                                                                throw new Error(`Upload failed: ${response.status}`);
                                                                            }
                                                                            
                                                                            const data = await response.json();
                                                                            if (DEBUG) console.log('Upload successful:', data.url);
                                                                            
                                                                            const markdownImage = `\n![image](${data.url})`;
                                                                            insertFormatting('content', markdownImage, '', '');
                                                                            showToast('success', 'Image Uploaded', 'Image inserted into guide');
                                                                        } catch (error) {
                                                                            console.error('Upload error:', error.message);
                                                                            showToast('error', 'Upload Failed', error.message);
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </label>
                                                    </div>
                                                </div>
                                                {/* Formatting Toolbar */}
                                                <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)'}}>
                                                    <button onClick={() => insertFormatting('content', '**', '**', 'bold')} title="Bold" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'}}>B</button>
                                                    <button onClick={() => insertFormatting('content', '*', '*', 'italic')} title="Italic" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontStyle: 'italic'}}>I</button>
                                                    <button onClick={() => insertFormatting('content', '<u>', '</u>', 'underline')} title="Underline" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', textDecoration: 'underline'}}>U</button>
                                                    <button onClick={() => insertFormatting('content', '~~', '~~', 'strikethrough')} title="Strikethrough" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', textDecoration: 'line-through'}}>S</button>
                                                    <div style={{width: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '0 4px'}}></div>
                                                    <button onClick={() => insertFormatting('content', '# ', '', 'Heading')} title="Heading 1" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontWeight: '700'}}>H1</button>
                                                    <button onClick={() => insertFormatting('content', '## ', '', 'Heading')} title="Heading 2" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'}}>H2</button>
                                                    <button onClick={() => insertFormatting('content', '`', '`', 'code')} title="Inline Code" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontFamily: 'monospace'}}>{'<>'}</button>
                                                    <div style={{width: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '0 4px'}}></div>
                                                    <button onClick={() => insertFormatting('content', '- ', '\n', '')} title="Bullet List" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer'}}>•</button>
                                                    <button onClick={() => insertFormatting('content', '1. ', '\n', '')} title="Numbered List" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer'}}>1.</button>
                                                    <button onClick={() => {
                                                        setShowLinkModal(true);
                                                    }} title="Add Link" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer'}}>🔗</button>
                                                    <button onClick={() => {
                                                        setShowImageModal(true);
                                                    }} title="Add Image URL" style={{padding: '6px 10px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer'}}>🖼️</button>
                                                    <div style={{width: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '0 4px'}}></div>
                                                    <select onChange={(e) => {
                                                        if (e.target.value) {
                                                            const size = e.target.value;
                                                            insertFormatting('content', `<span style="font-size:${size}">`, '</span>', 'text');
                                                            e.target.value = '';
                                                        }
                                                    }} style={{padding: '6px 8px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer'}}>
                                                        <option value="">Size</option>
                                                        <option value="12px">12px</option>
                                                        <option value="14px">14px</option>
                                                        <option value="16px">16px</option>
                                                        <option value="18px">18px</option>
                                                        <option value="20px">20px</option>
                                                        <option value="24px">24px</option>
                                                    </select>
                                                    <select onChange={(e) => {
                                                        if (e.target.value) {
                                                            const font = e.target.value;
                                                            insertFormatting('content', `<span style="font-family:'${font}'">`, '</span>', 'text');
                                                            e.target.value = '';
                                                        }
                                                    }} style={{padding: '6px 8px', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer'}}>
                                                        <option value="">Font</option>
                                                        <option value="Arial">Arial</option>
                                                        <option value="Georgia">Georgia</option>
                                                        <option value="Courier New">Courier New</option>
                                                        <option value="Comic Sans MS">Comic Sans MS</option>
                                                    </select>
                                                </div>
                                                <textarea 
                                                    data-guide-field="content"
                                                    value={editingGuideData.content}
                                                    onChange={(e) => debouncedUpdateGuide(editingGuideId, 'content', e.target.value)}
                                                    style={{flex: 1, minHeight: '400px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px', boxSizing: 'border-box', resize: 'none'}}
                                                />
                                                <div style={{fontSize: '11px', color: 'var(--text-muted)', padding: '8px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '4px'}}>
                                                    💡 Tip: Use **bold**, *italic*, # Headings, and ![alt](url) for images
                                                </div>
                                            </div>

                                            <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                                                <button 
                                                    onClick={() => {
                                                        // Clear any pending debounce timers
                                                        Object.values(guideDebounceTimers.current).forEach(timer => {
                                                            if (timer) clearTimeout(timer);
                                                        });
                                                        guideDebounceTimers.current = { title: null, description: null, content: null };
                                                        
                                                        // Reload guides from localStorage to sync with editor changes
                                                        try {
                                                            const saved = localStorage.getItem('hot_guides');
                                                            if (saved) {
                                                                setGuides(JSON.parse(saved));
                                                            }
                                                        } catch (e) { 
                                                            console.error('Failed to reload guides from localStorage', e); 
                                                        }
                                                        
                                                        // Close the editor
                                                        setEditingGuideId(null);
                                                    }}
                                                    style={{padding: '10px 24px', backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'}}
                                                >Save</button>
                                                <button 
                                                    onClick={() => {
                                                        // Clear any pending debounce timers when canceling
                                                        Object.values(guideDebounceTimers.current).forEach(timer => {
                                                            if (timer) clearTimeout(timer);
                                                        });
                                                        guideDebounceTimers.current = { title: null, description: null, content: null };
                                                        setEditingGuideId(null);
                                                    }}
                                                    style={{padding: '10px 24px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'}}
                                                >Cancel</button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Guides Grid - Show when not viewing/editing a specific guide */}
                                    {!selectedGuideId && !editingGuideId && (
                                        <div>
                                            {guides.length === 0 ? (
                                                <div style={{textAlign: 'center', padding: '80px 40px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)'}}>
                                                    <div style={{fontSize: '64px', marginBottom: '20px'}}>📖</div>
                                                    <div style={{fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px'}}>No Guides Yet</div>
                                                    <div style={{fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px'}}>Start creating guides to help your guild learn and grow</div>
                                                    {(userRole === 'admin' || userRole === 'super_admin') && (
                                                        <button 
                                                            onClick={() => {
                                                                setSelectedGuideId(null);
                                                                setIsCreatingNewGuide(true);
                                                                setNewGuide({ title: '', description: '', content: '', backgroundImage: '' });
                                                            }}
                                                            style={{padding: '8px 16px', fontSize: '12px', backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}
                                                        >➕ Create First Guide</button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px'}}>
                                                    {guides.sort((a, b) => {
                                                        // Pinned guides first
                                                        if (a.isPinned && !b.isPinned) return -1;
                                                        if (!a.isPinned && b.isPinned) return 1;
                                                        // Then by creation date (newest first)
                                                        return new Date(b.createdAt) - new Date(a.createdAt);
                                                    }).map(guide => {
                                                        // Show live editing value if editing this guide, otherwise show saved value
                                                        const backgroundImageValue = editingGuideId === guide.id ? editingGuideData.backgroundImage : guide.backgroundImage;
                                                        const bgImage = backgroundImageValue && backgroundImageValue.trim() ? backgroundImageValue : null;
                                                        return (
                                                        <div 
                                                            key={guide.id}
                                                            onClick={() => navigateToGuide(guide.id)}
                                                            style={{
                                                                backgroundColor: 'rgba(255,255,255,0.02)',
                                                                backgroundImage: bgImage ? `linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('${bgImage}')` : 'none',
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center',
                                                                backgroundRepeat: 'no-repeat',
                                                                border: '0px solid rgba(255,255,255,0.08)',
                                                                borderRadius: '16px',
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                display: 'flex',
                                                                flexDirection: 'column'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.borderColor = 'var(--accent-primary)';
                                                                e.currentTarget.style.backgroundColor = 'rgba(255,107,75,0.08)';
                                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                                                                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }}
                                                        >
                                                            {/* Card Header with Icon */}
                                                            <div style={{backgroundColor: 'linear-gradient(135deg, rgba(255,107,75,0.2), rgba(255,107,75,0.1))', padding: '24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'relative'}}>
                                                                <div style={{fontSize: '48px'}}>📖</div>
                                                                {guide.isPinned && <div style={{position: 'absolute', top: '12px', right: '12px', fontSize: '16px', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(46,204,113,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(46,204,113,0.4)', backdropFilter: 'blur(10px)'}}>📌</div>}
                                                            </div>

                                                            {/* Card Body */}
                                                            <div style={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, alignItems: 'center'}}>
                                                                <div style={{fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', lineHeight: '1.3'}}>{guide.title}</div>
                                                                <div style={{fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{guide.description || 'No description provided'}</div>
                                                            </div>

                                                            {/* Card Footer */}
                                                            <div style={{padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                                <div style={{fontSize: '12px', color: 'var(--text-muted)'}}>Updated {new Date(guide.updatedAt).toLocaleDateString()}</div>
                                                                <div style={{fontSize: '13px', fontWeight: '600', color: 'var(--accent-primary)'}}>Read →</div>
                                                            </div>
                                                        </div>
                                                    );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right Panel */}
                    {activeTab === 'guides' ? (
                        <div className="right-panel">
                            {/* Guides List on Right Panel */}
                            <div className="card" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                                <div className="card-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px'}}>
                                    <div className="card-title" style={{fontSize: '14px'}}><span className="card-icon">📚</span>Guides</div>
                                    {(userRole === 'admin' || userRole === 'super_admin') && !editingGuideId && !selectedGuideId && !isCreatingNewGuide && (
                                        <button 
                                            onClick={() => {
                                                setSelectedGuideId(null);
                                                setIsCreatingNewGuide(true);
                                                setNewGuide({ title: '', description: '', content: '', backgroundImage: '' });
                                            }}
                                            style={{padding: '6px 12px', fontSize: '11px', backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}
                                        >➕ New</button>
                                    )}
                                </div>
                                <div className="card-body" style={{display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flex: 1}}>
                                    {guides.length === 0 ? (
                                        <div style={{textAlign: 'center', padding: '24px 12px', fontSize: '12px', color: 'var(--text-muted)'}}>
                                            <div style={{fontSize: '28px', marginBottom: '8px'}}>📖</div>
                                            <div>No guides yet</div>
                                        </div>
                                    ) : (
                                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                            {guides.sort((a, b) => {
                                                // Pinned guides first
                                                if (a.isPinned && !b.isPinned) return -1;
                                                if (!a.isPinned && b.isPinned) return 1;
                                                // Then by creation date (newest first)
                                                return new Date(b.createdAt) - new Date(a.createdAt);
                                            }).map(guide => {
                                                // If we're editing this guide, show the live editing value; otherwise show the saved value
                                                const backgroundImageValue = editingGuideId === guide.id ? editingGuideData.backgroundImage : guide.backgroundImage;
                                                const bgImage = backgroundImageValue && backgroundImageValue.trim() ? backgroundImageValue : null;
                                                return (
                                                <div 
                                                    key={guide.id}
                                                    onClick={() => navigateToGuide(guide.id)}
                                                    style={{
                                                        padding: '12px',
                                                        backgroundColor: selectedGuideId === guide.id ? 'rgba(255,107,75,0.15)' : 'rgba(255,255,255,0.02)',
                                                        backgroundImage: bgImage ? `linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('${bgImage}')` : 'none',
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundAttachment: 'fixed',
                                                        border: selectedGuideId === guide.id ? '1px solid var(--accent-primary)' : '1px solid rgba(255,255,255,0.08)',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        minHeight: '80px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'flex-end'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (selectedGuideId !== guide.id) {
                                                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                                                            e.currentTarget.style.opacity = '0.9';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (selectedGuideId !== guide.id) {
                                                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                                                            e.currentTarget.style.opacity = '1';
                                                        }
                                                    }}
                                                >
                                                    <div style={{fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '3px', lineHeight: '1.3'}}>{guide.title}</div>
                                                    <div style={{fontSize: '11px', color: 'var(--text-muted)', lineHeight: '1.3', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{guide.description || 'No description'}</div>
                                                </div>
                                            );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="right-panel">
                            <div className="panel-section"><div className="panel-section-title">📊 Quick Stats</div>
                                <div className="quick-stat"><span className="quick-stat-label">Total Events</span><span className="quick-stat-value" style={{color: 'var(--accent-primary)'}}>{events.length}</span></div>
                                <div className="quick-stat"><span className="quick-stat-label">Total Members</span><span className="quick-stat-value" style={{color: 'var(--accent-cyan)'}}>{members.length}</span></div>
                                <div className="quick-stat"><span className="quick-stat-label">Active Members</span><span className="quick-stat-value" style={{color: 'var(--accent-success)'}}>{members.filter(m => m.is_active !== false).length}</span></div>
                            </div>
                            {selectedEvent && (<div className="panel-section"><div className="panel-section-title">⚔️ Current Event</div>
                                <div style={{marginBottom: '12px'}}><div style={{fontWeight: '700', fontSize: '16px', marginBottom: '6px'}}>{selectedEvent.name} vs {selectedEvent.opponent || extractOpponentFromName(selectedEvent?.name)}</div>
                                <div style={{color: 'var(--text-muted)', fontSize: '12px'}}>{new Date(selectedEvent.event_date).toLocaleDateString()} {selectedEvent.end_date ? `- ${new Date(selectedEvent.end_date).toLocaleDateString()}` : ''}</div></div>
                            </div>)}
                        </div>
                    )}
                </div>
            </div>

            {/* Login Modal */}
            {showLoginModal && !currentUser && (<div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setShowLoginModal(false); }}> 
                <div className="modal-box"><div className="modal-title">🔐 Admin Access</div><div className="modal-subtitle">Enter password to access management features</div>
                    <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                    <div className="form-group"><input className="form-input" type="email" placeholder="Email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} /></div>
                    <div className="form-group"><input className="form-input" type="password" placeholder="Enter password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} autoFocus /></div>
                    <div className="btn-group" style={{marginTop: '20px'}}><button type="submit" className="btn btn-primary" style={{flex: 1}}>Login</button><button type="button" className="btn btn-secondary" onClick={() => { setShowLoginModal(false); setPasswordInput(''); }} style={{flex: 1}}>Cancel</button></div>
                    </form>
                </div>
            </div>)}

            {/* Link Modal */}
            {showLinkModal && (
                <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setShowLinkModal(false); }}>
                    <div className="modal-box" style={{maxWidth: '400px'}}>
                        <div className="modal-title">🔗 Add Link</div>
                        <div className="form-group">
                            <label className="form-label">Link Text</label>
                            <input 
                                className="form-input" 
                                type="text" 
                                placeholder="link text"
                                value={linkInput.text}
                                onChange={(e) => setLinkInput({...linkInput, text: e.target.value})}
                                onKeyPress={(e) => { if (e.key === 'Enter') {
                                    insertFormatting('content', '[' + linkInput.text + '](', linkInput.url + ')');
                                    setShowLinkModal(false);
                                    setLinkInput({ text: 'link text', url: 'https://' });
                                }}}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">URL</label>
                            <input 
                                className="form-input" 
                                type="text" 
                                placeholder="https://"
                                value={linkInput.url}
                                onChange={(e) => setLinkInput({...linkInput, url: e.target.value})}
                                onKeyPress={(e) => { if (e.key === 'Enter') {
                                    insertFormatting('content', '[' + linkInput.text + '](', linkInput.url + ')');
                                    setShowLinkModal(false);
                                    setLinkInput({ text: 'link text', url: 'https://' });
                                }}}
                                autoFocus
                            />
                        </div>
                        <div className="btn-group" style={{marginTop: '20px'}}>
                            <button 
                                className="btn btn-primary" 
                                onClick={() => {
                                    insertFormatting('content', '[' + linkInput.text + '](', linkInput.url + ')');
                                    setShowLinkModal(false);
                                    setLinkInput({ text: 'link text', url: 'https://' });
                                }}
                                style={{flex: 1}}
                            >OK</button>
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => {
                                    setShowLinkModal(false);
                                    setLinkInput({ text: 'link text', url: 'https://' });
                                }}
                                style={{flex: 1}}
                            >Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {showImageModal && (
                <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setShowImageModal(false); }}>
                    <div className="modal-box" style={{maxWidth: '400px'}}>
                        <div className="modal-title">🖼️ Add Image</div>
                        <div className="form-group">
                            <label className="form-label">Alt Text</label>
                            <input 
                                className="form-input" 
                                type="text" 
                                placeholder="image description"
                                value={imageInput.alt}
                                onChange={(e) => setImageInput({...imageInput, alt: e.target.value})}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Image URL</label>
                            <input 
                                className="form-input" 
                                type="text" 
                                placeholder="https://"
                                value={imageInput.url}
                                onChange={(e) => setImageInput({...imageInput, url: e.target.value})}
                                onKeyPress={(e) => { if (e.key === 'Enter') {
                                    insertFormatting('content', '\n![' + imageInput.alt + '](', imageInput.url + ')', '');
                                    setShowImageModal(false);
                                    setImageInput({ alt: '', url: '' });
                                }}}
                            />
                        </div>
                        <div style={{display: 'flex', gap: '8px', marginBottom: '16px'}}>
                            <button 
                                className="btn btn-primary" 
                                onClick={() => {
                                    if (!imageInput.url) {
                                        showToast('error', 'Missing URL', 'Please enter an image URL');
                                        return;
                                    }
                                    insertFormatting('content', '\n![' + imageInput.alt + '](', imageInput.url + ')', '');
                                    setShowImageModal(false);
                                    setImageInput({ alt: '', url: '' });
                                }}
                                style={{flex: 1}}
                            >Add URL</button>
                            <label 
                                className="btn btn-primary" 
                                style={{flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', margin: 0}}
                            >
                                📤 Upload
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    style={{display: 'none'}}
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file && editingGuideId) {
                                            try {
                                                const guide = guides.find(g => g.id === editingGuideId);
                                                const guideSlug = guide ? slugify(guide.title) : 'unknown';
                                                
                                                
                                                const formData = new FormData();
                                                formData.append('file', file);
                                                
                                                const response = await fetch(buildUploadEndpoint(guideSlug), {
                                                    method: 'POST',
                                                    body: formData
                                                });
                                                
                                                if (!response.ok) {
                                                    throw new Error('Upload failed: ' + response.status);
                                                }
                                                
                                                const data = await response.json();
                                                if (DEBUG) console.log('Upload response data:', data);
                                                
                                                setImageInput(prev => ({ ...prev, url: data.url }));
                                                showToast('success', 'Image Uploaded', 'Click "Add URL" to insert the image');
                                            } catch (err) {
                                                showToast('error', 'Upload Failed', err.message);
                                                console.error('Image upload error:', err);
                                            }
                                        }
                                    }}
                                />
                            </label>
                        </div>
                        <div className="btn-group" style={{marginTop: '10px'}}>
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => {
                                    setShowImageModal(false);
                                    setImageInput({ alt: '', url: '' });
                                }}
                                style={{flex: 1}}
                            >Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (<div className={`toast ${toast.type}`}><span className="toast-icon">{toast.type === 'success' ? '✓' : toast.type === 'error' ? '✗' : '⚠'}</span><div className="toast-content"><div className="toast-title">{toast.title}</div>{toast.message && <div className="toast-message">{toast.message}</div>}</div><button className="toast-close" onClick={() => setToast(null)}>×</button></div>)}
            
            {/* Performance Details Modal */}
            {performanceModalData && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => setPerformanceModalData(null)}>
                    <div className="modal-scrollable" style={{backgroundColor: 'rgba(20,12,28,0.95)', border: '2px solid var(--border-accent)', borderRadius: '12px', padding: '24px', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)'}} onClick={(e) => e.stopPropagation()}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                            <h2 style={{margin: 0, color: 'var(--text-primary)'}}>{performanceModalData.playerName}</h2>
                            <button onClick={() => setPerformanceModalData(null)} style={{background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '24px', padding: '0'}}>×</button>
                        </div>
                        
                        {!performanceModalData.isAllEvents && (
                        <div style={{marginBottom: '20px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', borderLeft: '3px solid var(--accent-cyan)'}}>
                            <div style={{fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'}}>Event Details</div>
                            <div style={{fontSize: '18px', fontWeight: '700', color: 'var(--accent-cyan)', marginBottom: '8px'}}>{performanceModalData.eventName}</div>
                            <div style={{fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px'}}>📅 {performanceModalData.eventDate} • vs {performanceModalData.opponent}</div>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '12px'}}>
                                <div>
                                    <div style={{color: 'var(--text-muted)', marginBottom: '4px', fontSize: '10px'}}>HOT GUILD SCORE</div>
                                    <div style={{color: 'var(--accent-cyan)', fontWeight: '700', fontSize: '14px'}}>{formatCompact(performanceModalData.hotTotal)}</div>
                                </div>
                                <div>
                                    <div style={{color: 'var(--text-muted)', marginBottom: '4px', fontSize: '10px'}}>{performanceModalData.opponent.toUpperCase()}</div>
                                    <div style={{color: 'var(--accent-purple)', fontWeight: '700', fontSize: '14px'}}>{formatCompact(performanceModalData.opponentTotal)}</div>
                                </div>
                            </div>
                        </div>
                        )}
                        
                        {/* Overall Performance Stats */}
                        <div style={{marginBottom: '20px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', borderLeft: '3px solid var(--accent-green)'}}>
                            <div style={{fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'}}>Overall Performance</div>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                                <div>
                                    <div style={{color: 'var(--text-muted)', fontSize: '10px', marginBottom: '4px'}}>TOTAL SCORE</div>
                                    <div style={{fontSize: '18px', fontWeight: '700', color: 'var(--accent-green)'}}>{formatCompact(performanceModalData.overallStats?.totalScore || 0)}</div>
                                </div>
                                <div>
                                    <div style={{color: 'var(--text-muted)', fontSize: '10px', marginBottom: '4px'}}>AVG PER {performanceModalData.isAllEvents ? 'CONTRIBUTION' : 'DAY'}</div>
                                    <div style={{fontSize: '18px', fontWeight: '700', color: 'var(--accent-green)'}}>{formatCompact(performanceModalData.overallStats?.averageScore || 0)}</div>
                                </div>
                            </div>
                            {performanceModalData.isAllEvents && (
                                <div style={{marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '12px'}}>
                                    <div>
                                        <div style={{color: 'var(--text-muted)', fontSize: '10px', marginBottom: '4px'}}>EVENTS PARTICIPATED</div>
                                        <div style={{fontSize: '16px', fontWeight: '600', color: 'var(--accent-cyan)'}}>{performanceModalData.overallStats?.eventCount || 0}</div>
                                    </div>
                                    <div>
                                        <div style={{color: 'var(--text-muted)', fontSize: '10px', marginBottom: '4px'}}>TOTAL CONTRIBUTIONS</div>
                                        <div style={{fontSize: '16px', fontWeight: '600', color: 'var(--accent-cyan)'}}>{performanceModalData.overallStats?.contributionCount || 0}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div style={{marginBottom: '20px'}}>
                            <div style={{fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px', fontWeight: '600'}}>
                                {performanceModalData.isAllEvents ? '📋 Underperformance by Event' : 'Underperformance Details'}
                            </div>
                            {performanceModalData.isAllEvents ? (
                                // Show breakdown by event for "All Events" view
                                <div className="modal-scrollable" style={{display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto'}}>
                                    {performanceModalData.eventBreakdown && performanceModalData.eventBreakdown.length > 0 ? (
                                        performanceModalData.eventBreakdown.map((eventData, eventIdx) => (
                                            <div key={eventIdx} style={{backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '12px', borderLeft: '3px solid var(--accent-purple)'}}>
                                                <div style={{fontSize: '12px', fontWeight: '600', color: 'var(--accent-cyan)', marginBottom: '8px'}}>{eventData.eventName}</div>
                                                <div style={{fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px'}}>📅 {eventData.eventDate} • vs {eventData.opponent}</div>
                                                <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                                                    {eventData.zeroDays.length > 0 && (
                                                        <div style={{fontSize: '11px', color: 'var(--accent-red)'}}>
                                                            ❌ Zero Days: {eventData.zeroDays.map((d, i) => `${DAY_ICONS[d-1]} Day ${d}`).join(', ')}
                                                        </div>
                                                    )}
                                                    {eventData.belowDays.length > 0 && (
                                                        <div style={{fontSize: '11px', color: 'var(--accent-warning)'}}>
                                                            ⚠️ Below Threshold: {eventData.belowDays.map((d, i) => `${DAY_ICONS[d-1]} Day ${d} (${formatCompact(eventData.scores[d-1])})`).join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                            <div style={{fontSize: '12px', fontWeight: '600', marginTop: '8px', color: 'var(--accent-cyan)'}}>Total: {formatCompact(eventData.eventTotal)}</div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{fontSize: '12px', color: 'var(--text-muted)', padding: '12px', textAlign: 'center'}}>No underperformance found</div>
                                    )}
                                </div>
                            ) : (
                                // Show single event details for individual event view
                                <div style={{backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '12px'}}>
                                    {performanceModalData.underperformanceDays.map((day, idx) => {
                                        const score = performanceModalData.playerScores[day - 1];
                                        const dayName = DAY_NAMES[day - 1];
                                        const dayIcon = DAY_ICONS[day - 1];
                                        const isZero = score === 0;
                                        return (
                                            <div key={idx} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < performanceModalData.underperformanceDays.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'}}>
                                                <div>
                                                    <div style={{fontSize: '12px', fontWeight: '500', color: 'var(--text-primary)'}}>{dayIcon} Day {day}: {dayName}</div>
                                                    <div style={{fontSize: '11px', color: isZero ? 'var(--accent-red)' : 'var(--accent-warning)'}}>
                                                        {isZero ? '❌ No contribution' : '⚠️ Below threshold'}
                                                    </div>
                                                </div>
                                                <div style={{fontSize: '14px', fontWeight: '600', color: isZero ? 'var(--accent-red)' : 'var(--accent-warning)'}}>{formatCompact(score)}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        
                        {!performanceModalData.isAllEvents && (
                        <div style={{marginBottom: '20px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px'}}>
                            <div style={{fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px'}}>Event Performance</div>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                                <div>
                                    <div style={{color: 'var(--text-muted)', fontSize: '11px', marginBottom: '4px'}}>Total Score</div>
                                    <div style={{fontSize: '16px', fontWeight: '600', color: 'var(--accent-cyan)'}}>{formatCompact(performanceModalData.playerRanking?.total || 0)}</div>
                                </div>
                                <div>
                                    <div style={{color: 'var(--text-muted)', fontSize: '11px', marginBottom: '4px'}}>Average/Day</div>
                                    <div style={{fontSize: '16px', fontWeight: '600', color: 'var(--accent-green)'}}>{formatCompact((performanceModalData.playerRanking?.total || 0) / 6)}</div>
                                </div>
                            </div>
                        </div>
                        )}
                        
                        <button onClick={() => setPerformanceModalData(null)} style={{width: '100%', padding: '10px', backgroundColor: 'var(--btn-primary-bg)', color: 'var(--btn-primary-text)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'}}>Close</button>
                    </div>
                </div>
            )}

            {/* Delete Guide Confirmation */}
            {guideToDelete && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => setGuideToDelete(null)}>
                    <div style={{backgroundColor: 'rgba(25, 28, 35, 0.95)', border: '1px solid var(--border-accent)', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px', boxShadow: 'var(--shadow-lg)', backdropFilter: 'blur(10px)'}} onClick={(e) => e.stopPropagation()}>
                        <h2 style={{margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)'}}>Delete Guide?</h2>
                        <p style={{margin: '0 0 20px 0', color: 'var(--text-muted)', fontSize: '13px'}}>Are you sure you want to delete "{guides.find(g => g.id === guideToDelete)?.title || 'this guide'}"? This cannot be undone.</p>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <button 
                                onClick={() => deleteGuide(guideToDelete)}
                                style={{flex: 1, padding: '10px', backgroundColor: 'var(--accent-red)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}
                            >Delete</button>
                            <button 
                                onClick={() => setGuideToDelete(null)}
                                style={{flex: 1, padding: '10px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'}}
                            >Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Member Details Modal */}
            {memberDetailsModal && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => setMemberDetailsModal(null)}>
                    <div style={{backgroundColor: 'rgba(25, 28, 35, 0.95)', border: '1px solid var(--border-accent)', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', backdropFilter: 'blur(10px)', scrollbarColor: 'var(--accent-primary) rgba(0,0,0,0.3)', scrollbarWidth: 'thin'}} onClick={(e) => e.stopPropagation()} className="modal-scrollbar">
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                            <h2 style={{margin: 0, color: 'var(--text-primary)', fontSize: '24px', fontWeight: '700'}}>{memberDetailsFullData?.display_name || memberDetailsModal?.name || 'Player'}</h2>
                            <button onClick={() => setMemberDetailsModal(null)} style={{background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: 'var(--text-secondary)'}}>×</button>
                        </div>

                        {memberDetailsFullData && (
                            <div style={{marginBottom: '20px', padding: '16px', backgroundColor: 'rgba(255, 90, 58, 0.15)', borderRadius: '10px', borderLeft: '3px solid var(--accent-primary)'}}>
                                <div style={{fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase'}}>📊 Database Information</div>
                                <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}><strong>Canonical Name:</strong> {memberDetailsFullData.canonical_name}</div>
                                <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}><strong>Display Name:</strong> {memberDetailsFullData.display_name || '(not set)'}</div>
                                <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}><strong>Member Status:</strong> <span style={{color: memberDetailsFullData.is_active !== false ? 'var(--accent-success)' : 'var(--accent-warning)'}}>{memberDetailsFullData.is_active !== false ? 'active' : 'inactive'}</span></div>
                                <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}><strong>Type:</strong> {memberDetailsFullData.is_alt ? 'Alt' : 'Main'}</div>
                                <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}><strong>Joined Date:</strong> {memberDetailsFullData.joined_date ? (() => { const dateStr = memberDetailsFullData.joined_date.includes('T') ? memberDetailsFullData.joined_date.split('T')[0] : memberDetailsFullData.joined_date; return dateStr.split('-').reverse().join('/'); })() : '(not set)'}</div>
                                <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}><strong>Left Date:</strong> {memberDetailsFullData.left_date ? (() => { const dateStr = memberDetailsFullData.left_date.includes('T') ? memberDetailsFullData.left_date.split('T')[0] : memberDetailsFullData.left_date; return dateStr.split('-').reverse().join('/'); })() : '(not set)'}</div>
                                <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}><strong>Aliases:</strong> {Array.isArray(memberDetailsFullData.aliases) && memberDetailsFullData.aliases.length > 0 ? memberDetailsFullData.aliases.join(', ') : '(none)'}</div>
                            </div>
                        )}

                        <div style={{marginBottom: '20px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '10px', backdropFilter: 'blur(8px)'}}>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px'}}>
                                <div>
                                    <div style={{fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px'}}>Total Score</div>
                                    <div style={{fontSize: '18px', fontWeight: '700', color: 'var(--accent-cyan)'}}>{memberDetailsScores && memberDetailsScores.reduce((a,b) => a+b, 0) > 0 ? formatCompact(memberDetailsScores.reduce((a,b) => a+b, 0)) : '0'}</div>
                                </div>
                                <div>
                                    <div style={{fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px'}}>Avg per Day</div>
                                    <div style={{fontSize: '18px', fontWeight: '700', color: 'var(--accent-primary)'}}>{memberDetailsScores && memberDetailsScores.filter(s => s > 0).length > 0 ? formatCompact(memberDetailsScores.reduce((a,b) => a+b, 0) / memberDetailsScores.filter(s => s > 0).length) : '0'}</div>
                                </div>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <div style={{flex: 1, textAlign: 'center'}}>
                                    <div style={{fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px'}}>Status</div>
                                    <div style={{fontSize: '12px', fontWeight: '600', color: memberDetailsModal?.status === 'active' || memberDetailsModal?.is_active !== false ? 'var(--accent-success)' : 'var(--accent-warning)'}}>{((memberDetailsModal?.status === 'active' || memberDetailsModal?.is_active !== false) ? 'active' : 'inactive').toUpperCase()}</div>
                                </div>
                                <div style={{flex: 1, textAlign: 'center'}}>
                                    <div style={{fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px'}}>Type</div>
                                    <div style={{fontSize: '12px', fontWeight: '600', color: memberDetailsModal?.isAlt || memberDetailsModal?.is_alt ? 'var(--accent-secondary)' : 'var(--text-primary)'}}>{(memberDetailsModal?.isAlt || memberDetailsModal?.is_alt) ? 'Alt' : 'Main'}</div>
                                </div>
                            </div>
                        </div>

                        {(() => {
                            const zeroDayCount = (memberDetailsScores || []).filter(s => s === 0).length;
                            const lowDayCount = (memberDetailsScores || []).filter(s => s > 0 && s < MINIMUM_SCORE).length;
                            if (zeroDayCount === 0 && lowDayCount === 0) return null;
                            return (
                                <div style={{marginBottom: '20px', padding: '12px', backgroundColor: 'rgba(202, 96, 66, 0.15)', borderRadius: '10px', borderLeft: '3px solid var(--accent-danger)'}}>
                                    <div style={{fontSize: '11px', fontWeight: '600', color: 'var(--accent-danger)', marginBottom: '8px', textTransform: 'uppercase'}}>⚠️ Performance Issues</div>
                                    {zeroDayCount > 0 && (
                                        <div style={{fontSize: '12px', color: 'var(--text-primary)', marginBottom: '4px'}}>🔴 <strong>{zeroDayCount} day(s)</strong> with NO contributions</div>
                                    )}
                                    {lowDayCount > 0 && (
                                        <div style={{fontSize: '12px', color: 'var(--text-primary)'}}>🟡 <strong>{lowDayCount} day(s)</strong> with low scores (&lt; {MINIMUM_SCORE})</div>
                                    )}
                                </div>
                            );
                        })()}

                        <div style={{marginBottom: '20px'}}>
                            <div style={{fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px'}}>Editable Information</div>
                            
                            <div style={{marginBottom: '12px'}}>
                                <label style={{display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase'}}>Display Name</label>
                                <input 
                                    type="text" 
                                    value={memberDetailsFormData.displayName} 
                                    onChange={(e) => setMemberDetailsFormData({...memberDetailsFormData, displayName: e.target.value})}
                                    style={{width: '100%', padding: '8px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                />
                            </div>

                            <div style={{marginBottom: '12px'}}>
                                <label style={{display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase'}}>Status</label>
                                <select 
                                    value={memberDetailsFormData.status} 
                                    onChange={(e) => setMemberDetailsFormData({...memberDetailsFormData, status: e.target.value})}
                                    style={{width: '100%', padding: '8px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>

                            <div style={{marginBottom: '12px'}}>
                                <label style={{display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase'}}>Joined Date</label>
                                <input 
                                    type="date" 
                                    value={memberDetailsFormData.joinedDate} 
                                    onChange={(e) => setMemberDetailsFormData({...memberDetailsFormData, joinedDate: e.target.value})}
                                    style={{width: '100%', padding: '8px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                />
                            </div>

                            <div style={{marginBottom: '12px'}}>
                                <label style={{display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase'}}>Left Date</label>
                                <input 
                                    type="date" 
                                    value={memberDetailsFormData.leftDate} 
                                    onChange={(e) => setMemberDetailsFormData({...memberDetailsFormData, leftDate: e.target.value})}
                                    style={{width: '100%', padding: '8px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                />
                            </div>

                            <div style={{marginBottom: '12px'}}>
                                <label style={{display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase'}}>Aliases (comma separated)</label>
                                <input 
                                    type="text" 
                                    value={memberDetailsFormData.newAliases} 
                                    onChange={(e) => setMemberDetailsFormData({...memberDetailsFormData, newAliases: e.target.value})}
                                    style={{width: '100%', padding: '8px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box'}}
                                />
                            </div>

                            <div style={{marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', position: 'relative'}}>
                                    <input 
                                        type="checkbox" 
                                        checked={memberDetailsFormData.isAlt} 
                                        onChange={(e) => setMemberDetailsFormData({...memberDetailsFormData, isAlt: e.target.checked})}
                                        style={{
                                            appearance: 'none',
                                            width: '20px',
                                            height: '20px',
                                            minWidth: '20px',
                                            minHeight: '20px',
                                            border: '2px solid var(--border-accent)',
                                            borderRadius: '4px',
                                            backgroundColor: memberDetailsFormData.isAlt ? 'var(--accent-primary)' : 'rgba(0,0,0,0.3)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                    />
                                    {memberDetailsFormData.isAlt && (
                                        <span style={{
                                            position: 'absolute',
                                            left: '2px',
                                            width: '16px',
                                            height: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            pointerEvents: 'none',
                                            lineHeight: '1'
                                        }}>✓</span>
                                    )}
                                    <span style={{fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)'}}>This is an Alt account</span>
                                </label>
                            </div>
                        </div>

                        <div style={{marginBottom: '20px'}}>
                            <div style={{fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px'}}>Daily Scores</div>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px'}}>
                                {DAY_NAMES.map((day, idx) => {
                                    const score = memberDetailsScores?.[idx] || 0;
                                    return (
                                        <div key={idx} style={{backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px', textAlign: 'center', backdropFilter: 'blur(8px)'}}>
                                            <div style={{fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '4px'}}>{day.substring(0, 3)}</div>
                                            <div style={{fontSize: '14px', fontWeight: '700', color: score === 0 ? 'var(--accent-danger)' : score < MINIMUM_SCORE ? 'var(--accent-warning)' : 'var(--text-primary)'}}>{formatCompact(score)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                            <button onClick={() => { 
                                const updateMember = async () => {
                                    try {
                                        // Try memberDetailsFullData first, then fallback to memberDetailsModal
                                        const memberId = memberDetailsFullData?.id || memberDetailsModal?.id;
                                        if (!memberId) {
                                            console.error('No member ID available for update');
                                            showToast('error', 'Error', 'Cannot save: member ID not found');
                                            return;
                                        }
                                        
                                        const updateData = {
                                            display_name: memberDetailsFormData.displayName,
                                            is_active: memberDetailsFormData.status === 'active',
                                            is_alt: memberDetailsFormData.isAlt === true,
                                            aliases: memberDetailsFormData.newAliases.split(',').map(a => a.trim()).filter(a => a)
                                        };
                                        
                                        // Only update date fields if they have values
                                        if (memberDetailsFormData.joinedDate) {
                                            updateData.joined_date = memberDetailsFormData.joinedDate;
                                        }
                                        if (memberDetailsFormData.leftDate) {
                                            updateData.left_date = memberDetailsFormData.leftDate;
                                        }
                                        
                                        if (DEBUG) console.log('Updating member ID:', memberId, 'with data:', updateData);
                                        const { error } = await supabase.from('members').update(updateData).eq('id', memberId);
                                        
                                        if (error) {
                                            console.error('Supabase error:', error);
                                            showToast('error', 'Error', error?.message || 'Failed to save member');
                                        } else {
                                            if (DEBUG) console.log('Member updated successfully');
                                            showToast('success', 'Success', 'Member saved successfully');
                                            setMemberDetailsModal(null);
                                            setMemberDetailsFullData(null);
                                            loadMembers();
                                        }
                                    } catch (err) {
                                        console.error('Error updating member:', err);
                                        showToast('error', 'Error', err?.message || 'Error saving member');
                                    }
                                };
                                updateMember();
                            }} style={{flex: 1, padding: '12px', background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer'}}>Save Changes</button>
                            <button onClick={() => setMemberDetailsModal(null)} style={{flex: 1, padding: '12px', backgroundColor: 'var(--bg-glass)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', borderRadius: '10px', fontWeight: '700', cursor: 'pointer'}}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Error Modal - Redesigned */}
            {uploadErrorModal && (
                <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setUploadErrorModal(null); }}>
                    <div className="modal-box" style={{maxWidth: '680px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: 'rgba(15, 8, 20, 0.95)', backdropFilter: 'blur(10px)'}}>
                        {/* Header Section */}
                        <div style={{padding: '24px 24px 16px', borderBottom: '1px solid rgba(255,165,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                            <div>
                                <div className="modal-title" style={{fontSize: '24px', marginBottom: '4px'}}>📋 Score Upload Status</div>
                                <div style={{fontSize: '12px', color: 'var(--text-muted)'}}>Review and resolve failed member entries</div>
                            </div>
                            <button onClick={() => setUploadErrorModal(null)} style={{background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '28px', padding: '0', lineHeight: '1', transition: 'color 0.2s'}} onMouseOver={(e) => e.target.style.color = 'var(--text-primary)'} onMouseOut={(e) => e.target.style.color = 'var(--text-muted)'}>×</button>
                        </div>

                        {/* Stats Cards Section */}
                        <div style={{padding: '20px 24px', borderBottom: '1px solid rgba(255,165,0,0.08)'}}>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px'}}>
                                {/* Updated Card */}
                                <div style={{backgroundColor: 'rgba(76, 175, 80, 0.08)', border: '1px solid rgba(76, 175, 80, 0.2)', borderRadius: '12px', padding: '16px', boxShadow: 'inset 0 1px 0 rgba(76, 175, 80, 0.1)'}}>
                                    <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                                        <div style={{width: '44px', height: '44px', backgroundColor: 'rgba(76, 175, 80, 0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px'}}>✓</div>
                                        <div style={{flex: 1}}>
                                            <div style={{fontSize: '11px', color: 'rgba(76, 175, 80, 0.8)', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.8px', marginBottom: '6px'}}>Successfully Updated</div>
                                            <div style={{fontSize: '32px', fontWeight: '800', color: 'var(--accent-success)', lineHeight: '1'}}>{uploadErrorModal.found}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Failed Card */}
                                <div style={{backgroundColor: 'rgba(244, 67, 54, 0.08)', border: '1px solid rgba(244, 67, 54, 0.2)', borderRadius: '12px', padding: '16px', boxShadow: 'inset 0 1px 0 rgba(244, 67, 54, 0.1)'}}>
                                    <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                                        <div style={{width: '44px', height: '44px', backgroundColor: 'rgba(244, 67, 54, 0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px'}}>!</div>
                                        <div style={{flex: 1}}>
                                            <div style={{fontSize: '11px', color: 'rgba(244, 67, 54, 0.8)', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.8px', marginBottom: '6px'}}>Requires Action</div>
                                            <div style={{fontSize: '32px', fontWeight: '800', color: 'var(--accent-danger)', lineHeight: '1'}}>{uploadErrorModal.notFound.length}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Members List Section */}
                        {uploadErrorModal.notFound.length > 0 && (
                            <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '20px 24px'}} className="error-list-scroll">
                                <div style={{fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px'}}>Fix Members ({uploadErrorModal.notFound.length} remaining)</div>
                                
                                <div style={{display: 'flex', flexDirection: 'column', gap: '14px'}}>
                                    {uploadErrorModal.notFound.map((entry, idx) => {
                                        const searchValue = errorSearchInputs[entry.name] !== undefined ? errorSearchInputs[entry.name] : '';
                                        const suggestions = getMemberSuggestions(searchValue);
                                        const showSuggestions = activeSearchIndex === idx && suggestions.length > 0;
                                        
                                        return (
                                            <div key={entry.name} style={{backgroundColor: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 165, 0, 0.1)', borderRadius: '12px', padding: '16px', position: 'relative', backdropFilter: 'blur(4px)'}}>
                                                <div style={{display: 'grid', gridTemplateColumns: '1fr 90px 140px', gap: '14px', alignItems: 'flex-end'}}>
                                                    {/* Name Input with Autocomplete */}
                                                    <div style={{position: 'relative'}}>
                                                        <label style={{display: 'block', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: '700', marginBottom: '8px', letterSpacing: '0.8px'}}>Member Name</label>
                                                        <input 
                                                            type="text" 
                                                            value={searchValue}
                                                            onChange={(e) => {
                                                                setErrorSearchInputs({...errorSearchInputs, [entry.name]: e.target.value});
                                                                setActiveSearchIndex(idx);
                                                            }}
                                                            onFocus={() => setActiveSearchIndex(idx)}
                                                            placeholder={entry.name}
                                                            style={{width: '100%', padding: '10px 12px', backgroundColor: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(255, 165, 0, 0.15)', color: 'var(--text-primary)', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'all 0.2s'}}
                                                        />
                                                        
                                                        {/* Autocomplete Dropdown */}
                                                        {showSuggestions && (
                                                            <div style={{position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'rgba(10, 5, 15, 0.98)', border: '1px solid rgba(255,165,0,0.25)', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 100, marginTop: '4px', boxShadow: '0 12px 32px rgba(0,0,0,0.6)'}}>
                                                                {suggestions.map((member, sidx) => (
                                                                    <button
                                                                        key={sidx}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            setErrorSearchInputs({...errorSearchInputs, [entry.name]: member.display_name || member.name});
                                                                            setActiveSearchIndex(null);
                                                                        }}
                                                                        style={{width: '100%', textAlign: 'left', padding: '10px 12px', backgroundColor: sidx === 0 ? 'rgba(255,165,0,0.1)' : 'transparent', border: 'none', borderBottom: sidx < suggestions.length - 1 ? '1px solid rgba(255,165,0,0.08)' : 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '13px', transition: 'background-color 0.15s', fontWeight: member.is_alt ? '600' : '400'}}
                                                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,165,0,0.15)'}
                                                                        onMouseLeave={(e) => e.target.style.backgroundColor = sidx === 0 ? 'rgba(255,165,0,0.1)' : 'transparent'}
                                                                    >
                                                                        {member.display_name || member.name} {member.is_alt && <span style={{color: 'var(--text-muted)', fontSize: '11px'}}>(Alt)</span>}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Score Display */}
                                                    <div>
                                                        <label style={{display: 'block', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: '700', marginBottom: '8px', letterSpacing: '0.8px'}}>Score</label>
                                                        <div style={{fontSize: '14px', fontWeight: '700', color: 'var(--accent-cyan)', padding: '10px', backgroundColor: 'rgba(0, 150, 200, 0.1)', borderRadius: '8px', textAlign: 'center', border: '1px solid rgba(0, 150, 200, 0.2)', fontFamily: 'monospace'}}>{entry.score}</div>
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div style={{display: 'flex', gap: '8px'}}>
                                                        <button 
                                                            onClick={() => retryFailedMember(idx, searchValue || entry.name)}
                                                            style={{flex: 1, padding: '10px 0', backgroundColor: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s'}}
                                                            onMouseOver={(e) => e.target.style.backgroundColor = 'var(--accent-secondary)'}
                                                            onMouseOut={(e) => e.target.style.backgroundColor = 'var(--accent-primary)'}
                                                        >↻ Retry</button>
                                                        <button 
                                                            onClick={() => createAndRetryMember(idx, searchValue || entry.name, entry.score)}
                                                            style={{flex: 1, padding: '10px 0', backgroundColor: 'rgba(76, 175, 80, 0.85)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s'}}
                                                            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(76, 175, 80, 1)'}
                                                            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(76, 175, 80, 0.85)'}
                                                        >+ Add</button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Footer Section */}
                        <div style={{padding: '20px 24px', borderTop: '1px solid rgba(255,165,0,0.08)', display: 'flex', gap: '12px'}}>
                            <button 
                                onClick={() => retryAllFailedMembers()}
                                style={{flex: 1, padding: '12px 20px', background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.9), rgba(56, 142, 60, 0.9))', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px'}}
                                onMouseOver={(e) => e.target.style.background = 'linear-gradient(135deg, rgba(76, 175, 80, 1), rgba(56, 142, 60, 1))'}
                                onMouseOut={(e) => e.target.style.background = 'linear-gradient(135deg, rgba(76, 175, 80, 0.9), rgba(56, 142, 60, 0.9))'}
                            >🔄 Retry All Failed</button>
                            <button 
                                onClick={() => setUploadErrorModal(null)}
                                style={{flex: 1, padding: '12px 20px', backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--text-primary)', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px'}}
                                onMouseOver={(e) => {e.target.style.backgroundColor = 'rgba(255,255,255,0.12)'; e.target.style.borderColor = 'rgba(255,255,255,0.2)'}}
                                onMouseOut={(e) => {e.target.style.backgroundColor = 'rgba(255,255,255,0.08)'; e.target.style.borderColor = 'rgba(255,255,255,0.12)'}}
                            >Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

ReactDOM.render(<GuildDashboard />, document.getElementById('root'));